import { UNIT, PASU, DOG, ENEMY, GRASSHOPPER, GROUND_ATTACK, EAGLE, ENEMY_SEPARATION, STAB, PLAYER, NIGHT_SEC, PIECE,
         SWEEP_AT_END, pieceReq, LOSS_TABLE, LOSS_BASE, DOWN, SHOP, RESCUE, ACT,
         buyPrice, rerollCum, WATER,
         BUILD, NIGHT_NO, axisProgressFor,
         // 보스(6-2) — 상태기계 정본은 boss.js 다. world 는 세 지점에서만 부른다.
         CAMPAIGN_LEVEL_BASE, NIGHTS, spawnBudgetOf, spawnMultOf, SPAWN_VOLUME, BOSS_VOLUME,
         campaignSpawnTarget, campaignConcurrentTarget, nightVolumeFactor, enemyNightScale,
         FINALE, isFinalNight,
         LEVEL_CURVE, TARGET_LV, ORBIT, HIT, SKIPW, SETFX,
         // ⚠ 그림 상수인데 **world 가 링버퍼 상한 하나만** 읽는다(`SKIPV.cap`).
         //   자국을 만드는 자리가 world 이므로 상한도 여기서 걸어야 배열이 안 자란다.
         SKIPV,
         // ⚠ 그림 상수인데 **world 가 물기 주기 둘만** 읽는다(`hold`·`bite`).
         //   무는 순간을 아는 자리가 world 이므로 표식도 여기서 찍어야 한다.
         DOGFX,
         // 밤 물량의 배분(몫)과 레벨업 요구량 곡선 — 둘 다 2026-07-31 신설
         SPAWN_SHAPE, LEVEL_REQ, levelRequirement,
         // 【2026-08-02】 벽 제거 — 들판에 끝이 없다. 스폰 고리·치움 반경이 여기서 온다.
         FIELD } from './config.js';
import { makeBoss, snapshotBossRecord, bossSwarmMult, BOSS_ATTACK_FX, bossKindForNight } from './boss.js';
// 요령 카드 → 훅. **값의 정본은 mods.js 의 EFFECTS 표다** — 이 파일은 읽기만 한다.
// 그래서 강화 트리가 값을 바꿔도 여기는 안 바뀐다.
import { gearMods, gearWeaponStats,
         FIRE, SHARD, PIECE_PULL, SHAKE, BLOOD, DMGTEXT, GATHER,
         shardDamage } from './mods.js';
// 부품 — 카드·갈래·진화를 대체한다. 밤 진행률이 등급을 정하므로 world 가 u 를 준다.
import { startGear, pickKinds, rampGear, maxGear } from './gear.js';
import { normalizeDayIn, mergeDayIn, DENSE } from './dayin.js';
import { SCREEN_RESPONSE_PRIORITY } from './presentation.js';
import { makeSpeciesPicker } from './enemyart.js';
import {
  EnemyBehaviorDirector, createBehaviorMetrics, eagleTalonGeometry,
  enforceEnemyStandoff, enemyNeedsStandoff, moveEnemyFallback, recordBehaviorSweep,
} from './enemybehavior.js';

// N13의 새 행동이 처치 지점을 직선 돌진 경로(ACT.charge.dist)와 늑대 공전선
// (ACT.circle.orbit)으로 갈라 놓았다. 그러모으기 접근선은 두 기존 행동 거리의
// 기하평균을 읽는다. 새 절대 반경을 만들지 않으면서 어느 한 경로에도 치우치지 않는다.
export const GATHER_TRIGGER_RADIUS = Math.sqrt(ACT.charge.dist * ACT.circle.orbit);

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const segmentCircleFirstFraction = (px, py, radius, x1, y1, x2, y2) => {
  const sx = x1 - px, sy = y1 - py;
  const c = sx * sx + sy * sy - radius * radius;
  if (c < 0) return 0;
  const vx = x2 - x1, vy = y2 - y1;
  const a = vx * vx + vy * vy;
  if (a <= 1e-12) return null;
  const b = 2 * (sx * vx + sy * vy);
  const disc = b * b - 4 * a * c;
  if (disc <= 0) return null;
  const first = (-b - Math.sqrt(disc)) / (2 * a);
  return first >= 0 && first <= 1 ? first : null;
};
const pointSegmentDistanceSq = (px, py, x1, y1, x2, y2) => {
  const vx = x2 - x1, vy = y2 - y1;
  const len2 = vx * vx + vy * vy;
  if (len2 <= 1e-24) return (px - x1) ** 2 + (py - y1) ** 2;
  const u = clamp(((px - x1) * vx + (py - y1) * vy) / len2, 0, 1);
  return (px - x1 - vx * u) ** 2 + (py - y1 - vy * u) ** 2;
};
const CURVE_ROOT_DEPTH = Math.ceil(Math.log2(1 / Math.sqrt(Number.EPSILON)));
const CURVE_ROOT_STEPS = 53; // IEEE-754 double significand bits, not a gameplay tune.
const batSineCircleFirstFraction = (segment, px, py, radius) => {
  const span = segment.phaseT1 - segment.phaseT0;
  const cycle = Math.max(Number.EPSILON, Math.abs(segment.cycleSec));
  const omega = Math.PI * 2 / cycle;
  const bx = segment.baseDx, by = segment.baseDy;
  const speed = segment.forwardSpeed, amp = segment.amplitude, side = segment.side;
  const sin0 = Math.sin(omega * segment.phaseT0);
  const pointAt = (u) => {
    const phaseT = segment.phaseT0 + span * u;
    const forward = speed * span * u;
    const lateral = amp * (Math.sin(omega * phaseT) - sin0) * side;
    return {
      x: segment.x1 + bx * forward - by * lateral,
      y: segment.y1 + by * forward + bx * lateral,
    };
  };
  const radius2 = radius * radius;
  const inside = (point) => (point.x - px) ** 2 + (point.y - py) ** 2 < radius2;
  const refineEntry = (outsideU, insideU) => {
    let lo = outsideU, hi = insideU;
    if (inside(pointAt(lo))) return lo;
    for (let i = 0; i < CURVE_ROOT_STEPS; i++) {
      const mid = (lo + hi) / 2;
      if (inside(pointAt(mid))) hi = mid;
      else lo = mid;
    }
    return hi;
  };
  const search = (a, pa, b, pb, depth) => {
    if (inside(pa)) return a;
    // Linear forward motion has zero interpolation error. The sine component's
    // second-derivative bound gives a rigorous tube around this interval's chord.
    const phaseWidth = Math.abs(span * (b - a));
    const curveError = Math.abs(amp) * omega * omega * phaseWidth * phaseWidth / 8;
    const chordDistance = Math.sqrt(pointSegmentDistanceSq(
      px, py, pa.x, pa.y, pb.x, pb.y,
    ));
    if (chordDistance - curveError >= radius) return null;
    const mid = (a + b) / 2;
    const pm = pointAt(mid);
    if (depth >= CURVE_ROOT_DEPTH) {
      if (inside(pm)) return refineEntry(a, mid);
      if (inside(pb)) return refineEntry(mid, b);
      // At machine-derived leaf width, minimize the remaining smooth pocket. This
      // catches a shallow enter/exit pair without fixed gameplay sampling.
      let lo = a, hi = b;
      for (let i = 0; i < CURVE_ROOT_STEPS; i++) {
        const u1 = (2 * lo + hi) / 3, u2 = (lo + 2 * hi) / 3;
        const p1 = pointAt(u1), p2 = pointAt(u2);
        const d1 = (p1.x - px) ** 2 + (p1.y - py) ** 2;
        const d2 = (p2.x - px) ** 2 + (p2.y - py) ** 2;
        if (d1 <= d2) hi = u2; else lo = u1;
      }
      const minimum = (lo + hi) / 2;
      return inside(pointAt(minimum)) ? refineEntry(a, minimum) : null;
    }
    const left = search(a, pa, mid, pm, depth + 1);
    return left == null ? search(mid, pm, b, pb, depth + 1) : left;
  };
  return search(0, pointAt(0), 1, pointAt(1), 0);
};
const behaviorSegmentFirstFraction = (segment, px, py, radius) =>
  segment.curve === 'bat-sine'
    ? batSineCircleFirstFraction(segment, px, py, radius)
    : segmentCircleFirstFraction(
      px, py, radius, segment.x1, segment.y1, segment.x2, segment.y2,
    );

// N40 reunion is simulation state, not a scene-side click shortcut. Keeping
// these values here makes the sequence deterministic in headless runs too.
// The darkness overlay opens a 330px circle around David. A 48px sheep first
// reads as a silhouette once its centre is about 9 units away: its near half
// is then safely inside the soft light instead of merely touching a black edge.
const FINALE_FIRST_VISIBLE_RADIUS = 9 * UNIT;
const FINALE_REUNION_HOLD_SEC = 5;
const FINALE_GOLDEN_ANGLE = 2.399963229728653;
// 최종 양 스프라이트의 가시폭은 약 31px. 36px 링 격자는 최소 34px 계약에
// 2px의 안전 여유를 남기며, 6층(반경 216px)이면 게임 상한 99마리를 담는다.
const FINALE_REUNION_RING_STEP = 36;
const FINALE_REUNION_MAX_RINGS = 6;
const FINALE_REUNION_MAX_SHEEP = 99;

// 그 밤의 보스 종류. **정본은 boss.js 의 실제 달력 맵** — 여기서 표를 다시 쓰지 않는다.
const bossKindFor = bossKindForNight;

// 【10-B 2-2】 다발 투사체의 발 사이 각. **측정 대기.**
// 기준: 사거리 13.5유닛에서 7°는 옆으로 1.65유닛 = 물량층 몸 지름(0.56유닛)의 약 3배.
// 즉 한산하면 이웃 발은 확실히 빗나가고, 몸이 붙어 늘어서면 다 맞는다.
const SHOT_SPREAD = 7 * Math.PI / 180;
// 물맷돌 일반 비행 속도의 단일 정본. 사거리는 이 속도로 나눈 수명으로 환산하고,
// 물수제비·도탄·바닥 구르기만 기존 계약대로 그 뒤의 수명을 명시적으로 연장한다.
const SHOT_SPEED = 620;
const OCTANT_TAN = Math.SQRT2 - 1;
// 같은 칸은 `next[i]` 로 한 번만, 나머지는 오른쪽·아래쪽 네 칸만 훑어 쌍 중복을 막는다.
const SEP_CELL_X = [0, 1, 0, 1, -1];
const SEP_CELL_Y = [0, 0, 1, 1, 1];
const SEP_DIR_X = [1, Math.SQRT1_2, 0, -Math.SQRT1_2, -1, -Math.SQRT1_2, 0, Math.SQRT1_2];
const SEP_DIR_Y = [0, Math.SQRT1_2, 1, Math.SQRT1_2, 0, -Math.SQRT1_2, -1, -Math.SQRT1_2];

// 회색박스 월드 — 렌더와 분리. 이 파일이 '게임'이고, 씬은 그리기만 한다.
//
// 밤의 구조:
//   · 양은 없다(N22 예외 — 7-2). '무리 수'는 안정성 배율에 들어가는 숫자일 뿐(3-5).
//   · 적은 전부 나에게 온다(2-4). 링·무리 중심·테더 폐기.
//   · 입력은 이동 하나(2-1).
//   · 안정성이 유일한 판돈. **반경 안에 적이 있으면 계속 깎이고, 비면 회복한다.**
//     **피격은 안정성을 건드리지 않는다(3-1).** HP = 사고 / 안정성 = 자세(3-2).
//     밤 전체의 **평균** 안정성이 그 밤에 잃을 양 수를 정한다(막판 버티기 무효 — 5장).
//   · 적이 죽으면 **조각**을 떨군다. 조각은 치즈(10:1)이자 레벨 게이지다(4-5).
//   · 레벨업 = 상점 열림. **1회당 구매 최대 3회**(4-3), 리롤은 별도 선형 가격 제약(+1).
//   · 하루는 낮 ↔ 밤 2박자. 잔여 조각은 **밤이 끝날 때** 전량 회수(4-6).
export class World {
  // opts: { weapon, seed, flockN, hp, spawnMult, concurrentMult, stabRadius,
  //         stabRadii[], retries, shopMult, reqMode, rescue, rescueDist }
  constructor(night, opts = {}) {
    this.cfg = night;
    this.nightKey = opts.nightKey ?? null;
    // 적 난이도는 한 밤 안의 레벨업이 아니라 캠페인 밤 번호로 한 번만 정한다.
    this.enemyNightScale = enemyNightScale(this.nightKey);
    // ══════════════════════════════════════════════════════════════
    //  **무기는 물맷돌 하나다** 【2026-07-31 지팡이 삭제 · 08-01 감사】
    //
    //  ⚠ **이것은 이중 방어다 — 없어도 동작은 같다.** 아래 `refreshWeapon()`(456행)이
    //    이미 비-pasu 를 던지고, 그 호출은 이 생성자 안(422행)에 있다. 그래서 여기를
    //    지워도 `new World(..., weapon:'bang')` 은 여전히 터진다. 실측으로 확인했다.
    //    남겨 두는 이유는 **터지는 자리와 말**뿐이다 — 잘못된 입력은 입구에서,
    //    「무엇이 잘못됐는지」를 말하며 죽는 편이 낫다. `refreshWeapon` 의 문구는
    //    *"gear 없는 무기 경로는 삭제됐다"* 라서, 옛 세이브를 연 사람에게는 안 읽힌다.
    //    ⇒ 변이 시험에서 이 줄만 지우면 프로브가 **안 잡는다.** 그건 검사 구멍이
    //      아니라 중복이라는 뜻이다. 진짜 방어는 `refreshWeapon` 과 `loadSave` 둘이다.
    // ══════════════════════════════════════════════════════════════
    this.weapon = opts.weapon ?? 'pasu';
    if (this.weapon !== 'pasu') {
      throw new Error(`무기 '${this.weapon}' 은 없다 — 지팡이는 2026-07-31 에 삭제됐고 `
        + `무기는 물맷돌('pasu') 하나다. 옛 세이브를 열었다면 day.js loadSave 를 볼 것.`);
    }
    // ── 낮이 들어온다 ─────────────────────────────────────────
    // **여기가 2박자의 나머지 반쪽이다.** 지금까지 밤 → 낮만 흘렀고 낮 → 밤은
    // 통로가 없어서, 낮 3축을 6단계까지 다 사도 밤에서 아무 일도 안 일어났다.
    this.dayIn = normalizeDayIn(opts.dayMods);
    // 타겟 우선순위 — 낮 3축이 연다. `elitePriority` 는 구 불리언이며 하위호환으로 남긴다.
    //   ⚠ `dayIn.priority` 는 중립값이 'nearest' 라 **항상 진리값**이다 —
    //     `??` 로 이으면 구 `elitePriority` 경로가 영영 안 걸린다. 낮이 실제로
    //     뭔가를 넘겼을 때만 그쪽을 본다.
    this.priority = opts.priorityMode
                 ?? (opts.dayMods ? this.dayIn.priority : null)
                 ?? (opts.elitePriority ? 'elite' : 'nearest');
    this.elitePriority = this.priority === 'elite';
    this.flockN = opts.flockN ?? night.flock ?? 20;     // 순수 파라미터(화면에 없다)
    this.flockMult = 1 + (this.flockN - 20) / STAB.flockDiv;   // 5장 무리 배율
    this.reqMode = opts.reqMode;
    this.shopMult = opts.shopMult ?? SHOP.mult;

    this.baseLoss = opts.baseLoss ?? night.baseLoss ?? LOSS_BASE;

    // ── 7-2 구출 밤: 필드만 3배 + 카메라 추적. 그 외 밤은 1920×1080 고정(8-4) ──
    // **기본은 꺼짐**이다 — N22를 ①의 밀도 곡선 표본으로 쓸 때는 평범한 밤 모델로
    // 돌려야 다른 밤과 같은 자 위에 놓인다. 구출 모델은 ④에서만 명시적으로 켠다.
    this.rescue = !!opts.rescue;
    const fm = this.rescue ? RESCUE.fieldMult : 1;
    // ══════════════════════════════════════════════════════════════
    //  ⚠⚠ **W·H 는 더 이상 「벽」이 아니다** 【벽 제거 2026-08-02】
    //    들판에는 끝이 없다(config 의 `FIELD` 머리말). 이 둘은 이제 **「처음 자리의
    //    크기」** 다 — 다윗·개가 서는 곳이고, 화면 한 판과 같은 크기다.
    //  ⚠ **여기에만 깔리던 물·올무는 둘 다 없어졌다** 【2026-08-07】: 우물은 화면
    //    격자로 무한 타일링되고(config.WATER), 올무는 개념째 폐기됐다. 그래서 이 사각형은
    //    이제 **시작 자리와 양 스폰 분포의 자**일 뿐이다.
    // ══════════════════════════════════════════════════════════════
    this.W = 1920 * fm; this.H = 1080 * fm;

    this.t = 0;
    this.over = false;
    // N40은 전투에서 쓰러져도 잃어버린 양을 찾는 피날레까지는 계속된다.
    // 화면 결과는 재회 뒤 '구출'이 되지만, 밤의 실패·손실 정산은 이 표식으로 보존한다.
    this.finalCombatDown = false;
    this.rnd = mulberry(opts.seed ?? 7);
    this._seed = opts.seed ?? 7;         // 전용 난수 파생용(placeWater — 밤 난수열 오염 방지)
    this.speciesRnd = mulberry(cellHash(this._seed, 0x53504543, Number(String(this.nightKey ?? '').slice(1)) || 0));
    this._pickSpecies = this.nightKey && !night.sabbath ? makeSpeciesPicker(this.nightKey) : null;
    this.jackpot = opts.jackpot ?? null; // 귀한 돌 — gearshop 이 읽는다

    // 【2026-08-06】 볶은 곡식(자루=영구/한 줌=그 밤) — 낮이 몸을 불린다.
    //   opts.hp 를 명시한 헤드리스(HP 1e9 등)는 그대로 존중한다 — 측정의 자가 흔들리면 안 된다.
    this.maxHp = Math.round((opts.hp ?? PLAYER.hp) * (this.dayIn?.maxHpMul ?? 1));
    this.player = { x: this.W / 2, y: this.H / 2, hp: this.maxHp, inv: 0,
                     fx: 1, fy: 0,      // 이동 방향(렌더)
                     vx: 0, vy: 0,      // 종 행동의 선행차단이 읽는 실제 프레임 속도
                     ax: 1, ay: 0 };    // **2-6 타격 정면 = 가장 가까운 적 방향**

    // ── 안정성 다중 반경 추적 (① 전용) ─────────────────────────
    // **반경은 시뮬레이션에 되먹임되지 않는다** — 안정성은 적·플레이어·무기 어느
    // 것도 읽지 않고, 봇 정책도 안정성을 안 본다. 따라서 한 번의 런에서 여러 반경의
    // 안정성을 동시에 적분해도 값이 정확히 같다. ①의 반경 스윕이 공짜가 되는 근거다.
    // (피격 항이 있던 시절에는 이것이 성립했지만 HP·다운이 궤적을 갈랐다. 3-1로 피격
    //  항이 빠지고 ②를 HP=∞ 궤적에서 해석적으로 풀면서 그 결합도 끊겼다.)
    const radii = (opts.stabRadii ?? [opts.stabRadius ?? STAB.radius]).slice().sort((a, b) => a - b);
    this.radii = radii;
    this.stabRadius = radii[0];
    // 시작값 — **낮이 정한다.** 전날 패닉으로 끝났으면 85로 시작한다(명세 5장).
    // 구 구현은 `s: 100` 하드코딩이라 패닉의 다음 밤 대가가 실재하지 않았다.
    // 카드도 시작값을 말할 수 있다(27 폭풍 — 담이 무너진 채 밤이 온다【리듬 2026-08-08】).
    //   낮(전날 패닉)과 겹치면 **더 낮은 쪽**이 이긴다 — 벌이 두 번 곱해지진 않는다.
    const s0 = Math.min(night.stabStart ?? 100, this.dayIn.stabStart);
    this.tracks = radii.map(r => ({ r, s: s0, sum: 0, n: 0, min: s0, panic: false,
                                    panicSec: 0, recSec: 0, capSec: 0, inRadSum: 0 }));
    // 대표 트랙 = opts.stabRadius(없으면 STAB.radius)에 가장 가까운 것. HUD·정산이 이걸 쓴다
    const want = opts.stabRadius ?? STAB.radius;
    this.primary = radii.reduce((bi, r, i) => Math.abs(r - want) < Math.abs(radii[bi] - want) ? i : bi, 0);

    // ── 계수 역산용 궤적 기록 (headless_coef.mjs) ────────────────
    // **반경 내 적 수의 시간 궤적은 coef·cap·rec 어느 것에도 의존하지 않는다.**
    // 안정성이 시뮬레이션에 되먹임되지 않기 때문이다(위 문단과 같은 논거).
    // 그래서 궤적을 한 번 기록해 두면 (반경 × 계수 × 상한) 3차원 스윕 전체를
    // **시뮬레이션 없이 재적분만으로** 돌 수 있다. 빈마다 두 값을 남긴다:
    //   busySum = (반경 내 적 수 × dt)의 합   busyN = 반경이 비어 있지 않던 시간
    // 이 둘이면 "빈 안에서 적이 있던 시간의 평균 적 수"와 "빈 시간"이 복원된다 —
    // 단순 평균 한 개만 남기면 회복 구간이 뭉개져 5장의 회복 규칙을 못 재현한다.
    this.traceBins = opts.traceBins ?? 0;
    if (this.traceBins) {
      this.traceDt = NIGHT_SEC / this.traceBins;
      this.trace = radii.map(() => ({
        busySum: new Float32Array(this.traceBins),
        busyN: new Float32Array(this.traceBins),
        allN: new Float32Array(this.traceBins),
      }));
    }

    this.sinceHit = 0;
    this.barks = [];                // **연출 전용**(2-7). 게임 로직·봇 정책이 참조하지 않는다

    // ── 개 ─────────────────────────────────────────────────────
    // 낮이 보낸 명단이 있으면 그것이 전부다. 없을 때만(회색박스·프로브)
    // 고정 목양견 서리·바람 둘을 만든다.
    const dogList = opts.dogs
      ?? Array.from({ length: DOG.slots },
                    (_, i) => ({ kind: DOG.kinds[i] ?? DOG.kinds[DOG.kinds.length - 1] }));
    this.dogs = dogList.map((d, i, a) => ({
      kind: d.kind ?? DOG.kinds[i] ?? 'baram',
      role: d.role ?? null,               // 방어 / 요격 — 낮이 정한다(서리·바람은 고정)
      // 플레이어를 중심으로 좌우로 벌려 세운다. **두 마리일 때 ±4유닛** —
      // 구 리터럴이 그 값이었고, 시작 위치가 바뀌면 실측이 통째로 흔들린다
      // (한 번 ±2로 좁혔다가 gear_world_check 의 짝 상승 폭이 어긋났다).
      x: this.W / 2 + (i - (a.length - 1) / 2) * 8 * UNIT,
      y: this.H / 2,
      mode: '순찰', attack: 0,
    }));
    this.enemies = [];
    this.eagles = [];
    const eagleCount = Math.max(0, night.threatCut | 0);
    this.eagleSchedule = Array.from({ length: eagleCount }, (_, i) =>
      (i + 1) * NIGHT_SEC / (eagleCount + 1));
    this._eagleScheduleI = 0;
    this.shots = [];
    this.fires = [];                // 「기름을 붓는다」 화염 지대(mods.FIRE)
    // ── 묵은 차돌 — **바닥에 남는 돌** ────────────────────────────
    // `null` 이면 이 빌드에 차돌이 없다(다른 다섯 돌은 이 배열을 안 만든다).
    // ⚠ **밤이 끝나면 리셋**된다(충돌 7) — World 가 밤마다 새로 만들어지므로
    //   저장 경로가 애초에 없다. *성장은 그 밤의 것이다.*
    // ── 굳은 세트 「터진 자리가 또 터진다」 — 예약된 폭발 ───────────
    // 세트가 안 걸리면 영영 비어 있다.
    this.blasts = [];
    // 손에 익은 ◆ — 여섯 박자마다 0.10초 뒤 열리는 절반 위력의 덧선.
    // 일반 발과 동일한 배열을 쓰되 예약을 분리해 자기를 다시 예약하지 않는다.
    this.lineEchoes = [];

    // ── 갈래 (강화 트리 v1 1-2) ────────────────────────────────
    // **결(結)은 0:00에 3택 1이고 활성 풀 밖의 특별 제시다.** 사람이 고르는 경로는
    // shop.js 가 열고, 회색박스 자동 경로는 여기서 시드로 하나 뽑는다 — 안 고르면
    // 무기 활성이 공통 2장뿐이라 그 밤이 다른 게임이 된다.
    // ── 【2026-07-31】 갈래·진화·공용 카드 초기화가 여기 있었다 — **카드 체계 삭제**(지팡이 삭제와 함께).

    this.pops = [];                 // 처치 표식(연출). 규칙은 아무도 안 읽는다
    this.blood = [];                // 피 얼룩 — 바닥에 남는다(상한 BLOOD.cap)
    this.corpses = [];              // 시체 — 잠깐 남았다가 스러진다
    this.dmgTexts = [];             // 뜨는 피해 숫자 — 물량층은 작게, 정예·보스는 크게
    this.shake = 0;                 // 화면 흔들림 — **조작을 뺏기는 순간**의 신호
    this.shakeKind = 'normal';
    this.shakeDx = 0; this.shakeDy = 0; this._shakeAt = -9;
    this._presentKind = null; this._presentAt = -9; this._presentPower = 0;
    this._presentDx = 0; this._presentDy = 0; this._presentSeq = 0;
    // **바닥 자국** — 착탄 광역·회전 타격이 0.3초 만에 사라져 「지나온 자리」가
    //   안 보였다. 피 얼룩은 남는데(BLOOD) 내 공격 자국은 안 남았다.
    this.decals = [];
    // ── 물수제비 자국 — **거친 납작한 돌의 유일한 시각 채널**  【2026-07-30】
    //   ⚠⚠ **연출이다. 판정은 아무도 이 배열을 안 읽는다.**
    //   자국은 광역이 금지돼 있어(gear.SKIP) `explodeAt` 을 안 타므로 `decals` 에
    //   한 톨도 안 남았다 — 즉 **예산 25 가 화면에서 완전히 안 보였다.** 재설계의
    //   목적이 *"화면에 안 보이는 셋"* 을 보이게 하는 것인데 이 돌만 그대로였다.
    //   ⚠ `decals`(상한 40)에 섞지 않는다. 자국은 초당 100개까지 나므로 섞으면
    //     무거운 돌의 착탄 자국을 **즉시 밀어낸다** — 한 채널이 다른 채널을 지운다.
    this.skipMarks = [];
    // 필드 드랍 — 지금은 「그러모으기」 하나. 드문 사건 확률은 처치 수에서 직접 나온다.
    this.drops = [];
    this._gatherUntil = -9;
    // ── 타격감 ──
    this.hitstop = 0;               // 남은 감속 시간. **정지가 아니라 감속**이다
    this._comboN = 0; this._comboT = -9;   // 짧은 창 안의 연쇄 처치 수

    // ── 보스 (6-2) ────────────────────────────────────────────
    // 상태기계는 `boss.js` 가 정본이고 여기서는 **세 지점만** 부른다:
    //   step() 옆에서 전진 / autoWeapon 에서 피해 배율 / spawn 에서 물량 −40%.
    // **HP·비급소 배율은 `측정 대기`(null)다.** 그래서 지금은 보스를 **죽일 수 없고**,
    // 그것이 고장이 아니라 미정 표시다 — 채우는 순간 그게 새 규칙이 된다(6-2 표).
    // 회색박스가 든 10밤 중 보스 밤은 **N30(곰) 하나**다(N28·N29는 카드가 없다).
    this.boss = null;
    this.bossRecord = null;
    const bk = bossKindFor(this.nightKey);
    if (bk && (opts.boss ?? true)) {
      // ── 등장 위치 ────────────────────────────────────────
      // 구 구현은 `(W/2, H*0.25)` **고정**이었다. 그러면 소년 시작점(화면 중앙)의
      // **수직 위**라 도주 방향이 모든 시드·무기에서 같아진다 — 실측에서 6런이
      // **바이트 단위로 동일**했다(전부 12.9초). **표본 6개가 아니라 1개였다.**
      // 등장각만 반경 위에서 돌리자 12.9 -> 50.7 / 61.7 / 66.6 / 81.3 / 83.5 /
      // 84.7 / 87.8 로 흩어졌다. 각도를 시드에 건다 — 여전히 결정적이되 시드마다 다르다.
      //   반경은 구 값과 같은 거리를 유지한다(화면 중앙에서 H*0.25 만큼).
      // ⚠ 【벽 제거 2026-08-02】 가두던 `clamp` 를 뺐다. **거리와 각은 그대로다** —
      //   이 시점의 다윗은 시작 자리(W/2, H/2)에 서 있으므로 구 값과 같은 자리에 난다.
      const bAng = this.rnd() * Math.PI * 2, bR = this.H * 0.25;
      this.boss = makeBoss(bk, {
        x: this.player.x + Math.cos(bAng) * bR,
        y: this.player.y + Math.sin(bAng) * bR,
        rnd: this.rnd,
        onCue: (c) => this.cues.push({ c, t: this.t }),
      });
    }
    this.cues = [];        // 사운드 큐 발화 기록(연출 + 계측). 규칙은 아무도 안 읽는다

    // ── 조각 ──────────────────────────────────────────────────
    this.pieces = [];               // 필드에 떨어진 조각
    this.piecesHeld = 0;            // 주운 조각 누적(= 치즈 원천 + 레벨 게이지)
    this.level = 1;
    this.gauge = 0;
    // 【2026-08-02 벽 제거】 `_pstride` 를 지웠다 — 열쇠가 `_pkey` 한 곳에서 나온다.
    //   구 열쇠는 필드 폭에 기대고 있어서 들판에 끝이 없어지는 순간 뜻을 잃는다.
    this._pgrid = new Map();

    // ── 4-3 상점 ──────────────────────────────────────────────
    // 레벨업 1회 = 상점 열림 = 구매 최대 3회. 리롤은 그 3회 밖이고 가격만 제약한다.
    // 소비 정책: **가진 치즈로 살 수 있는 만큼 산다(전량 구매).** 4-4가 성립을 요구하는
    // 것이 바로 이 정책이므로 회색박스는 그 최악(=최대 지출) 시나리오를 잰다.
    this.spent = 0; this.buysThisNight = 0; this.rerollsThisNight = 0;
    // ⚠ **되살렸다** 【2026-07-31 — 밤이 낮으로 못 돌아가던 결함】.
    //   카드 체계를 지울 때 이 줄을 같이 지웠는데, 이것은 카드 모델이 아니라
    //   **「그 밤에 한 장이라도 산 레벨업 횟수」** 라는 계량이고 **부품 상점이 지금도
    //   올린다**(`gearshop.js` — `sessionBought === 1` 일 때). 지워 두니 `undefined++`
    //   = NaN 이 됐고, 밤 끝 보고가 그것을 읽다가 터져 성적이 낮으로 안 넘어갔다.
    this.picksThisNight = 0;
    this.shopOpens = 0; this.buysWanted = 0;
    // **picks = 「한 장이라도 산 레벨업」 횟수.** 중첩은 여기서 나온다 —
    // 3택은 서로 다른 카드이므로 한 레벨업에 내 카드는 많아야 한 장이다(config CARD 주석).

    // 스폰 예산 — 3막 밀도 곡선으로 **밤 전체에 걸쳐** 소진한다.
    // **【2026-07-28】 기본 배수 = 그 밤의 10-A 확정 ① 배수**(night.spawnMult).
    //   구 기본값은 ×1(= 카드 원값)이었고 확정 곡선은 스윕 파라미터로만 존재했다.
    //   opts.spawnMult 를 명시하면 그것이 이긴다 — 스윕 스크립트(headless_spawn.mjs)는
    //   **카드 원값 기준 배수**를 계속 쓰므로 그쪽 의미는 바뀌지 않는다.
    // **5-9 기도**: 재시도 횟수만큼 스폰 −15% 누적(곱연산).
    // ⚠ **물량 손잡이(config.SPAWN_VOLUME)를 여기서 한 번 태운다.** 예산과 동시 상한이
    //   둘 다 `sm` 에서 나오므로, 한 곳에서 곱해야 둘이 같이 움직인다.
    //   `opts.spawnMult` 로 직접 넘어온 값(스윕·프로브)은 **그 값이 곧 지시**이므로 안 곱한다.
    // ⚠ **보스 밤은 물량 손잡이 밖이다**(사장님 지시 2026-07-29).
    //   정본이 「보스 활성 중 물량층 −40%」를 **해법의 성립 조건**으로 못 박았다 —
    //   *"배후를 잡으려면(곰) / 계속 도망 다니려면(사자) **이동할 공간이 있어야 한다**"*.
    //   그 위에 1.2배를 얹으면 그 공간이 사라진다. 실측도 그랬다: 1.2배를 걸어도
    //   −40% 에 먹혀 **N28 0.96배 · N29 0.97배** — 오히려 줄었다.
    const isBossNight = !!bossKindFor(this.nightKey);
    const smBase = opts.spawnMult ?? (this.nightKey && NIGHTS[this.nightKey] === night
      ? spawnMultOf(this.nightKey, undefined, isBossNight)
      : (night.spawnMult ?? 1) * (isBossNight ? BOSS_VOLUME : SPAWN_VOLUME));
    // ⚠ **기도 배수(`prayerMult(retries)`)가 여기 있었다** 【재시도 삭제 2026-08-03】.
    //   재시도마다 그 밤 물량을 15% 깎던 장치인데, 재시도가 없어져 근거가 사라졌다.
    //   기도는 **실패한 다음 날 아침의 연출**로 자리를 옮겼다(`day/ledger.prayerCue`).
    const sm = smBase;
    // ⚠ **마지막 2분의 두께도 보스 밤만 다르다.**   【2026-07-31 몫 기반으로 교체】
    //   보통 밤은 그 2분이 밤 물량의 **55%**(사장님 지시), 보스 밤은 **직전 구간의 1.2배**
    //   까지만이라 몫으로 28.5% 다 — 폭발하면 카이팅·급소 창이 성립하지 않는다.
    //   ⚠ 구 값(24.0 / 2.52)은 rate 를 손으로 적던 시절의 것이다. 지금은 몫에서 유도한다.
    this.endSurge = isBossNight ? BOSS_TAIL_RATE : TAIL_RATE;
    // 낮에서 들고 온 치즈. **밤은 이것을 쓸 수 있다**(사장님 지시 — 치즈는 모든 날 연속).
    this.walletCarry = Math.max(0, Math.round(opts.walletCarry ?? 0));
    // 여기까지 **잃은 양이 몇 마리인가** — 마지막 밤 화살표 끝에 그만큼 모여 있다.
    //   0 이면 화살표 끝에 **한 마리**가 있다(config.FINALE 주석 — 「나도 몰랐던」 하나).
    this.lostSheep = Math.max(0, Math.round(opts.lostSheep ?? 0));
    this.spawnMult = sm;
    // 캠페인 기본 런은 밤1 동시상한을 고정점으로 두고 +0.25%/일 곡선을 함께 탄다.
    // 카드 상한은 적 구성의 원자료로만 남는다. 프로브가 `concurrentMult` 를 명시하면
    // 그 값이 곧 실험 조건이므로 캠페인 곡선을 우회한다.
    const canon = this.nightKey && NIGHTS[this.nightKey] === night;
    const rawCap = Math.max(1, Math.round(night.concurrent * (opts.concurrentMult ?? sm)));
    const useCampaignCap = canon && opts.concurrentMult == null && opts.spawnMult == null && !night.sabbath;
    this.concurrent = useCampaignCap ? campaignConcurrentTarget(this.nightKey) : rawCap;
    // ── 예산의 정본은 config 의 `spawnBudgetOf()` 하나다 ────────────
    // **구 구현은 여기서 직접 `Math.round(v * sm)` 을 돌려 모든 층에 배수를 먹였다.**
    // 그래서 N1 늑대층 raw 통장이 3 대신 **19**가 됐다 — 면제 규칙
    // (`NIGHTS.N1.multExempt`)이 config 에만 있었고 회색박스가 그것을 안 읽었기 때문이다.
    // 배수는 면제되지 않은 층에만 계속 걸리므로 `opts.spawnMult` 스윕(headless_spawn.mjs)과
    // 기도 감소(5-9)의 의미는 그대로다. 면제층만 배수 밖에 선다.
    //   nightKey 가 없거나 넘어온 밤 객체가 카드 정본과 다른 객체이면(변형 밤을 만들어 쓰는 경로)
    //   config 표를 대신 읽으면 그 변형이 무시되므로, 그때는 구 계산식을 그대로 쓴다.
    this.budget = canon
      ? spawnBudgetOf(this.nightKey, sm)
      : Object.fromEntries(
          Object.entries(night.spawn).map(([k, v]) => [k, Math.round(v * sm)]));
    this.totalBudget = Object.values(this.budget).reduce((a, b) => a + b, 0);
    // ══════════════════════════════════════════════════════════════
    //  **통장을 다시 채울 원본** 【사장님 신고 2026-08-03】
    //    *"정해진 몹을 다 죽이면 안나오는거 고쳐"*
    //
    //  구 구현은 `this.budget` 이 **한 번 쓰면 끝나는 통장**이었다. 다 까이면
    //  `spawn()` 이 그대로 `return` 해서 **밤이 끝날 때까지 한 마리도 안 났다.**
    //  ⚠ 그리고 이건 **잘할수록 빨리 온다.** 통장은 「죽인 수」가 아니라 「난 수」로
    //    까이는데, 화면이 `concurrent` 까지 차 있으면 스폰이 멈춰 통장이 안 준다.
    //    빨리 죽여 화면을 비울수록 계속 나고, 그만큼 통장이 빨리 마른다.
    //    ⇒ **못하는 사람은 안 겪고 잘하는 사람만 겪는다.** 그래서 프로브가 못 잡았다
    //      (봇은 화면이 늘 꽉 차서 12분을 돌려도 안 말랐다. 사장님은 26초 남기고 말랐다).
    //  → 마르면 **같은 구성으로 다시 채운다.** 통장은 이제 「총 마릿수 상한」이 아니라
    //    **종별 비율표 + 속도 기준**이다. 총 마릿수는 스폰율 곡선이 정한다.
    //  ⚠ 폭주 걱정은 없다 — 리필은 통장이 0 일 때만 돌고, 실제로 나는 양은 여전히
    //    `spawnRate × spawnScale` 과 `concurrent` 가 묶는다. 리필은 **빈 화면만 막는다.**
    //  ※ 이미 설계에 있던 개념이다 — N19 카드: *"스폰 무한 보충, 마르지 않는 첫 적"*.
    // ══════════════════════════════════════════════════════════════
    this._budget0 = { ...this.budget };
    // ⚠ **밀도 보정** 【벽 제거 2026-08-02】. 사장님 계약이 *"체감 밀도 동일"* 이라,
    //   벽이 있던 시절의 **화면 안 적 수**에 맞추는 배수를 여기 한 번만 곱한다.
    //   `FIELD.densityCal` 은 실측으로 잡은 값이다(config 의 그 줄 참조).
    // 캠페인 기본 런의 속도 예산은 카드별 들쭉날쭉한 적합값이 아니라 밤1 raw 예산을
    // 고정점으로 한 +0.25%/일 곡선을 쓴다. 카드 예산은 종별 추첨 비율로 남고,
    // 마르면 같은 비율로 리필된다. `opts.spawnMult` 를 명시한 프로브만 우회한다.
    // 파형 — 카드가 든다(config 의 WAVE_OF). 없으면 ramp(기본 3막 곡선).
    //   ⚠ opts 가 이긴다 — 스윕·프로브의 지시가 곧 실험 조건이다(opts.spawnMult 와 같은 문법).
    this.waveKind = opts.wave ?? night.wave ?? 'ramp';
    const useCampaignSpawn = canon && opts.spawnMult == null && !night.sabbath;
    this._campaignSpawn = useCampaignSpawn;
    this._campaignBossCut = useCampaignSpawn && !!this.boss;
    this.volumeFactor = useCampaignSpawn ? nightVolumeFactor(this.nightKey) : null;
    this.spawnTarget = useCampaignSpawn ? campaignSpawnTarget(this.nightKey) : this.totalBudget;
    // 캠페인 총량에는 예약 스폰도 포함한다. 무리전은 `spawn()`의 누산기 밖에서
    // `m.spawned`를 올리므로, 여기서 그 몫을 먼저 떼지 않으면 N13만 9마리가 덧붙는다.
    // 수는 새 상수가 아니라 카드 자체의 예약 횟수 × 한 번의 무리 수에서 파생한다.
    this._campaignScriptedSpawns = useCampaignSpawn
      ? (night.pack?.at?.length ?? 0) * (night.pack?.n ?? 0)
      : 0;
    if (useCampaignSpawn) {
      // 밤1의 현행 실출생을 고정점으로 둔다. 밤1 ramp의 마지막 2분 55% 중 20%가
      // 잘리므로 실효 몫은 0.89이며, 아래 함수가 기존 파형 값에서 그대로 유도한다.
      // 각 밤의 wave·tailCut·bossCut을 분모에 함께 넣어 이 셋은 총량이 아니라
      // 오직 밤 안의 배분만 바꾼다.
      const referenceShare = campaignReferenceEffectiveShare();
      const campaignMass = this.spawnTarget * FIELD.densityCal * referenceShare;
      const streamedMass = Math.max(0, campaignMass - this._campaignScriptedSpawns);
      this.spawnScale = streamedMass
                      / (avgEffectiveSpawnRate(this.endSurge, this.waveKind,
                                               this._campaignBossCut) * NIGHT_SEC);
    } else {
      // 명시적 spawnMult 실험과 변형 밤은 종전 계산을 그대로 쓴다.
      this.spawnScale = this.spawnTarget * FIELD.densityCal
                      / (avgSpawnRate(this.endSurge, this.waveKind) * NIGHT_SEC);
    }

    // ── 7-2 구출 대상 ─────────────────────────────────────────
    // 양의 위치는 랜덤. **최소 거리 제약이 ④의 측정 대상**이므로 여기서는 거리를
    // 주입받고(rescueDist), 방향만 뽑는다. 미지정이면 필드 안 균등 랜덤.
    // **【10-B 5】 양은 밤의 2/3(현재 6:40)에 스폰한다.** 그 전에는 필드에 없다.
    //   구 구현은 생성자에서 양을 놓았고, 실측에서 거리 40·80유닛일 때 패널 전에 발견되는
    //   런이 나왔다(7-2 "앞 2/3는 평범한 밤"이 운에 먹혔다).
    //   거리 제약은 **스폰 시점의 플레이어 위치**를 기준으로 잰다 — 그래야 "6분 40초 뒤 그가
    //   선 자리에서 얼마나 먼가"라는 설계 질문과 측정이 같은 것을 가리킨다.
    this.sheep = null; this.panelOn = false; this.foundAt = null; this.foundBy = null;
    this.rescueDist0 = null;
    this._rescueDistOpt = opts.rescueDist ?? null;

    // ── 【10-B 1】 「물웅덩이」 — 회복 3체제 ① ─────────────
    // 고정 위치 + 쿨다운. 자동으로 마신다(입력은 이동 하나 — 2-1).
    // 자리는 시드로 고정된다 = **그 밤은 항상 같은 자리**. 외우면 이득이 되고,
    // 외운 자리로 가는 길이 밤마다 달라진다(적 분포가 다르므로).
    this.water = [];
    this.waterOn = opts.water ?? WATER.on;
    this.waterCfg = { ...WATER, ...(opts.waterCfg ?? {}) };
    // ⚠ **마름은 그룹이 든다** — 우물 개체가 아니다(config.WATER 머리말).
    //   멀어진 칸은 버려졌다 다시 만들어지므로, 개체가 들면 왕복 한 번에 쿨다운이 리셋된다.
    this.waterDry = new Array(Math.max(1, this.waterCfg.perCell | 0)).fill(0);
    this._waterCells = new Map();        // "cx,cy" → 그 칸의 우물 셋
    this._waterCx = null; this._waterCy = null;
    if (this.waterOn && this.waterCfg.perCell > 0) this.syncWaterCells();

    // ── 【10-B 6-2】 빌드 — 레벨업이 무기에 실제로 반영된다 ────
    // **구 회색박스는 레벨업을 세기만 하고 무기에 먹이지 않았다.** 그래서 후반 밤이
    // 전부 포화로 끝나 서로 구분되지 않았다(10-A 미해결 ③의 직접 원인).
    //   'ramp'  집은 레벨업 1회당 중첩 +0.263(3택에 뜰 확률, 상한 5) + 축 진척 = 밤/30  ← **새 기본값**
    //   'none'  무강화 — 구 회색박스와 같은 자. 차수 간 비교용으로 남긴다
    //   'mid'/'max'  명세 8장 공급 검산에 쓰는 고정 빌드
    this.build = opts.build ?? 'ramp';
    // ⚠ **사장님 지시(2026-07-29) — 「각 밤의 시작은 1밤 시작과 동일해야 한다.
    //   그 대신 치즈는 계속 이어지게.」**
    //   구 구현은 `axisProgressFor(밤번호/30)` 으로 **밤 번호만 보고 플레이어를 자동으로
    //   키웠다.** 그러면 아무것도 안 사도 30밤째엔 저절로 세진다 — 낮에 치즈로 축을
    //   사는 것과 **두 개의 성장 채널**이 겹쳐 있었던 셈이다.
    //   이제 채널은 하나다: **낮에 산 3축이 `dayMods` 로 들어오는 것**(effects.js →
    //   gb/dayin.js 17훅)뿐이고, 밤 번호는 플레이어를 안 키운다.
    //   ※ `axisProgressFor(1)` 로 두는 이유 — 「밤 1과 **동일**」이 지시이므로 0이 아니라
    //     N1 이 받던 그 값이다. `opts.axis` 로 직접 넘기는 경로(스윕·프로브)는 그대로 산다.
    //   ※ 이 한 줄이 「NIGHT_NO 가 10밤짜리라 나머지가 1로 떨어진다」던 화석도 같이 닫는다 —
    //     이제 **모든 밤이 같은 값**이라 표에 없는 밤이라는 개념 자체가 사라진다.
    this.axisProg = opts.axis ?? axisProgressFor(1);

    // ── 【10-B 6-2b】 레벨 요구량 직접 지정 ────────────────────
    // **구 구현의 두 번째 구멍**: `flatStep()` 이 `night.ledgerPieces`(산출기 v2.0 값)로
    // 요구량을 잡는데, 그 값은 **10-A 이전 스폰 곡선**의 산물이다. N1 원장 조각 406에
    // 맞춰 요구량 30을 쓰지만 10-A ① 곡선에서 실제 수입은 그 4배가 넘는다.
    //   → 실측 **레벨업 52회/밤**. 4-4 목표는 **12~15회**다. 3.5~4.3배 부풀어 있었다.
    // 원장 재스냅샷 전까지는 회색박스가 자기 실측 수입으로 요구량을 잡아야 한다.
    // headless_sss ⑥ 이 (수입 → 요구량) 을 수렴할 때까지 반복해 표를 낸다.
    // 실 플레이 정본은 밤마다 갈리던 측정표·원장 fallback을 타지 않는다.
    // 명시적 `opts.levelStep`은 계측 도구의 지시이므로 그대로 우선한다.
    const campaignNight = this.nightKey && NIGHTS[this.nightKey] === night && !night.sabbath;
    this.levelStep = opts.levelStep ?? (campaignNight ? CAMPAIGN_LEVEL_BASE : null);
    this._ws = null;

    // ── 【부품 재설계】 물맷돌 장비 4칸 ────────────────────────────
    // **물맷돌은 부품, 지팡이는 기존 카드.** 지팡이가 잠정 보류이므로 두 경로가
    // 당분간 공존한다(설계안 11장). 갈아엎은 것은 물맷돌 쪽뿐이다.
    //   opts.gear = 사람이 상점에서 낀 것(정본)
    //   없으면 build 모드로 만든다 — none 시작장비 / ramp 밤 진행률 / max 만렙 대조군
    this.gearKinds = null;
    this.gear = null;
    if (this.weapon === 'pasu') {
      this.gear = opts.gear ?? null;
      // 모든 급 조합이 정식 장비다. 낮은 급 짝은 resolveGear의 돌 피해 전달과 축 감쇠를
      // 그대로 받고, 직접 넣은 측정 장비도 상점 장비와 같은 경로로 계산한다.
      if (!this.gear) {
        this.gearKinds = pickKinds(this.rnd);
        this.gear = this.build === 'max'  ? maxGear(this.gearKinds)
                  : this.build === 'ramp' ? rampGear(this.gearKinds, 0)
                  : startGear();
      }
    }

    // 계측 — 이 회색박스의 존재 이유
    this.m = {
      refills: 0,                   // 통장을 다시 채운 횟수 — 0 이면 「원래 안 말랐다」
      killsPlayer: 0, killsDog: 0,
      hits: 0, dmgTaken: 0, hitsSwarm: 0, hitsElite: 0, hitsThreat: 0,
      dmgTrace: [],                 // ② 해석용 — [t, 누적 피해] 쌍
      downs: 0, downAt: null,
      moveDist: 0, idleSec: 0,
      noHitMax: 0,
      stabSqSum: 0, stabN: 0, crisisSec: 0, crisisEnters: 0,   // 대표 반경 전용(판정선 4종)
      shots: 0, shotHits: 0,
      // **동시 투사체 최고치** — 튐·조각 연쇄·무한 관통이 붙으면서 이것이 곧 프레임
      //   예산이 됐다. 12분 밤에서 몇까지 가는지 재는 것이 이번 단계의 판정선이다.
      maxShots: 0,
      skips: 0,                     // 물수제비가 실제로 튄 횟수
      skipBends: 0,                 // 그 중 **적으로 꺾인** 횟수 — 헛튐이 아닌 몫
      // ⚠⚠ **「예약한 간격」이 아니라 「실제로 움직인 거리」다.**   【검증 2026-07-30】
      //   직전 판은 `markSkip` 에서 `s.sgap`(= 다음 자국까지 **날아가야 하는** 거리)를
      //   더했다. 그런데 프레임 양자화 때문에 실제 이동은 항상 그보다 크다 — 한 프레임
      //   10.33px(620px/초 ÷ 60fps) 미만의 간격도 10.33px 를 움직인다. 게다가 **마지막
      //   자국의 간격은 발이 죽어 실제로 못 가는데도** 더해졌다. 실측 어긋남:
      //     ●●●●● 실좌표 18.66유닛  vs  예약 합 14.21유닛 (**−24%**)
      //   그 값으로 「커버리지 1.50배(예고 1.48배)」를 합격 판정했으므로, 측정이 아니라
      //   **같은 등비 공식을 두 번 계산한 것**이었다. 실좌표로 재면 t1→t5 는 2.06배다.
      //   → 이제 **자국에서 자국까지 실제 좌표 거리**를 더한다.
      skipDist: 0,                  // 자국 사이를 실제로 움직인 거리 합(px)
      skipSpans: 0,                 // 그 거리를 만든 구간 수 — 평균 간격 = skipDist/skipSpans
      blasts: 0,                    // 굳은 세트 — 「또 터진」 횟수
      lineEchoes: 0, skipAnchorBeats: 0, skipAnchors: 0, shardSeeks: 0,
      orbitSweeps: 0, splashCrowns: 0, splashCrownDmg: 0, splashCrownBaseDmg: 0,
      // ◆ 세트 전용 훅이 실제 판정까지 닿았는지. 두 피해 합은 무거운 ◆의 50% 계약을 잰다.
      setSec: 0,                    // 세트가 켜져 있던 시간(초)
      spawned: 0,
      eaglesSpawned: 0, eaglesKilled: 0, eaglesEscaped: 0,
      eagleImpacts: 0, eagleImpactHits: 0, eagleDamageEvents: 0,
      enemySum: 0, enemyN: 0,
      atCapSec: 0,                  // ⑤ — 동시 개체 상한에 붙어 있던 시간(절벽의 직접 원인)
      dropped: 0, pickedNight: 0, swept: 0,
      levelUpsNight: 0, levelUpTimes: [],
      levelUpsTotal: 0,
      // 【10-B 1】 물가 — ①의 6-5 완주율 해석에 쓰인다
      drinks: 0, healed: 0, healTrace: [],   // healTrace = [t, 회복량] 쌍
      // 종 행동 계측. 스폰 원장과 분리해 행동이 총량을 바꿨는지 독립 검산한다.
      behavior: createBehaviorMetrics(),
      // 【10-B 2-1】 밀집도 — ②의 k 결정에 쓰인다
    };
    this.enemyBehavior = new EnemyBehaviorDirector({
      seed: this._seed, nightKey: this.nightKey, metrics: this.m.behavior,
    });
    if (this.boss) this.enemyBehavior.count(this.boss.kind, 'spawn');
    this._lastPos = { x: this.player.x, y: this.player.y };
    this.refreshWeapon();     // this.m 이 선 뒤라야 한다 — stack 이 levelUpsNight 를 읽는다

    // 분리 격자(적끼리 겹치지 않게) — 헤드리스에서 O(n²)를 못 버틴다
    // ⚠⚠ **격자는 다윗을 따라다닌다** 【벽 제거 2026-08-02】
    //   구 구현은 필드 크기로 칸을 잡고 좌표를 `clamp(…, 0, cols−1)` 했다. 벽이 없어진
    //   지금 그대로 두면 **멀리 있는 적이 전부 가장자리 칸 하나에 뭉쳐** 분리 판정이
    //   그 칸에서 O(n²)로 터진다. 그래서 격자의 원점을 매 프레임 다윗에 맞춘다
    //   (`_gridOx/_gridOy`). 크기는 **치움 반경**이면 충분하다 — 그 밖의 적은 없다.
    this._cell = UNIT;
    const span = Math.ceil((Math.hypot(FIELD.viewW, FIELD.viewH) / 2 + FIELD.spawnPad)
                           * FIELD.cullMul * 2 / this._cell) + 4;
    this._cols = span; this._rows = span;
    this._gridOx = 0; this._gridOy = 0;
    this._head = new Int32Array(this._cols * this._rows);
    this._next = new Int32Array(512);
    this._separationFlags = new Uint8Array(512);
    this._separationNearSq = new Float64Array(512);
  }

  // ── 빌드 반영 — 레벨업할 때마다 다시 계산한다. 매 프레임 계산할 이유가 없다 ──
  // 【2026-07-31】 구 「카드 중첩(stack)」 게터는 카드 체계와 함께 삭제됐다.
  get axis() {
    if (this.build === 'ramp') return this.axisProg;
    return BUILD[this.build]?.axis ?? 0;
  }
  refreshWeapon() {
    // ── 【부품 재설계】 물맷돌이 gear 를 물고 있으면 그쪽이 정본이다 ──────
    // 카드·중첩·갈래·진화를 전면 대체한다(`아흔아홉_물맷돌강화트리_설계안.md`).
    // **반환 모양이 같으므로 아래 판정부는 무엇이 바뀐 줄도 모른다** — mods.js 가
    // 세워 둔 벽 그대로다. 지팡이는 **잠정 보류**라 여기 안 들어온다(설계안 11장).
    if (this.gear && this.weapon === 'pasu') {
      // ramp 는 **밤 진행률이 등급을 정한다.** 게이팅을 밤 시계에 걸었으므로
      // 레벨업 횟수(=경제)가 어디로 착지하든 이 곡선은 안 움직인다(설계안 3-4).
      if (this.gearKinds && this.build === 'ramp') {
        this.gear = rampGear(this.gearKinds, (this.t ?? 0) / NIGHT_SEC);
      }
      const ax = 1 + 0.1 * Math.min(1, Math.max(0, this.axis ?? 0));
      this.mods = mergeDayIn(gearMods(this.gear), this.dayIn);   // ← 낮 3축이 여기서 합쳐진다
      this._ws = gearWeaponStats(this.mods, PASU, ax);
      return;
    }
    // 【2026-07-31】 지팡이(카드 스택) 경로 · axisFromStacks · 진화(evoNeed·kyeolStack·
    //   checkEvolution)가 여기 있었다 — **카드 체계와 함께 삭제.** 무기는 물맷돌 하나다.
    throw new Error('refreshWeapon: gear 없는 무기 경로는 삭제됐다 (지팡이 삭제 2026-07-31)');
  }

  // ── 【10-B 1】 물가 배치 ───────────────────────────────────
  // 필드 안쪽(margin)에 서로 minGap 이상 떨어뜨려 놓는다. 기각 표집이고, 시도가
  // 다하면 간격 조건을 완화한다 — 필드가 좁은데 count 가 크면 배치가 불가능해서다.
  // **플레이어 시작 위치에서도 minGap 만큼 뗀다**: 시작하자마자 마시면 밤의 앞부분이
  // 공짜가 되고, 회복이 「가는 것」이 아니라 「거기 있는 것」이 된다.
  // ── 우물 — **화면 격자로 끝없이 타일링한다** 【사장님 지시 2026-08-07】 ──
  //  한 칸(FIELD.viewW×viewH)에 가·나·다 셋. 자리별로 연동되므로 `g` 가 곧 연동 묶음이다.
  //  ⚠ **배치는 칸 좌표 해시로 뽑는다 — 밤 난수열을 안 쓴다** 【2026-08-04 실측된 병】.
  //    구 판은 `this.rnd` 에서 뽑아서, 우물 개수를 바꾸는 순간 그 뒤의 모든 스폰 난수가
  //    밀렸다(같은 시드가 다른 밤이 됐다 — density_check 가 +16.5% 로 튀었다).
  //    이제는 걸어 다니며 칸이 생겨도 밤은 한 톨도 안 흔들린다 — 그게 해시여야 하는 이유다.
  cellWater(cx, cy) {
    const c = this.waterCfg, CW = FIELD.viewW, CH = FIELD.viewH;
    const rnd = mulberry(cellHash(this._seed ?? 7, cx, cy));
    const n = Math.max(1, c.perCell | 0);
    const x0 = cx * CW, y0 = cy * CH;
    // ⚠⚠ **기각 표집을 버리고 칸을 나눈다** 【결함 수선 2026-08-07】.
    //   구 판은 「minGap 만큼 떨어질 때까지 다시 뽑기」였는데 **자리가 없어서 거의 늘
    //   실패했다**: 여백 12유닛(384px)을 사방에서 떼면 배치 창이 1152×312 밖에 안 남고,
    //   거기에 576px 씩 떨어진 세 점은 사실상 못 놓는다. 실패하면 아래 「그냥 아무 데나」
    //   가지로 떨어져 **minGap 이 통째로 안 걸렸다**(실측 75쌍 중 33쌍 위반).
    //   덤으로 우물 셋이 화면 세로 29% 띠에 몰려 「한 화면에 3개」가 무색했다.
    //   → 칸을 **세로 띠 n 개**로 나누고 띠마다 하나씩 놓는다. 늘 성공하고, 간격이
    //     기각이 아니라 **구조**로 보장된다.
    //   ⚠ 여백(`margin`)은 벽이 있던 시절의 「벽에 붙은 샘 = 죽는 자리」 규칙이었다.
    //     벽이 없어졌으므로 칸 경계에서만 조금 떼고, 세로는 거의 다 쓴다.
    const band = CW / n;
    const mx = Math.min(c.margin, band * 0.28);      // 띠 안쪽 여백 — 이웃 띠와 겹치지 않게
    const my = Math.min(c.margin, CH * 0.14);        // 위아래는 조금만 뗀다(칸 경계 표시)
    const sx = this.W / 2, sy = this.H / 2;
    const inStart = Math.floor(sx / CW) === cx && Math.floor(sy / CH) === cy;
    const out = [];
    for (let g = 0; g < n; g++) {
      const bx0 = x0 + g * band + mx, bx1 = x0 + (g + 1) * band - mx;
      const x = bx0 + rnd() * Math.max(1, bx1 - bx0);
      let y = y0 + my + rnd() * Math.max(1, CH - my * 2);
      // 다윗이 **선 자리**는 피한다 — 첫 프레임에 공짜로 마시면 그 밤의 첫 판단이 사라진다.
      //   피하는 방향은 같은 띠 안의 위/아래 끝이라 배치가 결정적으로 남는다.
      if (inStart && Math.hypot(x - sx, y - sy) < c.minGap) {
        y = (y <= sy) ? y0 + my : y0 + CH - my;
      }
      out.push({ x, y, g, dry: 0 });
    }
    return out;
  }

  // 플레이어 둘레 3×3 칸만 들고 있는다. **무한 들판에서 목록이 자라면 안 된다.**
  //   버린 칸을 다시 밟아도 배치는 해시라 그대로고, 쿨다운은 그룹이 들고 있어 그대로다.
  syncWaterCells() {
    const c = this.waterCfg;
    if (!this.waterOn || !(c.perCell > 0)) return;
    const CW = FIELD.viewW, CH = FIELD.viewH, p = this.player;
    const cx = Math.floor(p.x / CW), cy = Math.floor(p.y / CH);
    if (cx === this._waterCx && cy === this._waterCy) return;   // 같은 칸이면 할 일이 없다
    this._waterCx = cx; this._waterCy = cy;
    const keep = new Set();
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const k = `${cx + dx},${cy + dy}`;
        keep.add(k);
        if (!this._waterCells.has(k)) this._waterCells.set(k, this.cellWater(cx + dx, cy + dy));
      }
    }
    for (const k of this._waterCells.keys()) if (!keep.has(k)) this._waterCells.delete(k);
    this.water = [];
    for (const k of keep) for (const w of this._waterCells.get(k)) this.water.push(w);
  }

  // 그룹 쿨다운을 흘리고, **씬이 읽는 개체 `dry` 에 거울처럼 비춘다**(씬은 안 고친다).
  stepWater(dt) {
    for (let g = 0; g < this.waterDry.length; g++) {
      if (this.waterDry[g] > 0) this.waterDry[g] = Math.max(0, this.waterDry[g] - dt);
    }
    for (const w of this.water) w.dry = this.waterDry[w.g] ?? 0;
  }

  // ── 【10-B 5】 6:40 양 스폰 ────────────────────────────────
  // 거리는 **이 시점의 플레이어 위치** 기준으로 잡는다. 방향만 뽑고, 미지정이면
  // 필드 안 균등 랜덤(구 생성자 구현과 같은 분포 — 시점만 옮겼다).
  spawnSheep() {
    const p = this.player;
    let x, y;
    // ⚠ 【벽 제거 2026-08-02】 가두던 `clamp` 를 뺐다 — 이제 **거리가 곧 거리**다.
    //   구 판은 필드 안으로 접어서, 멀리 두려 해도 벽에 붙어 실제 거리가 짧아졌다.
    //   *"양 찾는 밤"* 이 진짜로 찾는 밤이 되는 것이 이 변경의 목적 중 하나다.
    if (this._rescueDistOpt != null) {
      const a = this.rnd() * Math.PI * 2, d = this._rescueDistOpt;
      x = p.x + Math.cos(a) * d;
      y = p.y + Math.sin(a) * d;
    } else {
      // 미지정이면 구 분포와 **같은 넓이**의 사각형에서 뽑되, 다윗을 중심으로 둔다.
      x = p.x + (this.rnd() - 0.5) * (this.W - 80);
      y = p.y + (this.rnd() - 0.5) * (this.H - 80);
    }
    this.sheep = { x, y, r: RESCUE.sheepR };
    this.rescueDist0 = Math.hypot(x - p.x, y - p.y);
  }

  // 반경에 닿으면 자동으로 마신다. 마른 샘은 지나쳐도 아무 일이 없다.
  drinkWater(dt) {
    const p = this.player, c = this.waterCfg, R = c.radius + PLAYER.r, R2 = R * R;
    // 기본 `sipDwell` 은 0이라 **첫 접촉 프레임**에 바로 마신다. 프로브가 머무름 값을
    // 따로 넣을 수 있도록 누적기는 남기되, 실제 게임에는 입력 지연이 없다.
    let nearWet = false;
    for (const w of this.water) {
      // 마름은 **그룹이 든다** — 흘리는 일은 stepWater 가 이미 했다.
      if ((this.waterDry[w.g] ?? 0) > 0) continue;
      const dx = w.x - p.x, dy = w.y - p.y;
      // 경계에서 큰 월드 좌표를 더하고 빼며 생기는 부동소수점 오차로 접촉을 놓치지 않는다.
      if (dx * dx + dy * dy > R2 + 1e-6) continue;
      nearWet = true;
      if ((this._sipT ?? 0) < (c.sipDwell ?? 0)) continue;
      // 【2026-08-06】 무화과(뭉치=영구/몇 알=그 밤) — 한 모금이 더 달다
      const heal = Math.min(this.maxHp * c.heal * (this.dayIn?.waterHealMul ?? 1),
                            this.maxHp - p.hp);
      // **가득 찬 채로 지나가면 샘은 마르지 않는다.** 안 마셨으니까.
      // 이게 없으면 "멀쩡할 때 밟아 놔서 정작 필요할 때 말라 있다"는 벌칙이 생기는데,
      // 입력이 이동 하나뿐이라 플레이어는 그걸 피할 방법이 없다(밟지 않고 지나갈 수 없다).
      if (heal <= 0) continue;
      // 【사장님 지시 2026-08-07】 **자리별 연동** — 이 우물의 「가」를 마시면 들판의
      //   모든 「가」가 마른다. 나·다는 그대로다. 다음 화면으로 뛰어도 그쪽 「가」는
      //   이미 말라 있으므로, 도망으로 회복을 파밍하는 길만 정확히 막힌다.
      //   「가득 차면 안 마른다」 규칙은 그대로다 — 안 마셨으면 아무것도 마르지 않는다.
      this.waterDry[w.g] = c.cooldown;
      // 거울은 stepWater 가 매 프레임 맞추지만, **마신 프레임에 바로** 맞춰 둔다 —
      // 안 그러면 그 한 프레임 동안 같은 그룹의 다른 우물이 아직 물로 그려진다.
      for (const o of this.water) if (o.g === w.g) o.dry = c.cooldown;
      this.m.drinks++;
      this._sipT = 0;
      p.hp += heal; this.m.healed += heal;
      // **회복 궤적은 「마신 시각」만 남긴다.**
      // ①이 임의의 HP·회복률에 대해 해석적으로 재생하려면 이 런에서 실제로 회복한
      // 양이 아니라 시각이 필요하다 — 다른 HP에서는 상한이 다른 자리에 걸린다.
      // (HP=∞ 런에서는 항상 결손이 있으므로 「닿으면 반드시 마신다」가 되고,
      //  재생은 그만큼 **보수적**이다 — 실제 게임은 이보다 샘을 덜 쓴다.)
      this.m.healTrace.push(this.t, 1);
    }
    // 사용자 지정 머무름 값이 있을 때만 의미가 있다. 벗어나면 **그 자리에서 0** 이다.
    this._sipT = nearWet ? (this._sipT ?? 0) + dt : 0;
  }

  // 위기선 = 정산표의 ×2.0 경계. 표를 고치면 여기도 따라온다.
  get crisisLine() { return LOSS_TABLE.find(r => r.mult === 2.0)?.min ?? 40; }
  get stability() { return this.tracks[this.primary].s; }
  get panic() { return this.tracks[this.primary].panic; }
  get inRad() { return this._inRadPrimary ?? 0; }

  // **계획 스폰율(마리/초)** — 캠페인은 tail·wave·boss와 예약 무리까지 포함한
  // 실효 평균이다. 명시적 spawnMult 실험은 비교 계약을 깨지 않도록 종전 값을 돌려준다.
  get plannedPerSec() {
    if (!this._campaignSpawn)
      return this.spawnScale * avgSpawnRate(this.endSurge, this.waveKind);
    return this.spawnScale
         * avgEffectiveSpawnRate(this.endSurge, this.waveKind, this._campaignBossCut)
         + this._campaignScriptedSpawns / NIGHT_SEC;
  }
  // 화면과 리포트가 `m.spawned`(post-shape 실출생)와 같은 단위로 비교할 분모.
  // `spawnTarget`은 pre-density 인터페이스라 직접 표시하면 N1도 11,602/14,170으로
  // 거짓 미달처럼 보인다. 프레임 적분 반올림은 전수 계약상 ±1을 허용한다.
  get plannedSpawnTotal() { return Math.round(this.plannedPerSec * NIGHT_SEC); }

  // 큰 3막: 0~2분 정찰 → 2~8분 본대 → 8~10분 마지막 파도.
  spawnRate() {
    const p = this.t / NIGHT_SEC;
    const r = spawnRateAt(p, this.endSurge, this.waveKind);
    // ⚠ **마지막 2분만 실제로 깎는다** 【사장님 지시 2026-08-02 — SPAWN_SHAPE.tailCut】
    //   캠페인의 `avgEffectiveSpawnRate`도 이 곱을 읽어 밤끼리 총량을 맞춘다. 따라서
    //   파형 안에서는 앞 구간 대비 끝만 0.8배이고, 다른 밤보다 총량이 낮아지지는 않는다.
    //   명시적 spawnMult 실험은 종전 raw 평균 계약을 그대로 쓴다.
    return p >= SPAWN_SHAPE.tailFrom ? r * SPAWN_SHAPE.tailCut : r;
  }
  get phase() {
    const p = this.t / NIGHT_SEC;
    const [scoutEnd, mainEnd, lullEnd, waveEnd] = SPAWN_SHAPE.phaseEnds;
    if (p < scoutEnd) return '정찰';
    if (p < mainEnd) return this.cfg.howl ? '하울링 → 본대' : '본대(무예고)';
    if (p < lullEnd) return '소강';
    if (p < waveEnd) return '마지막 파도';
    return '밤의 끝';
  }

  // ══════════════════════════════════════════════════════════════
  //  **화살표 구간** — 마지막 밤 10분 뒤. 전투는 끝났고 들판은 비어 있다.
  //
  //  ■ 사장님 지시 그대로: **5초마다 방향이 꺾인다** (오른쪽 → 아래 → 오른쪽, 15초).
  //    화살표는 「어디로」만 말하고, 걷는 것은 플레이어다.
  //  ■ 끝에 **잃어버린 양들**이 모여 있다. 한 마리도 안 잃었으면 **딱 한 마리** —
  //    *"나도 몰랐던 잃어버린 양 느낌"*(사장님). 그것이 아흔아홉의 그 하나다.
  //  ⚠ **적을 지운다.** *"빈 들판"* 이라고 하셨고, 남겨 두면 마지막 장면이 전투가 된다.
  //  ⚠ 그림(화살표·양)은 여기서 안 그린다 — `scene.js` 가 `world.finale` 을 읽는다.
  //    월드는 **어디에 무엇이 있는가**만 든다.
  // ══════════════════════════════════════════════════════════════
  enterFinale() {
    this.enemyBehavior?.reset([...this.enemies, ...this.eagles]);
    this.enemies.length = 0;
    this.eagles.length = 0;
    this._eagleScheduleI = this.eagleSchedule.length;
    this.shots.length = 0;
    if (this.boss) {
      this.bossRecord = snapshotBossRecord(this.boss, this.m);
      this.boss = null;
    }
    // The combat loop normally decays these below, but the finale has its own
    // early-return loop. Clear the last lion hit atomically so its shake,
    // hit-stop, or invulnerability pose cannot become a permanent finale state.
    this.shake = 0;
    this.hitstop = 0;
    this.shakeKind = 'normal'; this._shakeAt = -9;
    this._presentKind = null; this._presentAt = -9; this._presentPower = 0;
    this.player.inv = 0;
    const p = this.player;
    const lost = Math.min(FINALE_REUNION_MAX_SHEEP, Math.max(1, this.lostSheep | 0));
                                                         // 0 이면 「나도 몰랐던」 한 마리
    const facingLen = Math.hypot(p.fx, p.fy) || 1;
    this._finale = {
      t: 0, searchT: 0, sampleCarry: 0,
      trailSamples: [{ x: p.x, y: p.y }],
      lastDir: [p.fx / facingLen || 1, p.fy / facingLen || 0],
      lost, flock: [],
      state: 'walk', stateT: 0,
      arrived: false, playerLocked: false, playerLock: null,
      approachProgress: 0, holdT: 0,
      completed: false, completedBy: null,
    };
    this.m.finaleSheep = lost;
  }

  /** 피날레 암전이 따라갈 실제 마지막 이동 방향. 고정 목적지 방향은 없다. */
  finaleDir() {
    const f = this._finale;
    if (!f) return null;
    return f.lastDir;
  }
  get finale() {
    const f = this._finale;
    if (!f) return null;
    const d = this.finaleDir();
    return {
      t: f.t, searchT: f.searchT,
      searchProgress: clamp(f.searchT / FINALE.sec, 0, 1),
      leg: Math.min(2, Math.floor(f.searchT / FINALE.legSec)),
      dir: d, state: f.state, arrived: f.arrived,
      playerLocked: f.playerLocked, flock: f.flock,
      approachProgress: f.approachProgress,
      firstVisibleRadius: FINALE_FIRST_VISIBLE_RADIUS,
      holdT: f.holdT, holdSec: FINALE_REUNION_HOLD_SEC,
      holdProgress: clamp(f.holdT / FINALE_REUNION_HOLD_SEC, 0, 1),
      arrow: null,
    };
  }

  recordFinaleExploration(from, to, moveSec) {
    const f = this._finale;
    if (!f || moveSec <= 0) return;
    const dx = to.x - from.x, dy = to.y - from.y;
    const moved = Math.hypot(dx, dy);
    if (moved <= 1e-9) return;
    f.lastDir = [dx / moved, dy / moved];

    // 실제 이동 0.1초마다 한 점을 남긴다. 과거 점의 반경 안으로 되돌아온 표본은
    // 검색 시간에 더하지 않으므로, 같은 길을 왕복해서 15초를 채울 수 없다.
    let untilSample = FINALE.sampleSec - f.sampleCarry;
    while (untilSample <= moveSec + 1e-9) {
      const u = clamp(untilSample / moveSec, 0, 1);
      const sample = { x: from.x + dx * u, y: from.y + dy * u };
      const retraced = f.trailSamples.some((old) =>
        Math.hypot(sample.x - old.x, sample.y - old.y) <= FINALE.retraceRadius);
      f.trailSamples.push(sample);
      if (!retraced) f.searchT = Math.min(FINALE.sec, f.searchT + FINALE.sampleSec);
      untilSample += FINALE.sampleSec;
    }
    f.sampleCarry = (f.sampleCarry + moveSec) % FINALE.sampleSec;
  }

  spawnFinaleFlock() {
    const f = this._finale;
    if (!f || f.flock.length) return;
    const p = this.player;
    const centreAngle = this.rnd() * Math.PI * 2;
    const spreadAngle = this.rnd() * Math.PI * 2;
    const cx = p.x + Math.cos(centreAngle) * FINALE.sheepNear;
    const cy = p.y + Math.sin(centreAngle) * FINALE.sheepNear;
    for (let i = 0; i < f.lost; i++) {
      const a = spreadAngle + i * FINALE_GOLDEN_ANGLE;
      const r = FINALE.sheepSpread * Math.sqrt((i + 0.5) / f.lost);
      const x = cx + Math.cos(a) * r;
      const y = cy + Math.sin(a) * r;
      const h0 = ((i * 29 + f.lost * 11) % 17) / 16;
      const h1 = ((i * 13 + f.lost * 7 + 3) % 19) / 18;
      f.flock.push({
        x, y, startX: x, startY: y, targetX: x, targetY: y,
        delay: 0.12 + (i % 6) * 0.11 + h0 * 0.12,
        speed: 104 + h1 * 34,
        arrivalRadius: 2.5 + h1 * 2,
        arc: (i % 2 ? -1 : 1) * (6 + h0 * 10),
        duration: 0, progress: 0, arrived: false,
      });
    }
  }

  beginFinaleApproach() {
    const f = this._finale;
    const p = this.player;
    f.arrived = true;
    f.playerLocked = true;
    f.playerLock = { x: p.x, y: p.y };
    f.state = 'sheepApproach';
    f.stateT = 0;

    // 36px 간격의 다층 링. 각 k번째 링은 최대 6k마리만 받아 같은 링의 현 간격도
    // 36px보다 작아지지 않는다. 인접 링의 반경 차 역시 36px라 모든 쌍이 안전하다.
    const n = Math.max(1, f.flock.length);
    let ringN = 1, ringCapacity = 6;
    while (ringCapacity < n && ringN < FINALE_REUNION_MAX_RINGS) {
      ringN++;
      ringCapacity += 6 * ringN;
    }
    const weightSum = ringN * (ringN + 1) / 2;
    const ringCounts = [];
    const remainders = [];
    let assigned = 0;
    for (let ring = 1; ring <= ringN; ring++) {
      const raw = n * ring / weightSum;
      const base = Math.floor(raw);
      ringCounts.push(base);
      remainders.push({ ring: ring - 1, fraction: raw - base });
      assigned += base;
    }
    remainders.sort((a, b) => b.fraction - a.fraction || b.ring - a.ring);
    for (let i = 0; assigned < n; i++, assigned++) {
      ringCounts[remainders[i % remainders.length].ring]++;
    }

    const targets = [];
    for (let ring = 1; ring <= ringN; ring++) {
      const count = ringCounts[ring - 1];
      const radius = ring * FINALE_REUNION_RING_STEP;
      const phase = Math.PI * 0.35 + (ring - 1) * FINALE_GOLDEN_ANGLE;
      for (let slot = 0; slot < count; slot++) {
        const a = phase + slot * Math.PI * 2 / count;
        targets.push({
          x: p.x + Math.cos(a) * radius,
          y: p.y + Math.sin(a) * radius,
          radius, ring,
        });
      }
    }

    const sheepOrder = f.flock.map((m, i) => ({
      i, d2: (m.x - p.x) ** 2 + (m.y - p.y) ** 2,
    })).sort((a, b) => b.d2 - a.d2 || a.i - b.i);
    const available = targets.slice();
    for (const { i } of sheepOrder) {
      const m = f.flock[i];
      let pick = 0, best = Infinity;
      for (let j = 0; j < available.length; j++) {
        const d2 = (available[j].x - m.x) ** 2 + (available[j].y - m.y) ** 2;
        if (d2 < best) { best = d2; pick = j; }
      }
      const target = available.splice(pick, 1)[0];
      m.startX = m.x; m.startY = m.y;
      m.targetX = target.x;
      m.targetY = target.y;
      m.targetRadius = target.radius;
      m.targetRing = target.ring;
      const travel = Math.hypot(m.targetX - m.startX, m.targetY - m.startY);
      const minDuration = 3.85 + (((i * 7 + n) % 9) / 8) * 0.35;
      const maxDuration = 5.15 + (((i * 11 + n) % 7) / 6) * 0.30;
      m.duration = clamp(travel / m.speed, minDuration, maxDuration);
      m.progress = 0;
      m.arrived = false;
    }
  }

  stepFinale(dt, input) {
    const f = this._finale;
    f.t += dt;
    if (f.state === 'walk') {
      const from = { x: this.player.x, y: this.player.y };
      this.movePlayer(dt, input);
      this.collectPieces();                     // 바닥에 남은 조각은 걸어가며 주울 수 있다
      const moved = Math.hypot(this.player.x - from.x, this.player.y - from.y);
      const speed = Math.max(1e-9, PLAYER.speed * this.mods.moveMul);
      this.recordFinaleExploration(from, this.player, moved / speed);
      if (f.searchT >= FINALE.sec - 1e-9) {
        this.spawnFinaleFlock();
        this.beginFinaleApproach();
      }
      return;
    }

    // 합류가 시작된 뒤에는 어떤 이동 입력도 다윗을 밀어내지 못한다.
    if (f.playerLock) {
      this.player.x = f.playerLock.x;
      this.player.y = f.playerLock.y;
    }
    f.stateT += dt;

    if (f.state === 'sheepApproach') {
      let progressSum = 0;
      let joined = 0;
      for (const m of f.flock) {
        const u = clamp((f.stateT - m.delay) / Math.max(0.001, m.duration), 0, 1);
        const eased = u * u * (3 - 2 * u);
        const dx = m.targetX - m.startX, dy = m.targetY - m.startY;
        const len = Math.hypot(dx, dy) || 1;
        // 얕고 교차하는 곡선으로 접근해 직선 보간처럼 미끄러져 보이지 않게 한다.
        const bow = Math.sin(Math.PI * u) * m.arc;
        m.x = m.startX + dx * eased + (-dy / len) * bow;
        m.y = m.startY + dy * eased + ( dx / len) * bow;
        m.arrived = u >= 1 || Math.hypot(m.targetX - m.x, m.targetY - m.y) <= m.arrivalRadius;
        m.progress = m.arrived ? 1 : u;
        if (m.arrived) {
          m.x = m.targetX; m.y = m.targetY;
          joined++;
        }
        progressSum += m.progress;
      }
      f.approachProgress = progressSum / Math.max(1, f.flock.length);
      if (joined === f.flock.length) {
        f.approachProgress = 1;
        f.state = 'reunionHold';
        f.stateT = 0;
        f.holdT = 0;
      }
      return;
    }

    if (f.state === 'reunionHold') {
      f.holdT = Math.min(FINALE_REUNION_HOLD_SEC, f.holdT + dt);
      if (f.holdT >= FINALE_REUNION_HOLD_SEC) {
        f.completed = true;
        f.completedBy = 'reunion';
        f.state = 'ending';
        this.over = true;
        this.result = '구출';
        // 이 밤만 끝낸다. 다음 화면은 기존 night-result handoff가 결정한다.
        this.finish();
      }
    }
  }

  // 화면 전체 채널만 한 문으로 모은다. 개별 입자·자국·피·투사체는 기존 배열에서
  // 즉시 발생하고, 여기서는 동시 사건의 카메라/히트스톱 주인만 고른다.
  present(kind, { shake = 0, hitstop = 0, power = 1, dx = 0, dy = 0 } = {}) {
    const now = Number(this.t) || 0;
    const priority = SCREEN_RESPONSE_PRIORITY[kind] ?? SCREEN_RESPONSE_PRIORITY.normal;
    const currentPriority = SCREEN_RESPONSE_PRIORITY[this._presentKind]
      ?? SCREEN_RESPONSE_PRIORITY.environment;
    const currentAlive = now - (this._presentAt ?? -9) < 0.22;
    const nextPower = Math.max(0, Number(power) || 0);
    const wins = !currentAlive || priority > currentPriority
      || (priority === currentPriority && nextPower > (this._presentPower ?? 0));
    if (wins) {
      const d = Math.hypot(dx, dy);
      this._presentKind = kind;
      this._presentAt = now;
      this._presentPower = nextPower;
      this._presentDx = d > 1e-5 ? dx / d : 0;
      this._presentDy = d > 1e-5 ? dy / d : 0;
      this._presentSeq++;
      // 히트스톱과 카메라도 같은 한 채널이다. 낮은 사건을 더해 시간을 늘리지 않고,
      // 이긴 사건의 가장 강한 값 하나만 남긴다. 국소 타격/처치 효과는 별도 배열에 남는다.
      if (hitstop > 0) this.hitstop = Math.min(HIT.max, Math.max(this.hitstop, hitstop));
      if (shake > 0) {
        const limit = kind === 'hurt' || kind === 'boss' ? SHAKE.knock : SHAKE.cap;
        this.shake = Math.min(limit, Math.max(this.shake, shake));
        this.shakeKind = kind;
        this.shakeDx = this._presentDx;
        this.shakeDy = this._presentDy;
        this._shakeAt = now;
      }
    }
  }

  step(dt, input) {
    if (this.over) return;
    // **히트스톱** — 큰 타격 직후 시간이 아주 잠깐 느려진다. 정지가 아니므로
    // 이동 입력이 안 씹힌다(불가침 1). 밤 시계(this.t)도 같이 느려져야
    // 「10분」이 안 늘어난다 — 그래서 dt 를 여기서 한 번만 줄인다.
    if (this.hitstop > 0) { this.hitstop = Math.max(0, this.hitstop - dt); dt *= HIT.slow; }
    this.t += dt;
    // 12번째 HP tick은 일반 전투 종료/40밤 피날레 분기보다 먼저 확정한다.
    // 그렇지 않으면 600초 프레임에서 보스가 지워져 패널이 1칸 남은 채 끝난다.
    if (this.boss) {
      this.boss.syncClock(Math.min(this.t, NIGHT_SEC));
      if (this.boss.dead && !this.bossDownAt) {
        this.bossDownAt = this.t;
        this.enemyBehavior?.recordKill(this.boss, this.t);
      }
    }
    // 【10-B 5】 양은 6:40에 스폰한다 — 그 전에는 필드에 없다(우연한 조기 발견 차단)
    if (this.rescue && !this.sheep && this.t >= RESCUE.spawnAt) this.spawnSheep();
    if (this.rescue && this.t >= RESCUE.panelAt) this.panelOn = true;
    // ══════════════════════════════════════════════════════════
    //  **마지막 밤은 10분에 바로 끝나지 않는다 — 화살표가 뜬다**
    //    *"12분지나고 … 낮으로 이동하는게 아니라 빈 들판에서 황금색 화살표가 나오는거지."*
    //  ⚠ 이 구간은 **밤 시계 밖**에서 흐른다(`_finale.t` 가 따로 센다). 안 그러면
    //    마지막 밤만 전투가 짧아진다. 10분은 어느 밤이든 10분이다.
    // ══════════════════════════════════════════════════════════
    if (this.t >= NIGHT_SEC) {
      if (this.rescue && !this.foundAt && this.t >= RESCUE.autoFindAt) {
        this.foundAt = RESCUE.autoFindAt; this.foundBy = '자동';
      }
      if (isFinalNight(this.nightKey) && !this._finale) { this.enterFinale(); return; }
      if (!this._finale) { this.over = true; this.result = '생존'; this.finish(); return; }
    }
    if (this._finale) { this.stepFinale(dt, input); return; }

    this.movePlayer(dt, input);
    this.stepEagles();
    // ⚠ **치우기가 스폰보다 먼저다** 【벽 제거 2026-08-02】. 뒤처진 적이 `concurrent`
    //   상한을 물고 있으면 그 프레임의 스폰이 막혀 **화면이 빈다.** 자리를 먼저 비운다.
    this.cullFar();
    this.spawn(dt);
    this.moveEnemies(dt);
    if (this.boss) this.stepBoss(dt);
    this.separate();
    this.contact();
    this.autoWeapon(dt, input);
    this.stepLineEchoes();      // 손에 익은 ◆ — 때가 된 덧선만 투사체로 승격
    this.moveShots(dt);
    // **동시 투사체 최고치** — 튐·조각 연쇄·무한 관통이 붙으면서 이것이 곧 프레임
    //   예산이 됐다. 「폭증하지 않는가」는 이 한 줄로만 답할 수 있다.
    if (this.shots.length > this.m.maxShots) this.m.maxShots = this.shots.length;
    this.stepBlasts();           // 굳은 세트 — 터진 자리가 또 터진다(비면 즉시 반환)
    if (this.mods.setId) this.m.setSec += dt;   // 세트가 켜져 있던 시간
    this.stepFire(dt);
    this.moveDogs(dt);
    this._dt = dt; this.pullPieces(dt); this.collectPieces();
    // ── 무리전(pack)【리듬 2026-08-08 — 13일 클라이맥스】 ────────────
    //   예약 시각마다 정예 셋이 **삼각으로 동시에** 난다. 새 적·새 아트가 아니라
    //   기존 정예의 **동시성**이 보스전으로 읽히게 하는 장치다(하울링의 근원은
    //   한 마리가 아니라 무리였다). 캠페인은 생성자에서 예약 수를 총량에서 먼저
    //   떼어 두므로 이 9마리까지 합쳐 목표가 된다. 모자라면 그만큼만 난다.
    const pk = this.cfg.pack;
    if (pk) {
      this._packI ??= 0;
      while (this._packI < pk.at.length && this.t >= pk.at[this._packI]) {
        const base = this.rnd() * Math.PI * 2, R = this.spawnRing;
        for (let i = 0; i < pk.n; i++) {
          if ((this.budget.elite ?? 0) <= 0) break;
          this.budget.elite--;
          const a = base + i * (Math.PI * 2 / pk.n);
          const enemy = this.createEnemy('elite', this.player.x + Math.cos(a) * R,
                                         this.player.y + Math.sin(a) * R);
          this.enemyBehavior?.admitEnemy(enemy, this.t);
          this.enemies.push(enemy);
          this.m.spawned++;
        }
        this.m.packWaves = (this.m.packWaves ?? 0) + 1;
        this._packI++;
      }
    }

    // 우물 — 걸어 나간 만큼 칸을 깔고(무한 타일), 그룹 쿨다운을 흘린 뒤 마신다.
    if (this.waterOn) { this.syncWaterCells(); this.stepWater(dt); this.drinkWater(dt); }
    this.barks = this.barks.filter(b => this.t - b.t < 4);   // 연출 수명
    // The hot flash is brief, but fragments and the second shock ring need room
    // to decay. Keeping only 0.30s clipped every elite VFX before its authored end.
    this.pops = this.pops.filter(k => this.t - k.t < 0.58);
    this.blood = this.blood.filter(b => this.t - b.t < BLOOD.life);
    this.corpses = this.corpses.filter(c => this.t - c.t < BLOOD.corpseLife);
    this.dmgTexts = this.dmgTexts.filter(d => this.t - d.t < DMGTEXT.life);
    if (this.shake > 0) this.shake = Math.max(0, this.shake - dt / SHAKE.decay);
    // 필드 드랍 — 닿으면 발동, 안 주우면 사라진다
    for (let i = this.drops.length - 1; i >= 0; i--) {
      const d = this.drops[i];
      if (this.t - d.t > GATHER.life) { this.drops.splice(i, 1); continue; }
      const dx = d.x - this.player.x, dy = d.y - this.player.y;
      // N13의 예약 무리전(pack)이 있는 밤만 새 행동 경로 자를 쓴다. 다른 34밤은
      // 기존 GATHER.r 접촉 규칙을 그대로 두어 경제·생존 난수열을 한 칸도 바꾸지 않는다.
      const lim = (this.cfg.pack ? GATHER_TRIGGER_RADIUS : GATHER.r) + PLAYER.r;
      if (dx * dx + dy * dy <= lim * lim) {
        this.drops.splice(i, 1);
        this._gatherUntil = this.t + GATHER.pullSec;   // **화면 전체가 빨려온다**
        this.m.gathers = (this.m.gathers ?? 0) + 1;
        this.present('normal', { shake: SHAKE.shock, power: 0.8 });
        // **그 자리에서 조각이 터진다** — 빨아들일 것을 만들어 준다(mods.GATHER 주석)
        // ⚠⚠ **기준은 `levelNeed` 가 아니라 `levelBase` 다.**   【2026-07-31】
        //   구 구현은 「그 시점 요구량 × 1.7」이었다. 요구량이 밤 안에서 상수이던
        //   시절에는 그것이 곧 `base × 1.7` 이라 문제가 없었는데, 요구량이 **레벨
        //   누진**이 되면서(LEVEL_REQ) 후반 요구량이 base 의 **24배**가 됐고,
        //   그 배수가 이 드랍에 그대로 곱해졌다. 실측(N30·ramp):
        //       조각 28,219(구) → **138,017**  ·  치즈 705 → **3,450**
        //       그러모으기 OFF 로 재면 13,280 — 즉 **조각의 90%를 이 드랍이 만들고 있었다.**
        //   레벨 곡선만 고치라는 지시인데 경제가 5배 움직이면 그건 곡선 수정이 아니다.
        //   → **밤의 기준 요구량(base)** 을 쓴다. 구 동작과 사실상 같은 값이고
        //     (구 `levelNeed` = base × k, k 평균 ≈ 1.1), 곡선과 완전히 분리된다.
        //   ⚠ 부작용 하나는 **의도한 것이다**: 「한 번에 1~2단계」가 초반에는 서너 단계,
        //     후반에는 반 단계가 된다. 그것이 「초반 빠르게 · 후반 느리게」와 같은 방향이다.
        const tot = this.levelBase * GATHER.needMul;
        const per = tot / GATHER.burst;
        for (let k = 0; k < GATHER.burst; k++) {
          const a = (k / GATHER.burst) * Math.PI * 2 + this.rnd() * 0.4;
          const sp = GATHER.burstV * (0.5 + this.rnd() * 0.8);
          const g2 = { x: d.x, y: d.y, v: per, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, born: this.t };
          this.pieces.push(g2);
          const c = this._pcell(d.x, d.y);
          let arr2 = this._pgrid.get(c);
          if (!arr2) this._pgrid.set(c, arr2 = []);
          arr2.push(g2);
          this.m.dropped += per;
        }
        this._gatherAt = this.t;    // 렌더용 — 터지는 순간
      }
    }
    // 튀어나온 조각이 곧 멎는다 — 흡인은 그 뒤에 걸린다
    for (const g of this.pieces) {
      // ⚠ **먹은 조각은 건드리지 않는다.** 죽은 조각도 `vx` 가 남아 있을 수 있는데,
      //   그것을 옮기면서 격자에 다시 꽂으면(`_reseatPiece`) **이미 먹은 것이 다시
      //   주울 수 있는 상태로 돌아온다.** 실측에서 주운 양이 떨군 양을 12% 넘었다.
      if (g.dead || !g.vx) continue;
      const oc = this._pcell(g.x, g.y);        // 옮기기 전 칸
      g.x += g.vx * dt; g.y += g.vy * dt;
      this._reseatPiece(g, oc);                // 칸을 넘었으면 격자도 따라간다
      const k = Math.max(0, 1 - 7 * dt);
      g.vx *= k; g.vy *= k;
      if (Math.abs(g.vx) + Math.abs(g.vy) < 4) { g.vx = 0; g.vy = 0; }
    }
    // 시체가 맞은 방향으로 밀려나며 스러진다 — **그냥 사라지지 않는다**
    for (const c of this.corpses) {
      if (!c.vx) continue;
      c.x += c.vx * dt; c.y += c.vy * dt;
      const k = Math.max(0, 1 - HIT.flyDrag * dt);
      c.vx *= k; c.vy *= k;
    }
    this.updateStability(dt);

    // 7-2: 닿으면 **자동으로 업는다 → 밤 종료.** 버튼 없음(9절 목자의 손 잔존 역할)
    if (this.rescue && this.sheep && !this.foundAt) {
      const p = this.player, s = this.sheep, lim = PLAYER.r + s.r;
      if ((p.x - s.x) ** 2 + (p.y - s.y) ** 2 < lim * lim) {
        this.foundAt = this.t; this.foundBy = '도달';
        this.over = true; this.result = '구출'; this.finish(); return;
      }
    }

    if (this.player.hp <= 0) {
      // ── 개축 캡스톤 「한 몸처럼」 — **밤 1회 다운 무효**(명세 113행) ──
      // ⚠ **회복량은 정본 미기재다.** 정본이 정한 것은 「무효」와 「1회」뿐이다.
      //   전량 회복으로 잠정한다 — 「무효」의 가장 곧은 읽기이고, 절반만 주면
      //   무효가 아니라 「부활」(치즈로 사는 것, 별도 항목)과 구별이 안 된다.
      // ⚠ **치즈 부활(명세 114행)은 여기 없다** — 가격 곡선이 측정 대기(경제)다.
      // ⚠ **횟수로 센다** 【결함 수선 2026-08-07】. 구 판은 `!this._guardUsed` 불리언이라
      //   낮이 2회분을 실어 보내도(기름 단지 영구 + 기름 한 병 그 밤 = downGuard 2)
      //   **한 번만 일어났다** — 두 번 치르고 한 번 받는 자리였다.
      if ((this._guardsUsed ?? 0) < this.dayIn.downGuard) {
        this._guardsUsed = (this._guardsUsed ?? 0) + 1;
        this.player.hp = this.maxHp;
        this.player.inv = PLAYER.iframe;
        this.m.downGuards = (this.m.downGuards ?? 0) + 1;
        this.present('hurt', { shake: SHAKE.knock, power: 1 });
        return;
      }
      this.m.downs++; this.m.downAt = this.t;
      if (isFinalNight(this.nightKey)) {
        this.finalCombatDown = true;
        // 피날레는 이동 장면이므로 쓰러진 전투 포즈/0 HP를 그대로 끌고 가지 않는다.
        // 손실 계산은 위 downAt과 finalCombatDown을 읽어 이미 실패로 처리된다.
        this.player.hp = 1;
        this.enterFinale();
        return;
      }
      this.over = true; this.result = '다운'; this.finish();
    }
  }

  displacePlayerForced(dx, dy) {
    const moveX = Number(dx) || 0, moveY = Number(dy) || 0;
    const p = this.player;
    p.x += moveX; p.y += moveY;
    // Flee density is based on intentional net travel. Move its two measurement
    // references with knockback so a forced hit cannot masquerade as player input.
    if (this._lastPos) {
      this._lastPos.x += moveX; this._lastPos.y += moveY;
    }
    if (this._lagPos) {
      this._lagPos.x += moveX; this._lagPos.y += moveY;
    }
  }

  movePlayer(dt, inp) {
    const p = this.player;
    let dx = Number.isFinite(inp.axisX)
      ? Math.max(-1, Math.min(1, inp.axisX))
      : (inp.right ? 1 : 0) - (inp.left ? 1 : 0);
    let dy = Number.isFinite(inp.axisY)
      ? Math.max(-1, Math.min(1, inp.axisY))
      : (inp.down ? 1 : 0) - (inp.up ? 1 : 0);
    const l = Math.hypot(dx, dy);
    if (l > 1) { dx /= l; dy /= l; }
    if (l > 0.01) {
      const facing = Math.hypot(dx, dy) || 1;
      p.fx = dx / facing; p.fy = dy / facing;
    }
    const sp = PLAYER.speed * this.mods.moveMul;          // 「신발 끈」
    p.vx = dx * sp; p.vy = dy * sp;
    // ⚠⚠ **벽이 없다** 【사장님 지시 2026-08-02 — config 의 FIELD 머리말】
    //   구 구현은 여기서 `clamp(…, 20, W−20)` 으로 가뒀다. 그 벽이 모서리 박기를
    //   최적 전략으로 만들었다(적이 90°에서만 오므로 안정성 반경 안의 수가 4분의 1).
    //   들판은 이제 **끝이 없다.** 대신 적이 **다윗을 따라 난다**(`spawnPoint`).
    p.x += dx * sp * dt;
    p.y += dy * sp * dt;
    // ══════════════════════════════════════════════════════════
    //  **「달아나는가」를 잰다** — 0 = 제자리 싸움 · 1 = 한 방향으로 계속 간다
    //
    //  ⚠⚠ **「움직이는가」로 재면 안 된다.** 처음엔 입력이 있는지로 쟀는데,
    //    이 게임은 **카이팅이 기본**이라 서서 싸워도 쉬지 않고 움직인다.
    //    그래서 싸우는 사람까지 「달린다」로 잡혀 밀도가 같이 올랐다
    //    (실측: 서서 싸울 때 화면 114 → 257. 「체감 밀도 동일」이 그 자리에서 깨졌다.)
    //  → 가려야 하는 것은 속도가 아니라 **알짜 이동**이다. 카이팅은 빙빙 돌아
    //    제자리로 오고, 도망은 계속 멀어진다.
    //  느리게 따라오는 그림자(`_lagPos`)를 두고 **그것과 벌어진 거리**로 잰다.
    //    · 빙빙 돌면 그림자가 따라잡아 거리가 안 벌어진다 → 0
    //    · 곧게 달리면 `속도 × TAU` 만큼 벌어진다 → 1
    // ══════════════════════════════════════════════════════════
    //  ⚠⚠ **죽은 구간이 반드시 있어야 한다** — 이것도 실측으로 배웠다.
    //    날것의 값은 카이팅에서도 0 이 아니라 **평균 0.33** 이 나온다(빙빙 돌아도
    //    조금씩은 밀려나므로). 그 0.33 이 후반 스폰율에 곱해지면 눈덩이가 된다:
    //    HP 무한으로 N1 완주를 재니 **화면 366 → 1038 (+184%)** 였다. 계약 파탄이다.
    //    ⚠ HP 350 으로만 재면 5분쯤 죽어서 **이게 안 보인다.** 무한으로 재야 보인다.
    //  → 그래서 **문턱 아래는 0 으로 눌러 버린다.** 넣고 다시 재니 **+1.9%** 다.
    //
    //  ■ 문턱을 0.60 으로 잡은 근거 — 그리고 **최대값으로 잡으면 안 되는 이유**
    //      카이팅  평균 0.33 · 최대 1.00
    //      도망    평균 1.00 · 최대 1.00
    //    카이팅의 **최대**도 1.00 이다. 위험할 때 잠깐 크게 빼는 순간이 있기
    //    때문인데, **그 순간은 실제로 달아나는 것이 맞다** — 잡혀야 한다.
    //    그러니 「카이팅의 최대가 문턱 아래」는 틀린 기준이다. 옳은 기준은
    //    **평균 밀도가 안 밀리는가**이고(0.33 이 눌려 0 이 되므로 지켜진다),
    //    그것이 사장님이 실제로 체감하는 것이다.
    const TAU = 2.5;                       // 그림자가 따라붙는 데 걸리는 시간(초)
    const GATE = 0.60;                     // 이 아래는 「싸우는 중」으로 본다 (카이팅 **평균** 0.33)
    const lag = this._lagPos ??= { x: p.x, y: p.y };
    const k = Math.min(1, dt / TAU);
    lag.x += (p.x - lag.x) * k; lag.y += (p.y - lag.y) * k;
    const away = Math.hypot(p.x - lag.x, p.y - lag.y);
    const raw = Math.max(0, Math.min(1, away / (sp * TAU * 0.55)));
    this._fleeRaw = raw;                   // 날것 — 프로브가 문턱을 다시 잡을 때 본다
    this._fleeFrac = Math.max(0, (raw - GATE) / (1 - GATE));
    const d = Math.hypot(p.x - this._lastPos.x, p.y - this._lastPos.y);
    this.m.moveDist += d;
    if (d < 0.5) this.m.idleSec += dt;
    // 「발을 멈춘다」 — 움직이면 초기화. 판정은 standBonus() 가 읽는다.
    this._stillFor = d < 0.5 ? (this._stillFor ?? 0) + dt : 0;
    this._lastPos = { x: p.x, y: p.y };
    if (p.inv > 0) p.inv -= dt;
  }

  // 스폰 — 6장 "화면 밖 링 스폰". 경기장이 유한하므로 링 대신 **경기장 테두리 바깥**에
  // 둘레 균등으로 놓고, 플레이어에게 너무 붙으면 다시 뽑는다.
  // **7-2 구출 밤 예외**: 필드가 3배라 테두리 스폰이면 적이 도달하기 전에 밤이 끝난다.
  //   카메라 추적 = "화면 밖"의 기준이 화면이라는 뜻이므로, 구출 밤은 **플레이어 기준
  //   화면 반경 바깥 링**에 스폰한다(6장 "화면 밖 링 스폰"의 문자 그대로의 구현).
  // ══════════════════════════════════════════════════════════════
  //  **적은 다윗 둘레에서 난다** — 전 밤 통일  【사장님 지시 2026-08-02】
  //
  //  구 구현은 갈래가 둘이었다:
  //    · N22(양 찾는 밤) — **다윗 둘레 고리**(카메라가 따라가므로)
  //    · 나머지 밤       — **필드 네 변**(카메라가 고정이므로)
  //  벽을 없앤 지금 「필드 네 변」이라는 자리가 없다. 걸어 나가면 적이 안 따라오고
  //  화면이 텅 빈다. → **N22 가 쓰던 방식 하나로 합친다.** 새로 만드는 게 아니라
  //  이미 검증된 갈래를 전 밤으로 넓히는 것이다.
  //
  //  ⚠ 고리 반경은 **화면 대각 반경 바깥**이다 — 어느 방향으로 보든 화면 밖에서 난다.
  //    안쪽에서 나면 눈앞에 튀어나오고(팝인), 너무 밖이면 오는 데만 오래 걸린다.
  // ══════════════════════════════════════════════════════════════
  get spawnRing() {
    return Math.hypot(FIELD.viewW, FIELD.viewH) / 2 + FIELD.spawnPad;   // 1133px
  }
  spawnPoint() {
    const p = this.player, R = this.spawnRing;
    let a = this.rnd() * Math.PI * 2;
    // ── **달아나면 앞에서 난다** 【사장님 지시 2026-08-02 — FIELD.runBias】 ──────
    //   골고루 뿌리면 진행 방향 원뿔에 드는 것이 1/4뿐이라, 다윗이 적보다 3~4배
    //   빠른 이 게임에서는 **달리는 것이 곧 회피**가 된다(실측: 화면 88 → 28, 완주).
    //   VS 가 하는 것은 속도 조정이 아니라 **가는 쪽에 낳는 것**이다.
    //   ⚠ `_fleeFrac`(알짜로 멀어지는 정도)만큼만 걸린다 — **제자리 싸움은 골고루 그대로**이므로
    //     사장님이 못 박은 「체감 밀도 동일」이 그 자리에서는 안 흔들린다.
    const run = this._fleeFrac ?? 0;
    let biased = false;
    if (run > 0.05 && (p.fx || p.fy) && this.rnd() < FIELD.runBias * run) {
      const head = Math.atan2(p.fy, p.fx);
      const cone = (FIELD.runCone * Math.PI) / 180;
      a = head + (this.rnd() * 2 - 1) * cone;
      biased = true;
    }
    // ── 세 방향의 밤(arcs)【리듬 2026-08-08】 — 스폰이 고정 부채꼴 N개에서만 온다.
    //   방향은 시드가 정하고 밤새 고정이다 — 외우는 게 아니라 **읽는** 밤이다.
    //   ⚠ 도주 편향(biased) 스폰은 부채꼴을 무시한다 — 안 그러면 부채꼴 사이 틈으로
    //     달리는 것이 공짜 회피가 된다(「달아나면 앞에서 난다」가 이 밤에도 서야 한다).
    if (!biased && this.cfg.arcs > 0) {
      const N = this.cfg.arcs;
      const base = ((this._seed ?? 7) % 997) / 997 * Math.PI * 2;   // 시드 파생 — 밤새 고정
      const k = Math.floor(this.rnd() * N);
      a = base + k * (Math.PI * 2 / N) + (this.rnd() - 0.5) * (Math.PI * 2 / N) * 0.6;
    }
    return { x: p.x + Math.cos(a) * R, y: p.y + Math.sin(a) * R };
  }

  // ── 멀어진 적을 치운다 ────────────────────────────────────────
  //  ⚠⚠ **이게 없으면 화면이 도리어 빈다.** 벽이 없으면 뒤처진 적이 영영 남아
  //    `concurrent` 상한을 먹는데, 그 적은 화면 밖이라 아무 압박도 안 준다.
  //    「체감 밀도 동일」(사장님 계약)이 그 자리에서 깨진다.
  //  ⚠ 스폰 고리보다 넉넉히 밖에서 치운다 — 나자마자 지워지면 안 된다.
  cullFar() {
    // ⚠ 보스는 `this.boss` 로 따로 산다 — `enemies` 에 안 들어 있으므로 여기서
    //   걸러 낼 것이 없다(구 판에 `if (e.boss)` 를 적었다가 **닿지 않는 줄**이라 지웠다).
    const p = this.player, R = this.spawnRing * FIELD.cullMul, R2 = R * R;
    let n = 0;
    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const e = this.enemies[i];
      const dx = e.x - p.x, dy = e.y - p.y;
      if (dx * dx + dy * dy <= R2) continue;
      this.enemyBehavior?.removeEnemy(e);
      this.enemies.splice(i, 1); n++;
    }
    if (n) this.m.culled = (this.m.culled ?? 0) + n;
  }

  createEnemy(kind, x, y) {
    const st = ENEMY[kind];
    const fallback = kind === 'elite' ? 'wolf' : kind === 'hyena' ? 'hyena' : 'field-rat';
    const species = this._pickSpecies?.(kind, this.speciesRnd()) ?? fallback;
    const e = { kind, species, x, y, hp: st.hp * this.enemyNightScale, r: st.r };
    if (species === 'grasshopper') {
      e.motionState = 'idle';
      e.motionT = 0;
      e.hopDx = 0;
      e.hopDy = 0;
    }
    this.enemyBehavior?.initializeEnemy(e, this.t);
    return e;
  }

  spawnEagle(at = this.t, scheduledAt = at) {
    const p = this.player;
    const e = {
      kind: 'threat', species: 'eagle', x: p.x, y: p.y,
      hp: EAGLE.hp, r: EAGLE.r, bornAt: at, scheduledAt,
      state: 'high', stateT: 0, altitude: 1, targetable: false,
      facing: p.fx < 0 ? -1 : 1, impacted: false,
    };
    this.enemyBehavior?.admitEagle(e);
    this.eagles.push(e);
    this.m.eaglesSpawned++;
    return e;
  }

  stepEagles() {
    while (this._eagleScheduleI < this.eagleSchedule.length
           && this.t >= this.eagleSchedule[this._eagleScheduleI]) {
      const scheduledAt = this.eagleSchedule[this._eagleScheduleI];
      if (this.enemyBehavior && !this.enemyBehavior.canStartEagle(this)) {
        if (this.enemyBehavior) this.enemyBehavior.metrics.eagleDelayedFrames++;
        break;
      }
      this._eagleScheduleI++;
      this.spawnEagle(this.t, scheduledAt);
      this.enemyBehavior?.noteEagleDelay(this.t - scheduledAt);
    }
    for (let i = this.eagles.length - 1; i >= 0; i--) {
      const e = this.eagles[i], age = Math.max(0, this.t - e.bornAt);
      if (e._talonCommitFresh && this.t > (e._talonCommitAtT ?? -Infinity) + 1e-9) {
        e._talonCommitFresh = false;
      }
      if (age < EAGLE.highSec) {
        e.state = 'high'; e.stateT = age; e.altitude = 1 - age / EAGLE.warningSec;
        e.targetable = false;
      } else if (age < EAGLE.warningSec) {
        e.state = 'low'; e.stateT = age - EAGLE.highSec;
        e.altitude = 1 - age / EAGLE.warningSec; e.targetable = true;
      } else if (age < EAGLE.warningSec + EAGLE.groundSec) {
        e.state = 'grounded'; e.stateT = age - EAGLE.warningSec;
        e.altitude = 0; e.targetable = true;
        this.enemyBehavior?.syncEagle(e);
        if (!e.impacted) this.impactEagle(e);
      } else if (age < EAGLE.warningSec + EAGLE.groundSec + EAGLE.takeoffSec) {
        e.state = 'takeoff'; e.stateT = age - EAGLE.warningSec - EAGLE.groundSec;
        e.altitude = e.stateT / EAGLE.takeoffSec; e.targetable = false;
        this.enemyBehavior?.syncEagle(e);
        if (!e.impacted) this.impactEagle(e);
      } else {
        this.enemyBehavior?.removeEnemy(e);
        this.eagles.splice(i, 1);
        this.m.eaglesEscaped++;
        continue;
      }
      this.enemyBehavior?.syncEagle(e);
    }
  }

  playerGuardMul() {
    if (!(this.mods.dogGuard > 0)) return 1;
    const p = this.player, GR2 = (6 * UNIT) ** 2;
    for (const d of this.dogs) {
      const dx = d.x - p.x, dy = d.y - p.y;
      if (dx * dx + dy * dy <= GR2) return 1 - this.mods.dogGuard;
    }
    return 1;
  }

  impactEagle(e) {
    if (e.impacted) return false;
    e.impacted = true;
    e.impactAt = this.t;
    this.m.eagleImpacts++;
    e.behaviorSeq = (e.behaviorSeq || 0) + 1;
    e.behaviorState = 'commit'; e.behaviorProgress = 1;
    e.attackKind = 'talon-rake'; e.attackStage = 'commit';
    e.behaviorMajor = false; e.behaviorToken = 'eagle';
    e.behaviorCommitted = true; e.behaviorConnected = false;
    e.contactArmed = false; e.talonSignal = false; e.talonSignalProgress = 1;
    e.talonContactSpent = true;
    e._talonCommitFresh = true; e._talonCommitAtT = this.t;
    e.attackGeometry = eagleTalonGeometry(e, 'commit');
    this.enemyBehavior?.count(e, 'commit');

    const p = this.player, dx = p.x - e.x, dy = p.y - e.y;
    const intersectsTalon = e.attackGeometry.capsules.some((capsule) => {
      const radius = PLAYER.r + capsule.r;
      return pointSegmentDistanceSq(p.x, p.y,
        capsule.x1, capsule.y1, capsule.x2, capsule.y2) < radius * radius;
    });
    let hit = false;
    if (p.inv <= 0 && intersectsTalon) {
      const dmg = PLAYER.dmgElite * this.enemyNightScale
                * this.mods.dmgTakenMul * this.playerGuardMul();
      p.hp -= dmg;
      p.inv = PLAYER.iframe;
      this.m.hits++; this.m.hitsThreat++; this.m.eagleImpactHits++;
      this.m.dmgTaken += dmg; this.m.dmgTrace.push(this.t, this.m.dmgTaken);
      this.enemyBehavior?.recordContact(e, dmg, e.behaviorSeq);
      this.sinceHit = 0;
      this.present('hurt', { power: 0.72, dx, dy });
      hit = true;
    }
    this.enemyBehavior?.completeCommit(e);
    return hit;
  }

  spawn(dt) {
    // **보스 활성 중 물량층 −40%** (30밤시트 N28·N29·N30 공통).
    // 이것은 연출 배려가 아니라 **해법의 성립 조건**이다 — 배후를 잡으려면(곰),
    // 계속 도망 다니려면(사자) 이동할 공간이 있어야 한다(명세 6장).
    const bossCut = bossSwarmMult(!!this.boss && !this.boss.dead);
    // ⚠ **달리는 동안만 더 난다** 【사장님 지시 2026-08-02 — FIELD.runSpawnMul】.
    //   달리면 적이 스쳐 지나가 화면에 머무는 시간이 짧다 — 같은 밀도를 보려면
    //   그만큼 더 나야 한다. **서 있으면 1배**라 서서 싸울 때의 밀도는 안 건드린다.
    const runMul = 1 + FIELD.runSpawnMul * (this._fleeFrac ?? 0);
    this._acc = (this._acc ?? 0) + this.spawnRate() * this.spawnScale * bossCut * runMul * dt;
    // Cap blocks admission, not the authored spawn clock. Keep the accrued mass so
    // harmless behavior pauses cannot silently delete a night's volume; when slots
    // reopen the existing while-loop drains only up to the same concurrent ceiling.
    if (this.enemies.length >= this.concurrent) { this.m.atCapSec += dt; return; }
    while (this._acc >= 1 && this.enemies.length < this.concurrent) {
      this._acc -= 1;
      // 종별 추첨 = **남은 예산에 비례**
      let tot = 0;
      for (const k in this.budget) tot += this.budget[k];
      // ⚠ **마르면 다시 채운다** 【사장님 신고 2026-08-03 — 생성자 주석 참조】.
      //   구 판은 여기서 `return` 해서 밤 끝까지 한 마리도 안 났다(빈 화면).
      if (tot <= 0) {
        for (const k in this._budget0) this.budget[k] = this._budget0[k];
        tot = this.totalBudget;
        this.m.refills = (this.m.refills ?? 0) + 1;
        if (tot <= 0) return;                 // 정적 밤(통장 0) — 여기는 원래 안 난다
      }
      let r = this.rnd() * tot, kind = null;
      for (const k in this.budget) { r -= this.budget[k]; if (r <= 0) { kind = k; break; } }
      if (!kind) return;
      this.budget[kind]--;
      const pt = this.spawnPoint();
      const enemy = this.createEnemy(kind, pt.x, pt.y);
      this.enemyBehavior?.admitEnemy(enemy, this.t);
      this.enemies.push(enemy);
      this.m.spawned++;
    }
  }

  // **적은 전부 나를 노린다**(2-4 불가침). 다만 오는 방법이 셋이다(10-B 6-1).
  //   straight 직진 — 물량층. 흐름이자 벽.
  //   circle   선회 — 정예층. 궤도를 돌며 조인다. 등 뒤가 닫힌다.
  //   charge   돌진 — 하이에나. 예고 1초 → 3배속. 예고를 보고 옆으로 비킨다.
  // **어느 것도 목표를 바꾸지 않는다** — 양도 물가도 안 본다. 소년 하나다.
  moveEnemies(dt) {
    const p = this.player;
    this.enemyBehavior?.beginFrame(this, dt);
    for (const e of this.enemies) {
      if (e.species === 'grasshopper') {
        // A full-cycle hitch and normal 60fps must begin the hop from the same
        // safe staging radius. Grasshoppers acquire at the compress boundary,
        // so normalize an idle body before consuming any phase time.
        enforceEnemyStandoff(e, p);
        this.moveGrasshopper(e, dt, p);
        enforceEnemyStandoff(e, p);
        continue;
      }
      if (this.enemyBehavior?.moveEnemy(e, dt, this)) {
        // Bat approach owns its continuous sine path even without a token;
        // other handled species are inside an authored tell/commit/recovery.
        if (e.species === 'bat') enforceEnemyStandoff(e, p);
        continue;
      }
      moveEnemyFallback(e, dt, p);
    }
    this.enemyBehavior?.endFrame(this);
  }

  moveGrasshopper(e, dt, p = this.player) {
    e.motionState ??= 'idle';
    e.motionT ??= 0;
    e._grassAttackCycle ??= false;
    let left = dt;
    const next = { idle: 'compress', compress: 'air', air: 'land', land: 'idle' };
    while (left > 1e-9) {
      const slice = Math.min(left, GRASSHOPPER.phaseSec - e.motionT);
      if (e.motionState === 'air') {
        const speed = ENEMY.swarm.speed * GRASSHOPPER.leapSpeedMul;
        const fromX = e.x, fromY = e.y;
        e.x += (e.hopDx ?? 0) * speed * slice;
        e.y += (e.hopDy ?? 0) * speed * slice;
        const timeFrom = dt - left;
        // The leap relocates the insect; the physical attack is the rear-leg
        // extension after landing. Tagging this path harmless prevents a jump
        // and its kick from becoming two attacks under the same animation.
        recordBehaviorSweep(e, fromX, fromY, e.x, e.y, timeFrom, timeFrom + slice,
          e.behaviorSeq, { armed: false, attackKind: 'rear-kick', relocation: true });
      }
      e.motionT += slice;
      left -= slice;
      if (e.motionT + 1e-9 < GRASSHOPPER.phaseSec) break;
      e.motionState = next[e.motionState] ?? 'idle';
      e.motionT = 0;
      if (e.motionState === 'compress') {
        if (e._grassAttackCycle) {
          this.enemyBehavior?.completeCommit(e);
          this.enemyBehavior?.releaseMinor(e);
          e._grassAttackCycle = false;
          e.behaviorToken = null; e.behaviorMinor = false;
          e.attackKind = null; e.attackStage = null;
          e.bodyFacingDx = null; e.bodyFacingDy = null;
          e.behaviorTargetX = null; e.behaviorTargetY = null; e.behaviorDistance = 0;
          e.attackOriginX = null; e.attackOriginY = null;
          e.behaviorNextAt = Math.max(e.behaviorNextAt || 0,
            this.t + ACT.charge.cool);
        }
        const attackApproach = Math.hypot(p.x - e.x, p.y - e.y);
        const hopTravel = ENEMY.swarm.speed * GRASSHOPPER.leapSpeedMul
          * GRASSHOPPER.phaseSec;
        const attackReady = attackApproach <= hopTravel
          && this.t + 1e-9 >= (e.behaviorNextAt || 0)
          && this.enemyBehavior?.acquireMinor(e, 'rear-kick');
        e._grassAttackCycle = !!attackReady;
        e.behaviorState = attackReady ? 'tell' : 'approach';
        e.behaviorProgress = 0; e.behaviorMajor = false;
        e.behaviorMinor = !!attackReady; e.contactArmed = false;
        e.behaviorToken = attackReady ? 'minor' : null;
        e.attackKind = attackReady ? 'rear-kick' : null;
        e.attackStage = attackReady ? 'tell' : null;
        e.behaviorConnected = false; e.behaviorCommitted = false;
        if (attackReady) {
          e.behaviorSeq++;
          this.enemyBehavior?.count(e, 'start');
          if (this.enemyBehavior) this.enemyBehavior.metrics.bandStarts[this.enemyBehavior.band]++;
        }
      } else if (e.motionState === 'air') {
        const dx = p.x - e.x, dy = p.y - e.y, d = Math.hypot(dx, dy) || 1;
        e.hopDx = d === 1 && dx === 0 && dy === 0 ? 1 : dx / d;
        e.hopDy = d === 1 && dx === 0 && dy === 0 ? 0 : dy / d;
        const travel = ENEMY.swarm.speed * GRASSHOPPER.leapSpeedMul * GRASSHOPPER.phaseSec;
        e.behaviorState = e._grassAttackCycle ? 'tell' : 'approach';
        e.behaviorProgress = e._grassAttackCycle ? 0.5 : 0;
        e.behaviorDx = e.hopDx; e.behaviorDy = e.hopDy;
        e.behaviorTargetX = e.x + e.hopDx * travel;
        e.behaviorTargetY = e.y + e.hopDy * travel;
        e.behaviorDistance = travel;
        e.contactArmed = false;
      } else if (e.motionState === 'land') {
        if (e._grassAttackCycle) {
          const kickDx = -(e.hopDx ?? 0), kickDy = -(e.hopDy ?? 0);
          const kickStartX = e.x + kickDx * e.r;
          const kickStartY = e.y + kickDy * e.r;
          e.bodyFacingDx = e.hopDx; e.bodyFacingDy = e.hopDy;
          e.behaviorDx = kickDx; e.behaviorDy = kickDy;
          e.attackOriginX = kickStartX; e.attackOriginY = kickStartY;
          e.behaviorTargetX = kickStartX + kickDx * GROUND_ATTACK.grasshopper.kickReach;
          e.behaviorTargetY = kickStartY + kickDy * GROUND_ATTACK.grasshopper.kickReach;
          e.behaviorDistance = e.r + GROUND_ATTACK.grasshopper.kickReach;
          e.behaviorState = 'commit'; e.behaviorProgress = 0;
          e.behaviorCommitted = true; e.attackStage = 'commit';
          e.contactArmed = false;
          const at = dt - left;
          recordBehaviorSweep(e, kickStartX, kickStartY,
            e.behaviorTargetX, e.behaviorTargetY,
            at, at, e.behaviorSeq, {
              armed: true, attackKind: 'rear-kick', limb: true,
              // The player circle expands the thin leg path. Reusing the whole
              // enemy body radius here would make a rear kick hit in front.
              contactRadius: PLAYER.r,
            });
          this.enemyBehavior?.count(e, 'commit');
        } else {
          e.behaviorState = 'approach'; e.behaviorProgress = 0;
          e.contactArmed = false;
        }
      } else if (e.motionState === 'idle') {
        if (e._grassAttackCycle) {
          this.enemyBehavior?.completeCommit(e);
          this.enemyBehavior?.releaseMinor(e);
          e.behaviorState = 'recover'; e.behaviorProgress = 0;
          e.behaviorMinor = false; e.behaviorToken = null;
          e.attackStage = 'recover'; e.contactArmed = false;
        } else {
          e.behaviorState = 'approach'; e.behaviorProgress = 0;
          e.behaviorTargetX = null; e.behaviorTargetY = null; e.behaviorDistance = 0;
        }
      }
    }
    if (e._grassAttackCycle) {
      const base = e.motionState === 'compress' ? 0
        : e.motionState === 'air' ? 0.25
          : e.motionState === 'land' ? 0.5 : 0.75;
      e.behaviorProgress = Math.min(1,
        base + e.motionT / GRASSHOPPER.phaseSec * 0.25);
    } else {
      e.behaviorProgress = Math.min(1, e.motionT / GRASSHOPPER.phaseSec);
    }
  }

  // 보스에 무기가 닿았다 — **소년의 무기에만 배율이 붙는다**(6-1 "급소·저항 배율의 적용
  // 소스"). 개·올무·지대는 상시 저항을 그대로 받는다. *"사자와 곰을 이긴 자는 반드시
  // 플레이어 자신이어야 하기 때문이다."*
  //   배율이 **null 이면 「측정 대기」**다 — 그 타격은 세지 않고 기록만 한다.
  //   지금 상태에서 보스가 안 죽는 것은 고장이 아니라 6-2 표가 비어 있다는 뜻이다.
  hitBoss(dmg) {
    if (this.boss && !this.boss.dead) this.popDamage(this.boss.x, this.boss.y - this.boss.cfg.r * 0.6, dmg, 'boss');
    const b = this.boss, p = this.player;
    const mult = b.damageMult(p.x, p.y);
    this.m.bossSwings = (this.m.bossSwings ?? 0) + 1;
    if (mult == null) { this.m.bossPending = (this.m.bossPending ?? 0) + 1; return; }
    if (mult >= b.cfg.critMult && b.cfg.critMult > 1) this.m.bossCrits = (this.m.bossCrits ?? 0) + 1;
    const dealt = Math.max(0, dmg * mult);
    b.hit(dealt);
    this.enemyBehavior?.recordDamageTaken(b, dealt);
  }

  // ⚠ **`bossInArc(R, half)` 가 여기 있었다** 【폐기 2026-08-07】. 근접 호 판정인데
  //   지팡이 삭제(2026-07-31) 때 **호출부만 사라지고 몸통이 남았다** — 부르는 곳 0.
  //   물맷돌은 발사체가 따로 묻는다(호가 필요 없다).

  // ── 보스 진행 + 접촉 (6-2) ────────────────────────────────
  // boss.js 가 상태기계 전부를 갖고 있다. 여기가 하는 일은 셋뿐이다:
  //   ① 전진시킨다  ② 몸이 닿으면 HP를 깎는다  ③ 승패를 읽는다
  // **보스는 `enemies` 배열에 안 들어간다** — 안정성(반경 내 적 수)·분리 격자·무기
  // 자동조준이 전부 그 배열을 훑는데, 거기에 넣으면 보스 한 마리가 물량층 한 마리로
  // 세어져 3-1 안정성 정의가 조용히 오염된다. 보스는 별도 개체다.
  stepBoss(dt) {
    const b = this.boss, p = this.player;
    const bossDt = this.enemyBehavior?.bossStepDt(this, dt) ?? dt;
    b.step(bossDt, { px: p.x, py: p.y, elapsed: this.t });
    this.enemyBehavior?.afterBossStep(this);
    if (b.dead) {
      if (!this.bossDownAt) {
        this.bossDownAt = this.t;
        this.enemyBehavior?.recordKill(b, this.t);
      }
      return;
    }

    // ── 접촉 — 명세 4장: 곰 25 / 사자 20 ────────────────────────
    //
    // ■ 【ⓐ 사장님 결정 2026-07-28】 **급소 창 동안에는 접촉 피해가 없다.**
    //   명세 6-2 가 그 2초를 **「급소 창」**이라 부른다 — **플레이어의 기회**라는 뜻이다.
    //   곰이 숨을 고르며 등을 보이는 시간에 몸이 계속 아프면 이름과 정면으로 어긋난다.
    //   실측이 그 어긋남을 그대로 보여줬다: 돌진이 **플레이어 위에서 끝나고** 바로
    //   정지 2초가 오므로, 몸 반지름 108px 안에 갇힌 채 무적 0.5초마다 계속 맞았다.
    //   259초에 **54.7회**(사이클 9초인데 4.7초에 한 번) — 총피해의 77%.
    //   **급소 창이 기회가 아니라 고기 분쇄기였다.**
    //
    // ■ 【ⓒ】 **넉백이 몸 밖으로 나가야 넉백이다.**
    //   구 값 1.6유닛(58px)은 곰 몸 반지름 108px보다 **작다.** 밀려나도 여전히 안이라
    //   다음 무적이 풀리는 즉시 또 맞았다. 밀어내는 거리를 고정값이 아니라
    //   **「몸 밖까지」**로 푼다 — 하이에나(작은 몸)에서는 구 거동과 같다.
    if (p.inv <= 0 && b.canContactDamage) {
      const dx = b.x - p.x, dy = b.y - p.y, lim = PLAYER.r + b.cfg.r;
      const geometryHit = typeof b.contactGeometryHits === 'function'
        ? b.contactGeometryHits(p.x, p.y, PLAYER.r)
        : dx * dx + dy * dy < lim * lim;
      if (geometryHit && b.consumeContact()) {
        // ⚠ **보스 접촉만 계수를 안 타고 있었다** 【결함 수선 2026-08-07】.
        //   일반 피격(1231행)은 `dmgTakenMul`(가죽 앞치마)과 `dogGuard`(등을 맡긴다)를
        //   곱하는데 여기만 날값이었다 — **그 계수가 제일 필요한 밤에서 아무 일도 안 했다.**
        //   같은 식을 그대로 쓴다(표를 둘로 만들지 않는다).
        let bGuard = 1;
        if (this.mods.dogGuard > 0) {
          const GR2 = (6 * UNIT) ** 2;
          for (const d of this.dogs) {
            const gx = d.x - p.x, gy = d.y - p.y;
            if (gx * gx + gy * gy <= GR2) { bGuard = 1 - this.mods.dogGuard; break; }
          }
        }
        const bDmg = b.contactDamage * this.enemyNightScale
                   * this.mods.dmgTakenMul * bGuard;
        p.hp -= bDmg; p.inv = PLAYER.iframe;
        // 실제 접촉 판정이 성립한 그 프레임을 보스 시트에 전달한다. 사자는 상태가
        // `active` 하나뿐이라 이 신호 없이는 맞혀도 걷기 프레임만 보였다. 곰은 돌진
        // 신호에서 짧은 앞발 충격/회수로 이어진다. 피해 계산은 이미 위에서 끝났다.
        const attackFx = BOSS_ATTACK_FX[b.kind];
        if (attackFx) b.beginAttack(attackFx.kind, attackFx.duration);
        this.m.hits++; this.m.hitsBoss = (this.m.hitsBoss ?? 0) + 1;
        this.m.dmgTaken += bDmg;
        this.enemyBehavior?.recordContact(b, bDmg);
        this.m.dmgTrace.push(this.t, this.m.dmgTaken);
        this.present('boss', {
          shake: SHAKE.knock, power: 1,
          dx: p.x - b.x, dy: p.y - b.y,
        });
        if (b.cfg.knock) {
          let pushX = -dx, pushY = -dy;
          let d = Math.hypot(pushX, pushY);
          if (d <= 1e-9) {
            // 도약 종점과 다윗 중심이 정확히 같으면 몸 중심 벡터에는 방향이 없다.
            // 새 방향을 추측하지 않고 renderer/판정이 공유하는 잠금 facing을 그대로 쓴다.
            pushX = Number(b.attackGeometry?.facing?.x) || 0;
            pushY = Number(b.attackGeometry?.facing?.y) || 0;
            d = Math.hypot(pushX, pushY);
          }
          // 몸 밖 + 여유 한 뼘. 구 고정 1.6유닛보다 작아질 일은 없다.
          const centreDistance = Math.hypot(dx, dy);
          const kb = Math.max(1.6 * UNIT, lim - centreDistance + 0.6 * UNIT);
          if (d > 1e-9) {
            this.displacePlayerForced((pushX / d) * kb, (pushY / d) * kb);
          }
        }
        this.sinceHit = 0;
      }
    }
    if (b.dead && !this.bossDownAt) this.bossDownAt = this.t;
  }

  // 그 밤이 「밤을 깼는가」 — 보스 밤에만 의미가 있다.
  //   *구 주석은 여기서 곰 `win:'kill'` · 사자 `win:'kill-or-survive'` 를 적었다.
  //    **둘 다 없어진 값이다** — 아래 결정으로 셋이 같아졌다.*
  // **셋 다 `win: 'survive'` 다**(사장님 결정 2026-07-28). 살아서 밤을 마치면
  // 보스는 반드시 쓰러진다 — 페이즈 시계가 그것을 보장한다(boss.js 헤더).
  // 그래서 「보스를 못 죽여서 실패한 밤」이라는 것이 이제 없다. 실패는 **다운**뿐이다.
  get bossCleared() {
    if (!this.boss) return null;
    return this.boss.dead || this.result === '생존' || this.result === '구출';
  }

  // 적끼리 겹치지 않게 민다. 판정 몸집보다 작은 시각 여백을 더해 한 점처럼 뭉치는
  // 것을 풀되, 적 하나당 후보를 `maxChecks`개까지만 봐 최악의 프레임 비용을 고정한다.
  separate() {
    const n = this.enemies.length;
    this._separationChecks = 0;
    if (n < 2) {
      if (n === 1 && enforceEnemyStandoff(this.enemies[0], this.player)
          && this.enemyBehavior) this.enemyBehavior.metrics.standoffCorrections++;
      return;
    }
    if (this._next.length < n) {
      this._next = new Int32Array(n * 2);
      this._cx = new Int32Array(n * 2); this._cy = new Int32Array(n * 2);
      this._separationFlags = new Uint8Array(n * 2);
      this._separationNearSq = new Float64Array(n * 2);
    }
    const head = this._head, next = this._next, C = this._cell,
          cols = this._cols, rows = this._rows;
    const cx = this._cx ??= new Int32Array(512), cy = this._cy ??= new Int32Array(512);
    const separationFlags = this._separationFlags;
    const separationNearSq = this._separationNearSq;
    // **원점을 다윗에 맞춘다** — 격자가 따라다닌다(생성자의 ⚠⚠ 참조).
    //   치움 반경 안의 적은 전부 이 창 안에 들어오므로 `clamp` 가 실제로는 안 문다.
    const ox = this._gridOx = this.player.x - (cols * C) / 2;
    const oy = this._gridOy = this.player.y - (rows * C) / 2;
    head.fill(-1);
    for (let i = 0; i < n; i++) {
      const e = this.enemies[i];
      const waiting = enemyNeedsStandoff(e);
      separationFlags[i] = waiting ? 1 : 0;
      if (waiting) {
        const hold = PLAYER.r + Math.max(0, Number(e?.r) || 0)
          + GROUND_ATTACK.holdGap;
        const near = hold + 1e-9;
        separationNearSq[i] = near * near;
      }
      const gx = clamp(((e.x - ox) / C) | 0, 0, cols - 1);
      const gy = clamp(((e.y - oy) / C) | 0, 0, rows - 1);
      cx[i] = gx; cy[i] = gy;
      const c = gy * cols + gx;
      next[i] = head[c]; head[c] = i;
    }
    const maxChecks = ENEMY_SEPARATION.maxChecks;
    for (let i = 0; i < n; i++) {
      const a = this.enemies[i];
      let checks = 0;
      // 같은 칸의 앞선 개체 + 쌍을 한 번만 만드는 네 이웃 칸. 같은 칸부터 보므로
      // 극단적으로 한 칸에 몰려도 가장 심한 겹침을 먼저 푼다.
      for (let oi = 0; oi < SEP_CELL_X.length && checks < maxChecks; oi++) {
        const gx = cx[i] + SEP_CELL_X[oi], gy = cy[i] + SEP_CELL_Y[oi];
        if (gx < 0 || gy < 0 || gx >= cols || gy >= rows) continue;
        let j = oi === 0 ? next[i] : head[gy * cols + gx];
        for (; j !== -1 && checks < maxChecks; j = next[j]) {
          checks++; this._separationChecks++;
          const b = this.enemies[j];
          let dx = b.x - a.x, dy = b.y - a.y;
          const min = Math.min(ENEMY_SEPARATION.maxDistance,
                               a.r + b.r + ENEMY_SEPARATION.gap);
          const d2 = dx * dx + dy * dy;
          if (d2 >= min * min) continue;
          let d = Math.sqrt(d2), ux, uy;
          if (d > 1e-6) {
            ux = dx / d; uy = dy / d;
          } else {
            // 완전히 같은 좌표도 반드시 갈라진다. pair index로 고른 고정 8방향이라
            // 난수 호출·할당·프레임별 흔들림이 없다.
            const dir = ((i * 17) ^ (j * 31)) & 7;
            ux = SEP_DIR_X[dir]; uy = SEP_DIR_Y[dir]; d = 0;
          }
          const overlap = min - d;
          const aLocked = a.behaviorAnchored || (a.behaviorTargetX != null
            && (a.behaviorState === 'tell' || a.behaviorState === 'commit'));
          const bLocked = b.behaviorAnchored || (b.behaviorTargetX != null
            && (b.behaviorState === 'tell' || b.behaviorState === 'commit'));
          if (aLocked && bLocked) continue;
          if (aLocked) {
            const push = Math.min(ENEMY_SEPARATION.maxPush, overlap);
            b.x += ux * push; b.y += uy * push;
            if (separationFlags[j] & 1) {
              const pdx = b.x - this.player.x, pdy = b.y - this.player.y;
              if (pdx * pdx + pdy * pdy <= separationNearSq[j]) {
                if (enforceEnemyStandoff(b, this.player) && this.enemyBehavior) {
                  this.enemyBehavior.metrics.standoffCorrections++;
                }
                separationFlags[j] = 3;
              } else separationFlags[j] = 5;
            } else {
              separationFlags[j] = 2;
            }
          } else if (bLocked) {
            const push = Math.min(ENEMY_SEPARATION.maxPush, overlap);
            a.x -= ux * push; a.y -= uy * push;
            if (separationFlags[i] & 1) {
              const pdx = a.x - this.player.x, pdy = a.y - this.player.y;
              if (pdx * pdx + pdy * pdy <= separationNearSq[i]) {
                if (enforceEnemyStandoff(a, this.player) && this.enemyBehavior) {
                  this.enemyBehavior.metrics.standoffCorrections++;
                }
                separationFlags[i] = 3;
              } else separationFlags[i] = 5;
            } else {
              separationFlags[i] = 2;
            }
          } else {
            const push = Math.min(ENEMY_SEPARATION.maxPush, overlap / 2);
            a.x -= ux * push; a.y -= uy * push;
            b.x += ux * push; b.y += uy * push;
            if (separationFlags[i] & 1) {
              const pdx = a.x - this.player.x, pdy = a.y - this.player.y;
              if (pdx * pdx + pdy * pdy <= separationNearSq[i]) {
                if (enforceEnemyStandoff(a, this.player) && this.enemyBehavior) {
                  this.enemyBehavior.metrics.standoffCorrections++;
                }
                separationFlags[i] = 3;
              } else separationFlags[i] = 5;
            } else {
              separationFlags[i] = 2;
            }
            if (separationFlags[j] & 1) {
              const pdx = b.x - this.player.x, pdy = b.y - this.player.y;
              if (pdx * pdx + pdy * pdy <= separationNearSq[j]) {
                if (enforceEnemyStandoff(b, this.player) && this.enemyBehavior) {
                  this.enemyBehavior.metrics.standoffCorrections++;
                }
                separationFlags[j] = 3;
              } else separationFlags[j] = 5;
            } else {
              separationFlags[j] = 2;
            }
          }
        }
      }
    }
    // A skipped far-away call could not have corrected position. Replay only
    // the last such call to restore its exact final metadata. A near call has
    // already written metadata from the current position; repeating it would
    // renormalize holdDx/holdDy and change their low bits.
    for (let i = 0; i < n; i++) {
      if (separationFlags[i] === 5) {
        if (enforceEnemyStandoff(this.enemies[i], this.player)
            && this.enemyBehavior) {
          this.enemyBehavior.metrics.standoffCorrections++;
        }
      } else if (separationFlags[i] === 2) {
        this.enemies[i].holding = false;
      }
    }
  }

  enforceEnemyStandoffs() {
    let corrections = 0, insideContact = 0;
    const p = this.player;
    for (const e of this.enemies) {
      const waiting = enemyNeedsStandoff(e);
      if (enforceEnemyStandoff(e, p)) corrections++;
      if (!waiting) continue;
      const dx = e.x - p.x, dy = e.y - p.y;
      const contactRadius = PLAYER.r + e.r;
      if (dx * dx + dy * dy < contactRadius * contactRadius) insideContact++;
    }
    if (this.enemyBehavior) {
      const metrics = this.enemyBehavior.metrics;
      metrics.standoffCorrections += corrections;
      metrics.maxStandoffInsideContact = Math.max(
        metrics.maxStandoffInsideContact, insideContact,
      );
    }
    return insideContact;
  }

  // 접촉 피해. **3-1: 피격은 HP만 깎는다. 안정성은 건드리지 않는다.**
  contact() {
    const p = this.player;
    if (p.inv > 0) {
      this.enemyBehavior?.resolveFrameContacts();
      return;
    }
    let hit = null, hitTime = Infinity, hitViaSweep = false;
    let hitAction = false, hitSeq = null;
    const frameEnd = Math.max(0, Number(this.enemyBehavior?.frameDt) || 0);
    const director = this.enemyBehavior;
    for (const e of this.enemies) {
      const trail = e.behaviorSweep;
      // Authored bodies are harmless unless an attack explicitly arms either
      // their endpoint or a swept segment. Keep legacy undefined as armed.
      if (!trail && e.contactArmed === false) continue;
      const segments = trail?.segments;
      const segmentCount = segments != null ? segments.length : Number(!!trail);
      const bodyAction = e.behaviorTargetX != null
        && (e.behaviorState === 'commit' || e.behaviorState === 'recover');
      const dx = e.x - p.x, dy = e.y - p.y;
      const bodyD2 = dx * dx + dy * dy;
      const lim = PLAYER.r + e.r;
      let candidateTime = Infinity, candidateViaSweep = false;
      let candidateSeq = e.behaviorSeq;
      for (let segmentIndex = 0; segmentIndex < segmentCount; segmentIndex++) {
        const segment = segments != null ? segments[segmentIndex] : trail;
        if (segment.armed === false) continue;
        const seq = segment.seq ?? e.behaviorSeq;
        if (!(director ? director.contactAvailable(e, seq)
          : e.behaviorContactSeq !== seq)) continue;
        const segmentRadius = Number.isFinite(Number(segment.contactRadius))
          ? Math.max(0, Number(segment.contactRadius)) : lim;
        const fraction = behaviorSegmentFirstFraction(segment, p.x, p.y, segmentRadius);
        if (fraction == null) continue;
        const timeFrom = Math.max(0, Number(segment.timeFrom) || 0);
        const timeTo = Math.max(timeFrom, Number(segment.timeTo) || timeFrom);
        const toi = timeFrom + fraction * (timeTo - timeFrom);
        if (toi < candidateTime - 1e-9) {
          candidateTime = toi; candidateViaSweep = true; candidateSeq = seq;
        }
      }
      // A completed commit may leave a live segment while its endpoint is already
      // harmless recovery. Only an armed body is a frame-end candidate.
      const bodyHit = e.contactArmed !== false && bodyD2 < lim * lim
        && (director ? director.contactAvailable(e, e.behaviorSeq)
          : e.behaviorContactSeq !== e.behaviorSeq);
      if (bodyHit && frameEnd < candidateTime - 1e-9) {
        candidateTime = frameEnd; candidateViaSweep = false;
        candidateSeq = e.behaviorSeq;
      }
      if (!Number.isFinite(candidateTime)) continue;
      // Actual within-frame time of impact wins. Equal TOIs retain array/segment
      // order, so hitch frames choose the same cohort member as split frames.
      if (hit == null || candidateTime < hitTime - 1e-9) {
        hitTime = candidateTime; hit = e; hitViaSweep = candidateViaSweep;
        hitAction = candidateViaSweep || bodyAction; hitSeq = candidateSeq;
      }
    }
    if (!hit) {
      this.enemyBehavior?.resolveFrameContacts();
      return;
    }
    // 「막아내기」 — 곱연산, 최종 상한 60%(= 배율 하한 0.40). mods 가 이미 잘라서 준다.
    // 「등을 맡긴다」 — 개가 곁에 있는 동안만. 개를 데리고 다니는 것이 곧 방어가 된다.
    const guard = this.playerGuardMul();
    const dmg = (hit.kind === 'swarm' ? PLAYER.dmgSwarm : PLAYER.dmgElite)
                * this.enemyNightScale * this.mods.dmgTakenMul * guard;
    p.hp -= dmg; p.inv = PLAYER.iframe;
    // **넉백 — 하이에나만**(명세 4장 "넉백은 하이에나·곰·사자만. 늑대는 없음").
    // 입력이 이동 하나뿐인 게임에서 넉백은 **유일하게 조작을 뺏는 것**이라 세다.
    // 그래서 물량·늑대에는 없고, 예고를 놓쳤을 때의 대가로만 존재한다.
    const hitDx = hitViaSweep ? (hit.behaviorDx || 0) : p.x - hit.x;
    const hitDy = hitViaSweep ? (hit.behaviorDy || 0) : p.y - hit.y;
    const kb = ENEMY[hit.kind].knock ?? 0;
    if (kb > 0) {
      const d = Math.hypot(hitDx, hitDy) || 1;
      this.displacePlayerForced((hitDx / d) * kb, (hitDy / d) * kb);
      this.m.knocks = (this.m.knocks ?? 0) + 1;
      // **넉백은 이 게임에서 유일하게 조작을 뺏는 것**이다(contact 주석). 그 순간이
      // 화면에서도 흔들려야 「내가 밀렸다」가 읽힌다. 장식이 아니라 신호다.
    }
    this.present('hurt', {
      shake: kb > 0 ? SHAKE.knock : 0, power: kb > 0 ? 1 : 0.72,
      dx: hitDx, dy: hitDy,
    });
    this.m.hits++; this.m.dmgTaken += dmg;
    if (hitAction) {
      if (this.enemyBehavior) this.enemyBehavior.markBehaviorContact(hit, hitSeq);
      else hit.behaviorContactSeq = hitSeq;
    }
    this.enemyBehavior?.recordContact(hit, dmg, hitSeq);
    if (hit.kind === 'swarm') this.m.hitsSwarm++; else this.m.hitsElite++;
    this.m.dmgTrace.push(this.t, this.m.dmgTaken);   // ② 해석용 궤적
    this.sinceHit = 0;
    this.enemyBehavior?.resolveFrameContacts();
  }

  // 무기 — **택 1.** 자동. 순수 전투용. 구출·보호·유틸 없음(2-5).
  // ── 회전 광역 「도는 돌이 친다」 【사장님 결정 2026-07-29】 ──────────
  // **몸 옆에서 도는 물맷돌 자체가 판정을 갖는다.** 그림은 `scene.drawSling()` 이
  // 이미 그리고 있었고 **판정만 없었다.**
  //   반경 ← 끈(사거리와 같은 물리값) · 주기 ← 돌 + 걸이 · 피해 ← 돌 + **걸이**
  // ⚠ **피해에 걸이가 들어온 것이 이번 재설계다**(D — 걸이의 `aim` 을 궤도로 옮겼다).
  //   구 주석은 「피해 ← 돌」이었는데 그러면 걸이가 이 자리에서 죽어 있다.
  //   세 값이 어느 부위에서 오는지의 정본은 `config.ORBIT` 머리 주석이다.
  // **넉백 없다**(사장님 지시) — 밀어내면 자기 판정을 스스로 비운다.
  // `orbitR === 0` 이면 통째로 건너뛴다 → 지팡이·카드 경로는 **영향이 정확히 0**이다.
  orbitStrike(dt) {
    const M = this.mods;
    if (!(M.orbitR > 0)) return;
    const p = this.player;
    this._orbitT = (this._orbitT ?? 0) + dt * Math.max(0.2, M.orbitRate);
    // ⚠ **차돌은 자기 주기를 갖는다**(gear.ORB.tick). `ORBIT.tick 0.40` 이면 0.7초에
    //   한 번 「똑」 해서 **지속 피해로 안 느껴졌다** — 실측 원인 ⓒ.
    //   0 이면 기본값이므로 다른 다섯은 한 줄도 안 바뀐다.
    const TICK = M.orbitTick > 0 ? M.orbitTick : ORBIT.tick;
    while (this._orbitT >= TICK) {
      this._orbitT -= TICK;
      const R = ORBIT.r * M.orbitR + ORBIT.pad;
      // ⚠ **던지지 않는 돌은 `dmgRatio` 를 안 탄다**(gear.ORB.dmgFull). 그 값의 근거가
      //   *"던지는 것보다 세면 던질 이유가 없다"* 였고, 차돌에는 던지기가 없다 — 원인 ⓐ.
      const dmg = PASU.dmg * M.orbitDmg * (M.orbitFull ? 1 : ORBIT.dmgRatio);
      // ── 두 겹 — **원 안 전체 + 돌이 지나가는 대역**  【사장님 지시 2026-07-30 3차】
      //   `band === 0`(차돌이 아닌 다섯)이면 `inner` 가 `-1` 이 되어 **아무 적도 대역 밖으로
      //   판정되지 않는다** → 원 전체가 전량이다. 다른 돌의 궤도는 한 줄도 안 바뀐다.
      const band = M.orbitBand ?? 0;
      const inner = band > 0 ? R * (1 - band) : -1;
      const inner2 = inner > 0 ? inner * inner : -1;
      // 단단한 ◆ — 여섯 번째 판정에서만 원 안쪽도 대역과 같은 전량을 받는다.
      // 반경·틱 수·상한은 그대로라 6틱 평균은 안쪽만 약 20% 상승이다.
      const sweepEvery = M.orbitSweepEvery | 0;
      if (sweepEvery > 0) this._orbitSetBeat = (this._orbitSetBeat ?? 0) + 1;
      const sweep = sweepEvery > 0 && (this._orbitSetBeat % sweepEvery === 0);
      const weak = sweep ? dmg : dmg * ORBIT.discRatio;
      if (sweep) {
        this.m.orbitSweeps++;
        this._orbitSweepAt = this.t; this._orbitSweepSeq = (this._orbitSweepSeq ?? 0) + 1;
      }
      // **머리 위로 도는 타원이다.** 탑다운 60도에서 그 원은 세로가 짧게 보이고,
      // 그림만 타원으로 하면 판정과 어긋나므로 **판정도 같은 타원**이다.
      const cy = p.y + ORBIT.cy;
      // ⚠⚠ **상한이 묵은 차돌의 축이다**(gear.ORB · mods.orbitCap).
      //   `ORBIT.cap 4` 는 「300마리 화면의 난간」이라 부품이 안 건드리게 두었는데,
      //   차돌이 근접(궤도)이 되면서 **그 난간이 곧 차돌의 천장**이 됐다. 실측(N8·●5):
      //   반경·주기·피해를 다 올려도 cap 4 에서 4.89킬/초에 묶이고, cap 만 12 로
      //   풀면 8.21(+68%)이다. → 차돌만 반경²(면적)에 비례해 난간이 올라간다.
      //   `Infinity`(묵은 세트 ●●●●●)면 난간이 없다.
      const capMul = M.orbitCap ?? 1;
      let left = capMul === Infinity ? Infinity : Math.max(1, Math.round(ORBIT.cap * capMul));
      let hit = 0;
      for (let i = this.eagles.length - 1; i >= 0 && left > 0; i--) {
        const e = this.eagles[i];
        if (e.state !== 'grounded' || e.hp <= 0) continue;
        const dx = e.x - p.x, dy = (e.y - cy) / ORBIT.ky, lim = R + e.r;
        const d2 = dx * dx + dy * dy;
        if (d2 > lim * lim) continue;
        this.damageEagle(e, (inner2 > 0 && d2 < inner2) ? weak : dmg, 'orbit');
        left--; hit++;
      }
      // **인덱스를 안 믿는다.** ⚠ 구 이유(「damageEnemy 가 오버킬 이월로 배열을
      //   건드린다」)는 그 기제와 함께 사라졌지만 **규율은 그대로다** — 바로 아래
      //   `reapEnemy` 가 죽은 것을 splice 하므로 이 루프는 여전히 뒤에서 앞으로 돈다.
      for (let i = this.enemies.length - 1; i >= 0 && left > 0; i--) {
        const e = this.enemies[i];
        if (!e || e.hp <= 0) continue;
        const dx = e.x - p.x, dy = (e.y - cy) / ORBIT.ky, lim = R + e.r;
        const d2 = dx * dx + dy * dy;
        if (d2 > lim * lim) continue;
        // 대역 안(돌이 지나가는 자리)이면 전량, 그 안쪽이면 `discRatio` 만큼
        this.damageEnemy(e, (inner2 > 0 && d2 < inner2) ? weak : dmg, 'orbit');
        this.reapEnemy(e);
        left--; hit++;
      }
      if (hit) {
        this.m.orbitHits = (this.m.orbitHits ?? 0) + hit;
        this._orbitAt = this.t;          // 렌더용 — 링이 퍼지는 순간
        this._orbitR = R;                // 그 순간의 판정 반경(그림보다 넓다)
        this._orbitN = hit;              // 몇 마리를 쓸었나 — 링 크기에 쓴다
        // **여럿을 한 번에 쓸면 멈칫한다.** 하나만 스치면 아무 일도 안 일어난다.
        if (hit >= 2) {
          this.present(this.mods.tier >= 6 ? 'diamond' : 'normal', {
            // 차돌은 0.18초 상시 틱이라 매번 전역 시간을 늦추면 피해 밖의 숨은
            // 방어력이 된다. 링·흔들림은 유지하고, 근접 차돌만 hitstop을 끈다.
            hitstop: this.mods.meleeOnly ? ORBIT.meleeHitstop : HIT.orbit,
            shake: SHAKE.shock * 0.5 * Math.min(2, hit / 2),
            power: Math.min(1, hit / 4), dx: this.player.ax, dy: this.player.ay,
          });
        }
      }
    }
  }

  // ══════════════════════════════════════════════════════════════
  //  ■■ 묵은 차돌 — 「하나를 키운다」 (A-3)
  //
  //    던진다 → 부순다 → 바닥에 떨어져 남는다 → 지나가면 회수된다 →
  //    더 커져 있다 → 다시 던진다        ↑ 그동안은 궤도(도는 돌)가 지킨다
  //
  //  ■ **손에 있는 것만 던진다.** 그래서 회수 왕복이 실제 비용이 되고, 그 공백을
  //    궤도가 메운다(묵은 걸이가 궤도 최강인 것과 한 벌 — config.ORBIT 주석).
  //  ■ **개수는 받침이 정한다**(C-1). 두 개를 물면 둘이 각각 자라고 각각 회수해야 한다.
  //  ■ **밤이 끝나면 리셋** — World 가 밤마다 새로 만들어지므로 저장 경로가 없다(충돌 7).
  //
  //  ⚠⚠ **회수는 새 흡인 개념을 만들지 않는다**(충돌 5). `PLAYER.pickup` 반경과
  //    `PIECE_PULL`(base/gain/eat)을 **상수째로 재사용**한다 — 그래야 낮의 「그러모으기」
  //    (pickupMul)와 「팔을 크게 돌린다」가 차돌에도 똑같이 먹는다.
  //    ⚠ 다만 **`pullPieces`/`collectPieces` 함수 자체는 안 쓴다.** 그쪽은 조각 격자
  //      (`_pgrid`)를 돌고, 먹으면 `gainPieces`(경제 장부)를 때린다. 차돌은 많아야
  //      세 개라 격자가 과하고 **무기 상태이지 수입이 아니다** — 같은 함수에 넣으면
  //      조각 총량 장부가 오염된다. **상수는 공유, 루프는 분리.**
  // ══════════════════════════════════════════════════════════════
  // ⚠⚠ **구 차돌 계열 메서드 다섯이 여기 있었다 — 통째로 삭제됐다.**
  //   `syncKeepStones` · `keepInHand` · `dropKeepStone` · `markKeepBreak` · `stepKeepStones`.
  //   차돌이 근접(궤도)으로 재설계되면서 「던지면 필드에 남고 주우면 자란다」가 없어졌다
  //   (2026-07-30 · gear.STONE_AXIS.chadol 주석에 폐기 근거가 있다).
  //   같이 지운 것: `config.KEEP`·`KEEPV` · `mods.grow` · `gear.GROW` ·
  //   `world.keepStones`/`_keepDropAt`/`_keepDropXY`/`_keepBackAt`/`_keepCoolAt` ·
  //   지표 `keepThrown`/`keepBack`/`keepGrowMax`/`keepCool`/`keepThrowG` · `scene.drawKeepStones`.

  // ── 굳은 세트 「터진 자리가 또 터진다」 ────────────────────────
  // 예약된 폭발을 시간이 되면 터뜨린다. 세트가 안 걸리면 `blasts` 가 항상 비어 있어
  // **판정을 한 번도 안 돈다.**
  //
  // ⚠⚠ **재폭발은 히트스톱을 다시 쌓지 않는다.**   【실측 2026-07-30 — 회귀 수정】
  //   처음엔 `explodeAt` 을 그냥 다시 불렀다. 그러니 재폭발마다 `HIT.land`(0.075)가
  //   또 쌓여 히트스톱이 상한(0.14)에 **계속 붙어 있었고**, 히트스톱 동안 시간이
  //   0.18배로 흐르므로 **게임이 통째로 굶었다**:
  //     무거운 돌 ●●●●● — 세트 off 1392킬 / 세트 on **1116킬 (−20%)**
  //     히트스톱에 붙어 있던 프레임 12% → **23%**, 4분에 흐른 월드시간 217초 → **194초**
  //   즉 **세트가 발동하면 오히려 약해졌다.** 「회차에 한 번 볼까 말까 한 자랑거리」가
  //   순손해였던 것이므로 E의 요구가 거꾸로 깨져 있었다.
  //   → 재폭발은 **판정과 그림만** 한다. 손맛(히트스톱)은 첫 착탄 한 번의 것이다.
  //     흔들림은 `Math.max` 라 누적되지 않고 데칼은 40개 링버퍼라 둘은 그대로 둔다.
  stepBlasts() {
    if (!this.blasts.length) return;
    for (let i = this.blasts.length - 1; i >= 0; i--) {
      const b = this.blasts[i];
      if (this.t < b.at) continue;
      this.blasts.splice(i, 1);
      this.explodeAt(b.x, b.y, b.r, b.dmg, false);   // ← false = 손맛을 다시 쌓지 않는다
      if (b.left > 0) {
        this.blasts.push({ ...b, at: this.t + SETFX.againSec, left: b.left - 1 });
      } else if (!b.crown && b.crownDmg > 0) {
        // 무거운 ◆ — 기존 재폭발을 모두 쓴 뒤에만 절반 위력의 바깥 고리가 한 번 더 펼쳐진다.
        const baseDmg = b.baseDmg ?? b.dmg * 2;
        this.blasts.push({ x: b.x, y: b.y, r: b.r * b.crownR,
          dmg: baseDmg * b.crownDmg, baseDmg, at: this.t + SETFX.againSec,
          left: 0, crown: true, crownDmg: 0, crownR: 1 });
      }
      if (b.crown) {
        this.m.splashCrowns++;
        this.m.splashCrownDmg += b.dmg;
        this.m.splashCrownBaseDmg += b.baseDmg ?? b.dmg * 2;
        this._splashCrownAt = this.t; this._splashCrownSeq = (this._splashCrownSeq ?? 0) + 1;
      }
      this.m.blasts = (this.m.blasts ?? 0) + 1;      // 「정말로 또 터졌는가」의 실측값
    }
  }

  // 손에 익은 ◆ — 예약 시각을 넘긴 덧선을 일반 투사체 배열에 넣는다.
  // 예약본은 `echo` 표시와 `vid:null`을 가져 다시 덧선을 예약하지 않고
  // 기존 발사 묶음 계수도 오염시키지 않는다.
  stepLineEchoes() {
    if (!this.lineEchoes.length) return;
    for (let i = this.lineEchoes.length - 1; i >= 0; i--) {
      const e = this.lineEchoes[i];
      if (this.t < e.at) continue;
      this.lineEchoes.splice(i, 1);
      for (const shot of e.shots) {
        this.shots.push(shot);
        this.m.shots++;
      }
      this.m.lineEchoes++;
      this._lineEchoAt = this.t; this._lineEchoXY = { x: e.x, y: e.y };
      this._lineEchoAngle = e.angle; this._lineEchoSeq = (this._lineEchoSeq ?? 0) + 1;
    }
  }

  autoWeapon(dt, input = null) {
    const p = this.player, ws = this._ws;
    if (this.weapon === 'pasu') {
      this.orbitStrike(dt);
      this._fire = (this._fire ?? 0) + dt;
      // 「무거운 돌」의 간격 +15% 는 **벌점**이므로 상한(0.25초)을 다시 풀지 않는다 —
      // weaponStats 가 자른 뒤에 곱한다. 느려지는 방향이라 상한을 넘길 일이 없다.
      const M = this.mods;
      // 낮 물맷돌축 3단계(연사)는 여기서 먹는다. 사거리는 gearWeaponStats가
      // base×현재 축×장비×낮을 모두 한 번씩 합친 ws.range 하나가 정본이다.
      const iv = M.slowMul / (ws.rate * this.dayIn.fireRateMul), RG = ws.range;
      // Standard-layout Xbox and PlayStation pads expose the right stick on
      // axes 2/3. While it is outside the radial deadzone it owns the shot
      // angle; releasing it restores every existing auto-aim rule.
      const manualAim = !!input?.aimActive
        && Math.hypot(Number(input.aimX) || 0, Number(input.aimY) || 0) > 1e-6;
      const manualAimAngle = manualAim
        ? Math.atan2(Number(input.aimY) || 0, Number(input.aimX) || 0)
        : 0;
      if (manualAim) {
        p.ax = Math.cos(manualAimAngle);
        p.ay = Math.sin(manualAimAngle);
      }
      // Heavy stone normally chooses the densest of eight sectors. Manual aim
      // must bypass that selector completely or it would fight the right stick.
      const heavySectorAim = this.gear?.stone?.p?.id === 'mugeo' && !manualAim;
      let heavyAimReady = false, heavyAimTarget = null, heavyAimAngle = 0;
      while (this._fire >= iv) {
        this._fire -= iv;
        // ── 【10-B 2-2】 다발 투사체는 **부채꼴로 퍼진다** ────────
        // 첫 구현은 투사체마다 **서로 다른 최근접 표적**을 물렸다. 그러면 「양손 던지기」
        // 6중첩이 그대로 **×6 처치**가 되고, 실측에서 N1 원딜이 안정성 99.9 · 피해 0 —
        // 밤이 통째로 사라졌다. 유도탄 6발은 뱀서 문법도 아니다.
        //   근딜은 밀집도에 반응하는데 원딜만 무조건 명중이면 **원딜이 상한 없는 무기**가 된다.
        // 그래서 **원딜도 밀집도에 반응시키되, 반응하는 밀집도의 종류를 다르게 한다**:
        //   · 근딜 = **둘러싸임**에 반응한다(호 안의 수)
        //   · 원딜 = **정렬**에 반응한다(앞쪽에 줄지어 있는가)
        // 조준은 최근접 하나로 하고 나머지는 그 방향 좌우로 흩뿌린다. 한산하면 빗나가고,
        // 붐비면 다 맞는다. **8-6a "도는 돌 개수 ← 투사체 수"가 그대로 판정이 된다.**
        // ⚠⚠ **묵은 차돌은 던지지 않는다**(사장님 지시 2026-07-30 2차 재설계).
        //   *"던지는게 아니라 그 물멧돌 돌리는 범위 자체가 롤에 가렌처럼 빙글빙글 도는
        //   그 범위에 딜이 지속적으로 들어가게."*
        //   투사체를 아예 안 만든다 — 화력 전부가 `orbitStrike` 로 간다. 그래서
        //   **원거리가 0 이고 붙어야 강하다.** 그것이 이 돌의 대가다.
        if (M.meleeOnly) break;
        // 무거운 돌만 다윗 둘레를 8방향으로 나눠 **가장 많은 무리의 중심**을 노린다.
        // 한 프레임에 발사가 밀려도 집계와 atan2는 한 번뿐이다. 다른 돌은 기존 우선순위를
        // 그대로 써 장비 성격이 섞이지 않는다.
        let tgt = null;
        if (manualAim) {
          // A held right stick may deliberately fire into an empty direction.
        } else if (heavySectorAim) {
          if (!heavyAimReady) {
            heavyAimTarget = this.pickHeavySectorTarget(p, RG);
            if (heavyAimTarget) {
              heavyAimAngle = Math.atan2(heavyAimTarget.y - p.y, heavyAimTarget.x - p.x);
            }
            heavyAimReady = true;
          }
          tgt = heavyAimTarget;
        } else {
          tgt = this.pickTarget(p, RG);
        }
        if (!manualAim && !tgt) continue;
        const a0 = manualAim
          ? manualAimAngle
          : heavySectorAim
            ? heavyAimAngle
            : Math.atan2(tgt.y - p.y, tgt.x - p.x);
        p.ax = Math.cos(a0);
        p.ay = Math.sin(a0);
        // ── 【결③ 눌러 던진다】 조준선 위 **정렬 밀도**가 곧 발당 무게다 ──
        // 「어느 방향에 몇 마리가 줄지어 있는가」를 세고 그만큼 무거워진다. 갈래가
        // 묻는 것이 「어떤 모양의 줄인가」이므로, ③은 모양이 아니라 **밀도**를 산다.
        // 이 값은 밀집도 채널(v2 D5)이 화면에 그대로 그린다 — 조준선이 밝아진다.
        let aligned = 0;
        if (M.alignDmg > 0) {
          const cx = Math.cos(a0), cy = Math.sin(a0), CORR = 1.6 * UNIT;
          for (const e of this.enemies) {
            const ex = e.x - p.x, ey = e.y - p.y;
            const along = ex * cx + ey * cy;
            if (along < 0 || along > RG) continue;
            if (Math.abs(-ex * cy + ey * cx) > CORR) continue;   // 조준선까지의 수직 거리
            aligned++;
          }
        }
        this._aligned = aligned;                 // 렌더용(밀집도 채널)
        const T = M.traits ?? {};
        const shotDmg = PASU.dmg * M.dmgMul * (1 + M.alignDmg * aligned);
        let k = Math.max(1, ws.projectiles + Math.round(M.projAdd));
        // **확산각은 갈래가 고르는 값이다** — ①은 좁혀 실선으로, ②는 넓혀 고리로.
        const SP = SHOT_SPREAD * M.spreadMul;
        const vid = (this._vid = (this._vid ?? 0) + 1);   // 발사 묶음 — 결② 진화 조건이 읽는다
        const volleyStart = this.shots.length;
        // 발사 묶음의 모양만 렌더러에 남긴다. 잔돌 ◆는 이 한 신호로 부채 전체를
        // 한 박자에 열고, 고른 돌 ◆는 선두 돌을 강조한다. 판정은 이 값을 읽지 않는다.
        this._volleyAt = this.t;
        this._volleyXY = { x: p.x, y: p.y };
        this._volleyAngle = a0;
        this._volleyN = k;
        this._volleySeq = (this._volleySeq ?? 0) + 1;
        if (M.tier >= 6) {
          this.present('diamond', {
            shake: 0.12, power: 0.75, dx: Math.cos(a0), dy: Math.sin(a0),
          });
        }
        // 물수제비 — 첫 간격은 **사거리 배율(끈)을 탄다**(gear.SKIP 주석)
        const sgap0 = M.skips > 0 ? M.skipGap * (M.rangeMul || 1) : 0;
        for (let i = 0; i < k; i++) {
          // 가운데 한 발은 정확히 조준선, 나머지는 ±SPREAD 씩 벌어진다
          const off = k === 1 ? 0 : (i - (k - 1) / 2) * SP;
          const a = a0 + off;
          // dmg/pierce/frag 를 **발마다 실어 보낸다** — 파편은 같은 배열을 쓰되
          // 스스로는 안 쪼개지고(frag:0) 관통도 안 한다.
          this.shots.push({ x: p.x, y: p.y,
                            vx: Math.cos(a) * SHOT_SPEED, vy: Math.sin(a) * SHOT_SPEED,
                            // 발사 순간의 최종 사거리를 보존한다. 이후 장비가 바뀌어도 이미
                            // 던진 돌의 끝점과 「멀리서 더 아프다」 기준은 흔들리지 않는다.
                            maxRange: RG, life: RG / SHOT_SPEED,
                            dmg: shotDmg,
                            // 공통 관통 바닥의 **분수**는 기대값으로 돌린다. 가벼운 돌은
                            // 그 공통 몫에 +1을 더해 보내므로 한 몸은 확정으로 뚫고 분수도 보존한다.
                            // 관통 2.6을 매 발 3으로 올리지는 않는다 — 소수부만 확률로 보존한다.
                            pierce: M.pierceInf ? 999 : this.iPart(M.pierce),
                            frag: this.iPart(M.shards),
                            ramp: M.pierceRamp, hits: 0, pad: M.hitPad,
                            splash: M.landSplash, vid,
                            stoneId: M.stoneId, diamond: M.tier >= 6,
                            volleyIndex: i, volleyCount: k,
                            // ── 거친 납작한 돌 「물수제비」 ─────────────────
                            //   skips 남은 튐 · sgap 다음 간격(px) · sdec 등비 ·
                            //   sarm 다음 자국까지 **더 날아가야 하는 거리**
                            //   ⚠ sarm 이 있어야 자국이 화면에서 **앞으로 간다**.
                            //     없으면 한 자리에서 ◆32번 튄다.
                            //   ⚠ `wet` 0 = 아직 물에 안 닿았다. **첫 명중이 켠다** —
                            //     그래야 조준한 자리를 맞히고 그 자리에서 자국이 뻗는다.
                            skips: M.skips, skipTotal: M.skips, skipN: 0,
                            sgap: sgap0, sdec: M.skipDecay, sarm: 0, wet: 0,
                            //   sseek 다음 적을 찾는 반경의 **하한**(빗나감을 줄인다)
                            sseek: M.skips > 0 ? M.skipSeek * (M.rangeMul || 1) : 0,
                            anchorEvery: M.skipAnchorEvery, anchorMul: M.skipAnchorMul,
                            // ── 길든 세트 「조각이 다시 조각을 낸다」 ────────
                            chain: M.shardChain, shardGen: 0, finalSeek: M.shardFinalSeek,
                            // ── 굳은 세트 「터진 자리가 또 터진다」 ──────────
                            again: M.splashAgain,
                            crownDmg: M.splashCrownDmg, crownR: M.splashCrownR,
                            // ── 부가 줄(●●●○○부터) — **여덟이 전부 미구현이었다** ──
                            //   좋은 등급을 뽑아도 아무 일이 안 일어나서 보상이 없었다.
                            //   ric/roll 은 **투사체가 더 생기는** 것이라 화면 밀도에 직결된다.
                            ox: p.x, oy: p.y,
                            ric: T.ricochet ?? 0, roll: T.missRoll ?? 0,
                            first: T.firstShot ?? 0, rampH: T.rampHit ?? 0,
                            eDmg: T.eliteDmg ?? 0, fDmg: T.farDmg ?? 0 });
          this.m.shots++;
        }
        // 손에 익은 ◆ — 여섯 번째 **발사 묶음**만 방금 그린 선을 절반 위력으로 복사한다.
        if (M.lineEchoEvery > 0 && vid % M.lineEchoEvery === 0) {
          const echoShots = this.shots.slice(volleyStart).map(s => ({
            ...s, dmg: s.dmg * M.lineEchoDmg, vid: null, echo: true,
          }));
          if (echoShots.length) this.lineEchoes.push({
            at: this.t + M.lineEchoDelay, shots: echoShots,
            x: p.x, y: p.y, angle: a0,
          });
        }
      }
    }
    // 【2026-07-31】 지팡이 스윙 판정(호·밀집도 함수·되치기·충격파)이 여기 있었다 —
    //   **무기 삭제와 함께 통째로 삭제.** 근접 광역의 자리는 이제 차돌(궤도)이 잇는다.
  }

  // **투사체 충돌은 `separate()` 가 이미 만들어 둔 격자를 재사용한다.**
  // 구 구현은 발마다 전 적을 훑는 O(발 × 적)이었다. 「양손 던지기」로 투사체가 6발이 되고
  // 연사가 4발/초까지 오르면 화면의 발이 수십이 되는데, 거기에 적 400을 곱하면
  // **헤드리스가 런당 16초**까지 늘어졌다(실측). 게임 쪽에서도 0-A 벤치의 직접 위협이다.
  // 격자는 이번 프레임의 `separate()` 가 방금 채웠으므로 그대로 쓸 수 있다.
  // ── 투사체를 지우는 **유일한 문**  ────────────────────────────────
  // 차돌은 사라지지 않고 바닥에 남으므로(A-3) 지우는 자리가 여러 군데면 반드시
  // 한 군데가 새고 그 돌은 영영 안 돌아온다. **지우려면 여기를 통과해야 한다.**
  killShot(i) {
    const s = this.shots[i];
    this.shots.splice(i, 1);
  }

  // ══════════════════════════════════════════════════════════════
  //  ■■ 물수제비 — **자국은 「거리」로 난다. 「명중」으로 나지 않는다**  (A-2)
  //
  //  ⚠⚠ **처음 구현은 「맞으면 튄다」였고 그것이 축을 통째로 죽였다.**  【실측 회귀】
  //    자국을 명중에 묶으면 튐 수는 **예산이 아니라 명중 횟수**가 정한다. 한 발이
  //    구 고정 수명 동안 실제로 맞히는 것은 1~2체뿐이라 25 예산 중 2 만 쓰였다 — 실측:
  //      등급   예산   발당 실제 튐   나아간 거리
  //      ●1      5        1.58          468유닛
  //      ●5     25        1.66          502유닛   ← **예산 5배인데 +5%**
  //      ●5+세트 25(감쇠1) 1.54         539유닛   ← 세트도 아무 일을 안 했다
  //    A-2 가 *"이 규칙이 밸런스의 전부다"* 라고 못 박은 **「커버리지가 로그로 는다」**가
  //    성립하지 않았고, *"25개 착탄점"* 이 실제로는 2개였다.
  //
  //  → **자국은 적이 있든 없든 난다.** 물수제비는 물을 때리는 것이고, 그 자리에
  //    짐승이 있으면 맞는 것이다. 그래야 착탄점이 정말 25개가 되고 커버리지가 예산을
  //    따라 로그로 는다.
  //
  //  ■ 순서: 던진다 → (평범하게 날아 **첫 명중**) → 그 자리에서 물수제비가 시작된다
  //    → 간격만큼 미끄러진다(아무것도 못 친다) → **자국**: 그 자리의 한 체를 친다
  //    → 간격이 등비로 줄고 반복 → 예산이 다하면 가라앉는다(평범한 돌로 돌아간다)
  //  ⚠ 첫 자국을 발사점에 두지 않는다 — 그러면 25개가 전부 몸 옆에 쌓이고
  //    「조준한 자리를 맞힌다」가 사라진다. 그래서 `wet` 이 첫 명중에서 켜진다.
  //  ⚠ **광역을 안 붙인다.** 자국 하나는 그 자리의 한 체를 친다(gear.SKIP 주석).
  // ══════════════════════════════════════════════════════════════
  //  자국 하나를 **소비한다.** `hit` = 그 자국이 실제로 뭔가를 맞혔는가.
  //  예산이 없으면 `wet` 을 끄고 false — 그때부터 평범한 돌이다.
  markSkip(s, hit) {
    if (!(s.skips > 0)) { s.wet = 0; return false; }
    s.skips--;
    s.wet = 1;                  // 물수제비가 시작됐다(첫 명중에서 켜진다)
    s.sarm = s.sgap;            // 이만큼 더 날아가야 다음 자국이 난다
    // ⚠ **예약한 간격(`s.sgap`)이 아니라 실제로 움직인 거리를 센다**(m.skipDist 주석).
    //   앞 자국이 있을 때만 더한다 — 첫 자국까지는 「평범한 비행」이라 튐의 거리가 아니다.
    if (s.smx != null) {
      this.m.skipDist += Math.hypot(s.x - s.smx, s.y - s.smy);
      this.m.skipSpans++;
    }
    s.smx = s.x; s.smy = s.y;   // 다음 자국이 여기서부터 잰다
    // ══════════════════════════════════════════════════════════════
    //  ★ **적에서 적으로 꺾는다** — 직선 물수제비가 아니다.  【사장님 지시 2026-07-30】
    //    *"직선으로 하는게 아니라 곡선으로 진짜 몹들을 통통통 존나 튀어다니게"*
    //
    //  ■ 구 구현은 **방향을 안 바꿨다** — 그래서 튐 자국이 앞으로 일직선으로 깔리고,
    //    적이 그 선 위에 없으면 25개가 전부 **빈 땅을 때렸다.** 그게 이 돌이 여섯 중
    //    최하위였던 이유다(N8 6.93킬/초 · 히트/초 4.7 — 튐 수는 제일 많은데 히트가 최저).
    //  ■ 이제 **다음 자국을 「가장 가까운 적」에 둔다.** 그러면
    //      · 궤적이 저절로 꺾여 **곡선(지그재그)**이 된다
    //      · 헛튐이 사라져 **밸런스가 규칙으로 오른다**(값을 안 올려도)
    //      · 몹이 붐빌수록 오래 튄다 = **밀도가 곧 보상**
    //  ■ 탐색 반경은 **지금의 「다음 간격」(`s.sgap`)을 그대로 쓴다.** 그 값이 등비로
    //    줄고 있으므로(SKIP.decay) 뒤로 갈수록 가까운 적만 찾다가 **저절로 잦아든다** —
    //    「앞의 대여섯 번이 거리를 다 먹는다」는 성질이 그대로 유지된다. 새 상수가 없다.
    //  ⚠ **직전에 맞은 그 적은 제외한다**(`s.hit` 은 아래에서 비워지므로 여기서 읽는다).
    //    안 그러면 같은 적을 ◆32번 때려 「통통통」이 아니라 한 마리 처형이 된다.
    if (s.skips > 0) {
      // ⚠ **탐색 반경에는 하한이 있다**(gear.SKIP.seekMin). 간격(`s.sgap`)은 등비로 줄어야
      //   자국이 뒤로 갈수록 촘촘해지는데, **찾는 반경까지 같이 줄면 뒤쪽 튐이 적을 아예
      //   못 찾는다**(0.80^24 ≈ 0.005배). 실측 45%가 그래서 빗나갔다 —
      //   *"지금은 너무 빗나가는게 많아"*(사장님).
      const nextN = (s.skipN ?? 0) + 1;
      const anchorEvery = s.anchorEvery | 0;
      const anchor = anchorEvery > 0 && nextN % anchorEvery === 0;
      const sp = Math.hypot(s.vx, s.vy) || SHOT_SPEED;
      const normalR = Math.max(s.sgap, s.sseek ?? 0);
      // 매끈한 ◆ — 8·16·24번째 자국은 남은 최대 수명 안에 닿는 가장 가까운 적까지
      // 탐색한다. 단순히 평소 반경을 두 배만 하면 무리가 한 화면만 벌어져도 ◆ 박자가
      // 직선으로 새므로, 4초 생존 예산 안의 적은 반드시 다시 물게 한다.
      const anchorR = Math.max(normalR * (s.anchorMul ?? 1),
                               sp * Math.max(0, SKIPW.maxLife - SKIPW.lifePad));
      const R = anchor ? anchorR : normalR;
      const R2 = R * R;
      if (anchor) this.m.skipAnchorBeats++;
      let best = null, bd = Infinity;
      for (const e of this.enemies) {
        if (!e || e.hp <= 0 || e === s.hit) continue;
        const dx = e.x - s.x, dy = e.y - s.y, d2 = dx * dx + dy * dy;
        if (d2 > R2 || d2 >= bd) continue;
        bd = d2; best = e;
      }
      if (best) {
        const d = Math.sqrt(bd) || 1;
        s.vx = (best.x - s.x) / d * sp;
        s.vy = (best.y - s.y) / d * sp;
        s.sarm = d;               // 그 적까지가 이번 간격이다
        s.bent = (s.bent ?? 0) + 1;
        this.m.skipBends = (this.m.skipBends ?? 0) + 1;
        if (anchor) {
          this.m.skipAnchors++;
          this._skipAnchorAt = this.t; this._skipAnchorXY = { x: s.x, y: s.y };
          this._skipAnchorSeq = (this._skipAnchorSeq ?? 0) + 1;
        }
      }
      // 못 찾으면 방향을 유지한다 — 그때는 구 동작(직선으로 한 번 더)과 같다
    }
    // **자국이 화면에 남는다** — 렌더 전용 링버퍼(`this.skipMarks` 주석). `hit` 를 같이
    //   실어 보내 scene 이 「빈 땅을 때린 자국」과 「몸을 때린 자국」을 갈라 그린다.
    s.skipN = (s.skipN ?? 0) + 1;
    this.skipMarks.push({
      x: s.x, y: s.y, t: this.t, hit: hit ? 1 : 0,
      n: s.skipN, total: s.skipTotal ?? 0, diamond: !!s.diamond,
    });
    if (this.skipMarks.length > SKIPV.cap) this.skipMarks.shift();
    s.sgap *= s.sdec;           // 등비로 준다 — 앞의 대여섯 번이 거리를 다 먹는다
    s.hit = null;               // 다음 자국은 그 자리의 한 체를 친다
    // ⚠ `hits` 는 **맞은 것만** 센다. 「연달아 맞으면 는다」(rampH)와 결① 진화 조건이
    //   이 값을 읽으므로 헛자국을 세면 두 값이 거짓이 된다.
    if (hit) s.hits = (s.hits ?? 0) + 1;
    // 남은 간격을 날아갈 만큼은 살려 준다(거친 세트는 감쇠 1이라 멀리 간다)
    const sp = Math.hypot(s.vx, s.vy) || SHOT_SPEED;
    s.life = Math.min(SKIPW.maxLife, Math.max(s.life, s.sarm / sp + SKIPW.lifePad));
    this.m.skips++;
    return true;
  }
  // 이번 프레임이 **자국인가.** 미끄러지는 구간이면 그 프레임은 아무것도 못 친다.
  // `moveShots` 와 `moveShotsTail` 이 **같은 몸을 쓴다** — 갈리면 한쪽만 고쳐진다.
  //   반환: 'glide'(미끄러진다) · 'mark'(자국이다) · 'plain'(물수제비 전/후의 평범한 비행)
  skipPhase(s, dt) {
    if (!s.wet) return 'plain';
    s.sarm -= Math.hypot(s.vx, s.vy) * dt;
    return s.sarm > 0 ? 'glide' : 'mark';
  }
  // 미끄러지는 중 필드 밖으로 나간 돌은 바로 치운다 — 적은 필드 안에만 있으므로
  // 밖의 돌은 아무 일도 안 하면서 목록만 차지한다(동시 투사체 = 프레임 예산).
  skipOut(s) {
    // ⚠ **다윗 기준으로 잰다** 【벽 제거 2026-08-02】. 구 판은 필드 밖으로 나간 돌을
    //   쳤는데 필드가 없어졌다. 그대로 두면 **어떤 돌도 안 치워져** 목록만 불어난다.
    //   기준은 적을 치우는 반경과 같다 — 그 밖에는 맞을 것이 없기 때문이다.
    const p = this.player, O = SKIPW.outMargin;
    const R = this.spawnRing * FIELD.cullMul + O;
    const dx = s.x - p.x, dy = s.y - p.y;
    return dx * dx + dy * dy > R * R;
  }

  hitBossWithShot(s, shotIndex) {
    const b = this.boss;
    if (!b || b.dead || s.hitB || (s.hit && s.hit.includes(b))) return false;
    const dx = s.x - b.x, dy = s.y - b.y, lim = b.cfg.r + 4;
    if (dx * dx + dy * dy >= lim * lim) return false;
    this.hitBoss(this.shotDamage(s, b.kind));
    this.spawnShards(s, s.x, s.y, b);
    this.m.shotHits++;
    // ⚠ **자국도 `hitB` 를 세운다.**   【검증 2026-07-30 — 중복 타격】
    //   전엔 튐 가지가 `hitB` 를 안 세우고 빠져서, 자국 무장거리(93px)가 보스 몸보다
    //   짧을 때 **한 발이 보스를 두 번 때렸다**(실측 납작한 돌 ●5 발당 최대 2타 ·
    //   평균 1.16타. 다른 다섯 돌은 모두 최대 1타). `hitB` 는 바로 그 중복을 막으려고
    //   있는 값이므로 튐 경로가 그것을 우회하면 안 된다 — 「스치고 계속 난다」는
    //   **계속 나는 것**이지 다시 때리는 것이 아니다.
    if (this.markSkip(s, true)) { s.hitB = true; }
    else if (s.pierce > 0) { s.pierce--; s.hitB = true; }
    else this.killShot(shotIndex);
    return true;
  }

  hitEagleWithShot(s, shotIndex, mark = false) {
    for (let j = this.eagles.length - 1; j >= 0; j--) {
      const e = this.eagles[j];
      if (!e.targetable || e.hp <= 0 || (s.hit && s.hit.includes(e))) continue;
      const dx = s.x - e.x, dy = s.y - e.y, lim = e.r + 4 + (s.pad ?? 0);
      if (dx * dx + dy * dy >= lim * lim) continue;
      this.damageEagle(e, this.shotDamage(s, 'threat'), s);
      this.spawnShards(s, e.x, e.y, e);
      this.landSplash(s, e.x, e.y);
      this.m.shotHits++;
      this.tallyShotHit(s);
      if (this.markSkip(s, true)) { /* 다음 자국으로 */ }
      else if (s.pierce > 0) {
        s.pierce--; s.hits = (s.hits ?? 0) + 1; (s.hit ??= []).push(e);
      } else if (this.tryRicochet(s, e)) {
        s.hits = (s.hits ?? 0) + 1;
      } else this.killShot(shotIndex);
      return true;
    }
    if (mark) this.markSkip(s, false);
    return false;
  }

  // 한 프레임이 남은 수명보다 길어도 마지막 이동을 정확한 끝점에서 자른다.
  // 반환값은 실제로 움직인 시간이며, 물수제비 거리도 요청 dt가 아니라 이 값을 센다.
  advanceShot(s, dt) {
    const remaining = Math.max(0, Number(s.life) || 0);
    const elapsed = Math.min(Math.max(0, dt), remaining);
    s.x += s.vx * elapsed;
    s.y += s.vy * elapsed;
    s.life = elapsed >= remaining ? 0 : remaining - elapsed;
    return elapsed;
  }

  // 끝점도 충돌 판정을 한 번 거친 뒤 닫는다. 그 충돌이 물수제비나 도탄으로 수명을
  // 연장했다면 살리고, 아니면 기존 「빗나가면 구른다」를 마지막으로 시도한다.
  finishShotRange(s, i, expired) {
    if (!expired || s.life > 0 || this.shots[i] !== s) return;
    if (!this.tryRoll(s)) this.killShot(i);
  }

  moveShots(dt) {
    const head = this._head, next = this._next, C = this._cell,
          cols = this._cols, rows = this._rows, n = this.enemies.length;
    const grid = n >= 2;      // separate() 가 n<2 면 격자를 안 채운다
    for (let i = this.shots.length - 1; i >= 0; i--) {
      const s = this.shots[i];
      const elapsed = this.advanceShot(s, dt);
      const expired = s.life <= 0;
      // 물수제비 — **미끄러지는 구간이면 이 프레임은 아무것도 못 친다**
      const ph = this.skipPhase(s, elapsed);
      if (ph === 'glide') {
        if (this.skipOut(s)) this.killShot(i);
        this.finishShotRange(s, i, expired);
        continue;
      }
      const mark = ph === 'mark';        // 이번 프레임이 **자국**이다
      const groundN = this.enemies.length;
      // 보스 먼저 — 몸이 커서 물량층에 가려 안 맞는 일이 없어야 한다.
      // 격자 무효 뒤 꼬리 경로도 같은 helper를 써서 이 우선순위를 보존한다.
      if (this.hitBossWithShot(s, i)) {
        this.finishShotRange(s, i, expired);
        continue;
      }
      // 독수리도 일반 지상층보다 먼저 맞지만, 바로 위의 기존 보스 우선 계약은
      // 침범하지 않는다. N40에서 두 위협이 포개져도 보스가 물량에 가려지지 않는다.
      if (this.hitEagleWithShot(s, i, false)) {
        this.finishShotRange(s, i, expired);
        if (this.enemies.length !== groundN) return this.moveShotsTail(dt, i);
        continue;
      }
      let hitIdx = -1;
      if (grid) {
        // ⚠ **`separate()` 가 세운 격자를 그대로 읽는다** — 원점도 같아야 한다.
        //   벽 제거(2026-08-02)로 원점이 다윗을 따라다니므로 여기도 같이 옮긴다.
        //   한쪽만 고치면 **돌이 엉뚱한 칸을 뒤져 아무도 안 맞는다**(조용한 고장).
        const gx = clamp(((s.x - this._gridOx) / C) | 0, 0, cols - 1);
        const gy = clamp(((s.y - this._gridOy) / C) | 0, 0, rows - 1);
        outer:
        for (let ox = -1; ox <= 1 && hitIdx < 0; ox++) for (let oy = -1; oy <= 1; oy++) {
          const cxg = gx + ox, cyg = gy + oy;
          if (cxg < 0 || cyg < 0 || cxg >= cols || cyg >= rows) continue;
          for (let j = head[cyg * cols + cxg]; j !== -1; j = next[j]) {
            const e = this.enemies[j];
            if (!e) continue;
            if (s.hit && s.hit.includes(e)) continue;   // 관통 — 같은 몸을 두 번 안 뚫는다
            const dx = s.x - e.x, dy = s.y - e.y, lim = e.r + 4 + (s.pad ?? 0);
            if (dx * dx + dy * dy < lim * lim) { hitIdx = j; break outer; }
          }
        }
      } else {
        for (let j = n - 1; j >= 0; j--) {
          const e = this.enemies[j];
          if (s.hit && s.hit.includes(e)) continue;
          const dx = s.x - e.x, dy = s.y - e.y, lim = e.r + 4 + (s.pad ?? 0);
          if (dx * dx + dy * dy < lim * lim) { hitIdx = j; break; }
        }
      }
      if (hitIdx < 0) {
        // ⚠⚠ **자국인데 아무것도 없었다 — 그래도 자국은 소비된다.**
        //   물수제비는 물을 때리는 것이고 그 자리에 짐승이 있으면 맞는 것이다.
        //   여기서 소비하지 않으면 튐 수가 예산이 아니라 **명중 횟수**가 되어
        //   등급 축이 통째로 죽는다(`markSkip` 머리 주석의 실측).
        if (mark) this.markSkip(s, false);
        this.finishShotRange(s, i, expired);
        continue;
      }
      const e = this.enemies[hitIdx];
      const alive0 = e.hp > 0, len0 = this.enemies.length;
      this.damageEnemy(e, this.shotDamage(s, e.kind), s);
      this.spawnShards(s, e.x, e.y, e);
      this.landSplash(s, e.x, e.y);
      this.m.shotHits++;
      this.tallyShotHit(s);
      // ⚠ **튐이 관통보다 먼저다.** 납작한 돌의 정체가 튐이고, `pierce` 는 모든 돌이
      //   갖는 기본 광역 바닥일 뿐이다. 튐이 있는 발에서는 바닥이 축을 가리면 안 된다.
      //   ⚠ 첫 명중이 물수제비를 **시작**시킨다(`markSkip` 이 `wet` 을 켠다) — 그래서
      //     조준한 자리를 맞히고, 그 자리에서 자국이 앞으로 뻗는다.
      if (this.markSkip(s, true)) { /* 물 위를 미끄러져 다음 자국으로 */ }
      // 「매끈한 돌」 — **감쇠 없이** 다음 체로 지나간다(명세 575행).
      else if (s.pierce > 0) { s.pierce--; s.hits = (s.hits ?? 0) + 1; (s.hit ??= []).push(e); }
      else if (this.tryRicochet(s, e)) { s.hits = (s.hits ?? 0) + 1; }
      else this.killShot(i);
      // 착탄 광역이 이웃을 죽였으면 **격자 인덱스가 이미 무효**다 — 바로 꼬리로 넘긴다
      // (구 주석은 여기를 「오버킬 전파」라 불렀는데 그 기제는 지워졌다. 남은 것은
      //  `landSplash` 이며, 그쪽도 `killEnemy` 로 배열을 splice 하므로 검사는 그대로 필요하다.)
      if (this.enemies.length !== len0) {
        this.reapEnemy(e);
        this.finishShotRange(s, i, expired);
        return this.moveShotsTail(dt, i);
      }
      // **격자 인덱스는 죽이는 순간 무효가 된다**(splice 가 뒤를 당긴다). 그래서 이 프레임의
      // 남은 발들은 격자를 못 믿는다 — 죽인 뒤에는 그 프레임 동안 격자를 끈다.
      if (e.hp <= 0) {
        this.reapEnemy(e);
        this.finishShotRange(s, i, expired);
        return this.moveShotsTail(dt, i);
      }
      this.finishShotRange(s, i, expired);
    }
  }

  // 진화 조건 계상 — **「2체 이상」 분만 센다**(v2 D3). 1체만 맞은 발은 안 세어진다.
  //   결① = 한 발이 2체 이상을 **관통**한 횟수
  //   결② = 한 **발사(volley)**의 투사체 중 2발 이상이 맞은 횟수
  tallyShotHit(s) {
    if (s.shard) return;
    if (s.vid == null) return;
    const v = (this._vhits ??= new Map());
    const n = (v.get(s.vid) ?? 0) + 1;
    v.set(s.vid, n);
    if (v.size > 64) { const k = v.keys().next().value; v.delete(k); }
  }
  // 「바위를 던진다」 — 발사가 1발이 되고 **착탄점에서 광역 충격**
  landSplash(s, x, y) {
    if (!s.splash || s.shard) return;
    const baseDmg = s.dmg ?? PASU.dmg;
    this.explodeAt(x, y, s.splash, baseDmg * 0.5);
    // ── 굳은 세트 「터진 자리가 또 터진다」 ──────────────────────
    // **그 자리가 그대로 다시 터진다.** 새 개념이 아니라 같은 폭발을 한 번 더 예약할
    // 뿐이며, 세트가 안 걸리면 `again` 이 0 이라 여기 아무 일도 안 일어난다.
    if (s.again > 0) {
      this.blasts.push({ x, y, r: s.splash, dmg: baseDmg * 0.5, baseDmg,
                         at: this.t + SETFX.againSec, left: s.again - 1,
                         crownDmg: s.crownDmg ?? 0, crownR: s.crownR ?? 1 });
    }
  }
  // 한 자리를 터뜨린다. `landSplash` 와 굳은 세트의 재폭발이 **같은 몸을 쓴다** —
  // 둘이 갈리면 한쪽만 고쳐지는 사고가 난다.
  // ⚠ `feel` = 손맛(히트스톱)을 쌓는가. **첫 착탄만 true** 다 — 이유는 `stepBlasts` 주석.
  explodeAt(x, y, rU, dmg, feel = true) {
    const R2 = (rU * UNIT) ** 2;
    this._landAt = this.t; this._landXY = { x, y, r: rU * UNIT };   // 렌더용
    this._landFeel = !!feel;
    this._landStage = feel ? 0 : (this._landStage ?? 0) + 1;
    this._landSeq = (this._landSeq ?? 0) + 1;
    this.decals.push({ x, y, r: rU * UNIT * 0.8, t: this.t });
    if (this.decals.length > 40) this.decals.shift();
    // **큰 돌이 떨어지면 화면이 멈칫하고 흔들린다** — 착탄 광역의 무게가 손에 온다
    this.present(this.mods.tier >= 6 && this.mods.stoneId === 'mugeo' ? 'diamond' : 'normal', {
      hitstop: feel ? HIT.land : 0,
      shake: SHAKE.shock * Math.min(1.4, 0.6 + rU * 0.25),
      power: feel ? 1 : 0.58,
      dx: x - this.player.x, dy: y - this.player.y,
    });
    let areaHits = 0;
    for (let i = this.eagles.length - 1; i >= 0; i--) {
      const e = this.eagles[i];
      if (e.state !== 'grounded' || e.hp <= 0) continue;
      const dx = e.x - x, dy = e.y - y;
      if (dx * dx + dy * dy > R2) continue;
      areaHits++;
      this.damageEagle(e, dmg, { x, y });
    }
    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const o = this.enemies[i];
      if (!o) continue;
      const dx = o.x - x, dy = o.y - y;
      if (dx * dx + dy * dy > R2) continue;
      areaHits++;
      const kd = Math.hypot(dx, dy);
      if (kd > 0.01) { o.hitDx = dx / kd; o.hitDy = dy / kd; }
      this.enemyBehavior?.recordDamageTaken(o, Math.min(dmg, Math.max(0, o.hp)));
      o.hp -= dmg;
      // ⚠ **`feel` 이 죽음까지 따라간다.**   【검증 2026-07-30 — 절반만 고쳐져 있었다】
      //   `feel=false` 는 `HIT.land` 만 막았고 `killEnemy` 의 `HIT.elite`(정예가 쓰러질 때
      //   0.09) 경로는 그대로 열려 있었다. 그래서 굳은 세트가 **여전히 순손해**였다:
      //     격리 자·N30·720초·3시드 — off 13.37 → on 13.21 킬/초 = **×0.988**(3/3 손해)
      //     히트스톱 프레임 14.8% → 15.8% · 흐른 월드시간 632 → 627초(−0.8%)
      //   히트스톱은 **상한제**(HIT.max 0.14)이므로 죽음을 0.32초 간격으로 흩으면
      //   뭉쳐 죽일 때보다 **총 감속 시간이 늘어난다** — 킬 감소분과 크기가 같다.
      if (o.hp <= 0) this.killEnemy(i, 'player', feel);
    }
    if (areaHits > 0) this.m.areaHits = (this.m.areaHits ?? 0) + areaHits;
  }
  // 격자가 무효해진 뒤의 잔여 발 — 안전하게 선형으로 처리한다(프레임당 많아야 몇 발이다).
  moveShotsTail(dt, from) {
    for (let i = Math.min(from - 1, this.shots.length - 1); i >= 0; i--) {
      const s = this.shots[i];
      const elapsed = this.advanceShot(s, dt);
      const expired = s.life <= 0;
      // 물수제비 — **`moveShots` 와 같은 몸을 쓴다**(skipPhase/skipOut/markSkip).
      //   갈라 쓰면 한쪽만 고쳐지는 사고가 난다 — 실제로 그 사고가 이미 한 번 났다.
      const ph = this.skipPhase(s, elapsed);
      if (ph === 'glide') {
        if (this.skipOut(s)) this.killShot(i);
        this.finishShotRange(s, i, expired);
        continue;
      }
      const mark = ph === 'mark';
      if (this.hitBossWithShot(s, i)) {
        this.finishShotRange(s, i, expired);
        continue;
      }
      if (this.hitEagleWithShot(s, i, false)) {
        this.finishShotRange(s, i, expired);
        continue;
      }
      let struck = false;
      for (let j = this.enemies.length - 1; j >= 0; j--) {
        const e = this.enemies[j];
        if (s.hit && s.hit.includes(e)) continue;
        const dx = s.x - e.x, dy = s.y - e.y, lim = e.r + 4 + (s.pad ?? 0);
        if (dx * dx + dy * dy < lim * lim) {
          const alive0 = e.hp > 0;
          struck = true;
          this.damageEnemy(e, this.shotDamage(s, e.kind), s);
          this.spawnShards(s, e.x, e.y, e);
          this.landSplash(s, e.x, e.y);
          this.m.shotHits++;
          this.tallyShotHit(s);
          if (this.markSkip(s, true)) { /* 계속 난다 */ }
          else if (s.pierce > 0) { s.pierce--; s.hits = (s.hits ?? 0) + 1; (s.hit ??= []).push(e); }
          else if (this.tryRicochet(s, e)) { s.hits = (s.hits ?? 0) + 1; }
          else this.killShot(i);
          this.reapEnemy(e);
          break;
        }
      }
      // 자국인데 아무것도 없었다 — 그래도 자국은 소비된다(위 `markSkip` 주석)
      if (mark && !struck) this.markSkip(s, false);
      this.finishShotRange(s, i, expired);
    }
  }

  // 발 하나의 피해. **파편은 맞은 것의 종류로 값이 갈린다**(명세 575행) —
  // 물량층에는 고정 10(= 1발), 정예·위협에는 발사체 뎀의 30%.
  shotDamage(s, kind) {
    // **파편은 그 돌의 조각이다** — 약한 돌은 약한 조각(mods.SHARD 주석)
    if (s.shard) return shardDamage(kind, s.src);
    // 「가르는 돌」 — 관통할 때마다 다음 표적이 더 아프다. *줄이 깊을수록 뒤가 더 아프다.*
    let d = (s.dmg ?? PASU.dmg) * (1 + (s.ramp ?? 0) * (s.hits ?? 0));
    const h = s.hits ?? 0;
    // ── 부가 줄(●●●○○부터) — **여덟이 전부 미구현이었다** ────────
    if (s.first && h === 0) d *= 1 + s.first;            // 첫 발이 세다
    if (s.rampH) d *= 1 + s.rampH * h;                   // 연달아 맞으면 는다
    if (s.eDmg && kind !== 'swarm') d *= 1 + s.eDmg;     // 큰 놈에게 더 아프다
    if (s.fDmg && s.ox !== undefined) {                  // 멀리서 더 아프다
      const dx = s.x - s.ox, dy = s.y - s.oy;
      const far = Math.min(1, Math.hypot(dx, dy) / (s.maxRange || this._ws?.range || PASU.range));
      d *= 1 + s.fDmg * far;
    }
    return d;
  }

  // ── 「맞고 튄다」 — 맞은 뒤 **가까운 다른 적으로 방향을 튼다** ────
  // 투사체가 하나 더 생기는 것과 같아서 **화면 밀도가 실제로 는다.**
  tryRicochet(s, from) {
    if (!(s.ric > 0) || s.riced) return false;
    let best = null, bd = (7 * UNIT) ** 2;
    for (const e of this.enemies) {
      // 관통으로 이미 맞힌 몸은 충돌부도 다시 안 맞힌다. 방향 선택에서만 후보로 두면
      // 그 몸 쪽으로 되돌아간 뒤 아무것도 못 치고 단 한 번의 도탄을 허공에 소비한다.
      if (e === from || e.hp <= 0 || (s.hit && s.hit.includes(e))) continue;
      const dx = e.x - s.x, dy = e.y - s.y, d2 = dx * dx + dy * dy;
      if (d2 < bd) { bd = d2; best = e; }
    }
    if (!best) return false;
    const dx = best.x - s.x, dy = best.y - s.y, d = Math.hypot(dx, dy) || 1;
    const v = Math.hypot(s.vx, s.vy) || SHOT_SPEED;
    s.vx = (dx / d) * v; s.vy = (dy / d) * v;
    // 렌더 궤적이 반사점을 실제 꼭짓점으로 보존한다. 판정에는 쓰지 않는다.
    s._trailTurn = (s._trailTurn ?? 0) + 1;
    s._trailTurnX = s.x; s._trailTurnY = s.y;
    // 방향을 튼 다음 프레임에도 아직 원래 몸 안에 있을 수 있다. 원래 몸을 제외하지
    // 않으면 즉시 다시 맞고 `riced` 문에 막혀 연장 수명을 한 칸도 못 쓴다. 앞서 관통한
    // 몸의 제외 목록도 보존해, 방향을 틀었다고 같은 몸을 두 번 때리지는 않는다.
    const hit = s.hit ? [...s.hit] : [];
    if (!hit.includes(from)) hit.push(from);
    s.riced = 1; s.life = Math.max(s.life, 0.5); s.hit = hit;
    this.m.ricochets = (this.m.ricochets ?? 0) + 1;
    return true;
  }

  // ── 「빗나가면 굴러간다」 — 수명이 다한 돌이 **바닥을 구른다** ────
  // 10-B 2-1 「초반 헛스윙 90%」의 물맷돌판 처방이다. 빗나간 것이 그냥 안 사라진다.
  tryRoll(s) {
    if (!(s.roll > 0) || s.rolled) return false;
    s.rolled = 1; s.life = 0.75;
    s.vx *= 0.45; s.vy *= 0.45;      // 굴러가므로 느리다
    s.dmg = (s.dmg ?? PASU.dmg) * 0.6;
    this.m.rolls = (this.m.rolls ?? 0) + 1;
    return true;
  }

  // ── 스윙 간격 ────────────────────────────────────────────────
  // 「무쇠 고리」의 +15%는 벌점이므로 상한(0.25초)을 다시 풀지 않는다. 반대로 결②와
  // 「헛손질을 아낀다」는 상한을 **뚫으면 안 된다** — 명세 7장의 하드 상한이다.
  // ── 타겟 우선순위 ────────────────────────────────────────────
  // **낮 3축이 여는 것이다**(물맷돌축 2·4·6단계). 구 구현은 `elitePriority`
  // 불리언 하나뿐이라 「먼 적」·「밀집」을 살 수는 있는데 **밤에 들어갈 자리가
  // 없었다** — 4단계와 캡스톤의 절반이 아무 일도 안 했다.
  //
  //   nearest 가장 가까운 것        (기본)
  //   elite   정예·위협 우선        (물맷돌축 2단계)
  //   far     사거리 안 **가장 먼** 것 (물맷돌축 4단계)
  //   dense   주변에 제일 붐비는 곳  (캡스톤 「주머니의 것」)
  //
  // ⚠ far·dense 의 **점수식은 정본 미기재**다. 정본이 정한 것은 이름뿐이고,
  //   「먼 적을 어떻게 고르는가」는 어느 문서에도 없다. 아래는 **잠정**이며
  //   여기 한 곳만 고치면 된다.
  //     far   — 사거리 안에서 거리를 뒤집는다(밖은 애초에 후보가 아니다)
  //     dense — 이웃 수로 감점한다. 반경 DENSE_R 안의 적 수만큼 우선순위가 오른다
  _heavySectorOf(dx, dy) {
    const ax = Math.abs(dx), ay = Math.abs(dy);
    if (ay <= ax * OCTANT_TAN) return dx >= 0 ? 0 : 4; // 동 / 서
    if (ax <= ay * OCTANT_TAN) return dy >= 0 ? 2 : 6; // 남 / 북 (화면 y는 아래가 +)
    if (dx >= 0) return dy >= 0 ? 1 : 7;               // 남동 / 북동
    return dy >= 0 ? 3 : 5;                            // 남서 / 북서
  }

  pickHeavySectorTarget(p, RG) {
    const threat = this.pickThreatTarget(p, RG);
    if (threat) return threat;
    // count | sumX | sumY | sumD2 — 한 덩어리 typed array를 World 수명 동안 재사용한다.
    const bins = this._heavyAimBins ??= new Float64Array(32);
    bins.fill(0);
    const R2 = RG * RG;
    for (const e of this.enemies) {
      if (!e || e.hp <= 0) continue;
      const dx = e.x - p.x, dy = e.y - p.y, d2 = dx * dx + dy * dy;
      if (d2 > R2) continue;
      const s = this._heavySectorOf(dx, dy);
      bins[s]++;
      bins[8 + s] += e.x;
      bins[16 + s] += e.y;
      bins[24 + s] += d2;
    }
    let best = -1, bestN = 0, bestD2 = Infinity;
    for (let s = 0; s < 8; s++) {
      const count = bins[s], sumD2 = bins[24 + s];
      if (count > bestN || (count === bestN && count > 0 && sumD2 < bestD2)) {
        best = s; bestN = count; bestD2 = sumD2;
      }
    }
    if (best < 0) return null;
    const target = this._heavyAimTarget ??= { x: 0, y: 0, sector: 0, count: 0 };
    target.x = bins[8 + best] / bestN;
    target.y = bins[16 + best] / bestN;
    target.sector = best;
    target.count = bestN;
    return target;
  }

  pickTarget(p, RG) {
    const threat = this.pickThreatTarget(p, RG);
    if (threat) return threat;
    const mode = this.priority;
    let tgt = null, bs = Infinity;
    if (mode === 'dense') {
      // ⚠ **한 프레임에 한 번만 센다.** 마리별로 이웃을 세면 O(n²)이고,
      //   물량 밤은 동시 230마리라 발사마다 5만 번을 돈다 — 프레임이 죽는다.
      //   대신 칸(DENSE.r)마다 마릿수를 한 번 세고(O(n)) 그 값을 밀집도로 쓴다.
      const dens = this.denseGrid();
      for (const e of this.enemies) {
        const d = dist(p, e);
        if (d > RG) continue;
        const sc = d - (dens.get(this._dcell(e.x, e.y)) ?? 1) * DENSE.w;
        if (sc < bs) { bs = sc; tgt = e; }
      }
      return tgt;
    }
    for (const e of this.enemies) {
      const d = dist(p, e);
      if (d > RG) continue;
      const sc = mode === 'far'   ? -d
               : mode === 'elite' ? d + (e.kind !== 'swarm' ? -1200 : 0)
               : d;
      if (sc < bs) { bs = sc; tgt = e; }
    }
    return tgt;
  }
  pickThreatTarget(p, RG) {
    let best = null, bd = RG * RG;
    if (this.boss && !this.boss.dead) {
      const dx = this.boss.x - p.x, dy = this.boss.y - p.y, d2 = dx * dx + dy * dy;
      if (d2 <= bd) { bd = d2; best = this.boss; }
    }
    for (const e of this.eagles) {
      if (!e.targetable || e.hp <= 0) continue;
      const dx = e.x - p.x, dy = e.y - p.y, d2 = dx * dx + dy * dy;
      if (d2 <= RG * RG && (!best || d2 < bd)) { bd = d2; best = e; }
    }
    return best;
  }
  _dcell(x, y) { return ((y / DENSE.r) | 0) * 4096 + ((x / DENSE.r) | 0); }
  // 프레임당 1회 갱신. `this.t` 로 캐시한다 — 한 프레임에 여러 발이 나가도 한 번만 센다.
  denseGrid() {
    if (this._densAt === this.t && this._dens) return this._dens;
    const g = this._dens ?? (this._dens = new Map());
    g.clear();
    for (const e of this.enemies) {
      const c = this._dcell(e.x, e.y);
      g.set(c, (g.get(c) ?? 0) + 1);
    }
    this._densAt = this.t;
    return g;
  }

  // 【2026-07-31】 swingInterval(지팡이 스윙 간격 — crowdRate·whiffCut)이 여기 있었다 — 삭제.

  // ── ⚠ 구 「오버킬 이월」 절의 머리 주석이었다 — **기제와 함께 지웠다**(충돌 2) ──
  //   그 주석은 *"넘친 피해를 이웃으로 흘리면 뎀 카드가 물량층에 의미를 갖는다"* 를
  //   설명하고 있었는데, 그 기제가 사라졌으므로 설명도 같이 지운다(규율 5).
  //   지운 이유·남긴 것의 정본은 아래 `damageEnemy` 안쪽 주석 한 곳이다.
  //   ⚠ **물량층 HP 10 · 기본 뎀 10 이라 넘친 피해가 버려진다**는 사실 자체는 남는다 —
  //     이제 그 낭비를 메우는 것은 이월이 아니라 **여섯 축**(반경·방사·수·선·거리·시간)이다.
  // 뜨는 피해 숫자. **물량층에도 띄운다**(사장님 지시 — 구 구현은 정예 이상만이었다).
  //   동시 270마리에서 초당 수십 마리가 죽으므로 **상한이 유일한 방어**다. 상한을 넘으면
  //   오래된 것부터 밀어낸다 — 최신 타격이 항상 보인다.
  //   층은 **크기와 밝기**로 가른다(scene.drawDamageText) — 숫자가 다 같은 무게로 뜨면
  //   물량층 숫자가 정예 숫자를 묻어 버린다.
  popDamage(x, y, dmg, kind) {
    if (!DMGTEXT.on) return;
    this.dmgTexts.push({ x, y, t: this.t, v: Math.round(dmg), kind,
                         big: kind === 'boss', small: kind === 'swarm' });
    if (this.dmgTexts.length > DMGTEXT.cap) this.dmgTexts.shift();
  }

  damageEnemy(e, dmg, from) {
    // **맞은 적이 번쩍인다** — 이것이 없어서 「때렸는지」가 화면에 안 나왔다.
    //   플레이어 피격은 3프레임 모션이 있는데 적 쪽은 통째로 비어 있었다.
    //   뱀서류에서 초당 수십 번 일어나는 이 번쩍임이 화면 소음의 몸통이다.
    e.hitT = this.t;
    let hitDx = 0, hitDy = 0;
    if (from && typeof from === 'object') {
      if (Number.isFinite(from.vx) && Number.isFinite(from.vy)
          && Math.hypot(from.vx, from.vy) > 0.01) {
        hitDx = from.vx; hitDy = from.vy;
      } else if (Number.isFinite(from.x) && Number.isFinite(from.y)) {
        hitDx = e.x - from.x; hitDy = e.y - from.y;
      }
    }
    if (Math.hypot(hitDx, hitDy) <= 0.01) {
      hitDx = e.x - this.player.x; hitDy = e.y - this.player.y;
    }
    const hitD = Math.hypot(hitDx, hitDy) || 1;
    e.hitDx = hitDx / hitD; e.hitDy = hitDy / hitD;
    this.popDamage(e.x, e.y, dmg, e.kind);
    const applied = Math.min(Math.max(0, Number(dmg) || 0), Math.max(0, e.hp));
    this.enemyBehavior?.recordDamageTaken(e, applied);
    const over = dmg - e.hp;
    e.hp -= dmg;
    // ── ⚠⚠ 「오버킬 이월」 연쇄를 **지웠다** (2026-07-30 · 충돌 2) ──────────
    //   사장님 승인: *"CARRY 상수 + world 의 오버킬 체인 — 깨끗이 지운다."*
    //   차돌의 `carry: true` 가 사라지면서 이 기제는 **어느 부품에도 속하지 않는
    //   전 무기 기본 규칙**만으로 남아 있었고, 그 상태가 정확히 「배선만 있고
    //   주인이 없는 값」이다(규율 5가 이 저장소의 상습 결함이라 부른 자리).
    //   같이 지운 것: `mods.overkill` · `mods.spread` · `gear.CARRY` ·
    //                `EFFECTS.k_heavy/k_press` 의 spread 항.
    //   【2026-07-31】 그때 남겼던 계량(`evoProg.overkill`)도 이제 지웠다 —
    //     그 수를 읽던 진화 자체가 카드 체계와 함께 삭제됐다(지팡이 삭제).
  }
  damageEagle(e, dmg, from) {
    if (!e || !e.targetable || e.hp <= 0) return 0;
    const applied = dmg * (e.state === 'grounded' ? EAGLE.groundDamageMul : EAGLE.airDamageMul);
    e.hitT = this.t;
    let dx = e.x - this.player.x, dy = e.y - this.player.y;
    if (from && typeof from === 'object') {
      if (Number.isFinite(from.vx) && Number.isFinite(from.vy)
          && Math.hypot(from.vx, from.vy) > 0.01) {
        dx = from.vx; dy = from.vy;
      } else if (Number.isFinite(from.x) && Number.isFinite(from.y)) {
        dx = e.x - from.x; dy = e.y - from.y;
      }
    }
    const d = Math.hypot(dx, dy) || 1;
    e.hitDx = dx / d; e.hitDy = dy / d;
    this.popDamage(e.x, e.y, applied, 'threat');
    this.enemyBehavior?.recordDamageTaken(e, Math.min(applied, Math.max(0, e.hp)));
    e.hp -= applied;
    this.m.eagleDamageEvents++;
    if (e.hp <= 0) this.killEagle(e);
    return applied;
  }
  killEagle(e) {
    const i = this.eagles.indexOf(e);
    if (i < 0) return false;
    this.enemyBehavior?.recordKill(e, this.t);
    this.eagles.splice(i, 1);
    this.m.killsPlayer++;
    this.m.eaglesKilled++;
    if (this.t - this._comboT > HIT.comboWin) this._comboN = 0;
    this._comboT = this.t; this._comboN++;
    this.m.comboMax = Math.max(this.m.comboMax ?? 0, this._comboN);
    this.pops.push({ x: e.x, y: e.y, t: this.t, big: true, kind: 'threat', r: e.r,
                     n: this._comboN, dx: e.hitDx, dy: e.hitDy });
    if (this.pops.length > 96) this.pops.shift();
    this.present('elite', { hitstop: HIT.elite, shake: SHAKE.shock * 0.7, power: 1,
                            dx: e.x - this.player.x, dy: e.y - this.player.y });
    this.dropPiece(e.x, e.y, EAGLE.drop * (1 + (this.mods.traits?.pieceBonus ?? 0)));
    return true;
  }
  // 죽었으면 치운다. damageEnemy 가 배열을 건드릴 수 있어 **인덱스를 안 믿는다.**
  reapEnemy(e) {
    if (e.hp > 0) return;
    const j = this.enemies.indexOf(e);
    if (j >= 0) {
      // 「부딪혀 갈라진다」 — 정예·위협을 **처치**하면 그 자리에서 파편이 사방으로.
      // *큰 것을 고르는 것이 곧 광역이 된다.*
      if (this.mods.eliteBurst > 0 && e.kind !== 'swarm') this.burstAt(e.x, e.y, this.iPart(this.mods.eliteBurst));
      this.killEnemy(j, 'player');
    }
  }
  burstAt(x, y, k) {
    for (let i = 0; i < k; i++) {
      const a = (i / Math.max(1, k)) * Math.PI * 2;
      this.shots.push({ x, y, vx: Math.cos(a) * SHARD.speed, vy: Math.sin(a) * SHARD.speed,
                        life: SHARD.life, shard: true, shardGen: 1,
                        diamond: this.mods.tier >= 6 && this.mods.stoneId === 'monan',
                        stoneId: this.mods.stoneId,
                        pierce: 0, frag: 0, src: PASU.dmg });
      this.m.shards = (this.m.shards ?? 0) + 1;
    }
  }
  // 분수 중첩 → 정수 개수. 소수부만큼의 확률로 하나 더. 밤 전체로 보면 기대값이 맞는다.
  iPart(v) { const f = Math.floor(v); return f + (this.rnd() < v - f ? 1 : 0); }
  // 「깨지는 돌」 — 명중점에서 방사형. **파편은 다시 안 쪼개지고 관통도 안 한다**
  // (안 그러면 한 발이 화면을 채운다 — 정본에 연쇄를 허용하는 문장이 없다).
  // ── 길든 세트 「조각이 다시 조각을 낸다」만 그 금지의 예외다 ──────────
  // ⚠⚠ **한 조각이 내는 자식은 하나뿐이다.** 조각 수만큼 또 내면 13.6 → 185 → 2,500
  //   으로 터진다 — 위 괄호가 금지한 바로 그 자리다. 자식 하나 × 대(代) 수이므로
  //   총량이 **선형**으로만 는다(●●●●○ 2배 · ●●●●● 3배).
  spawnShards(s, x, y, sourceTarget = null) {
    const k = s.frag | 0;
    if (k <= 0) return;
    const a0 = Math.atan2(s.vy, s.vx);
    const reach = Math.min(
      SHARD.heavyReachCap,
      1 + Math.max(0, s?.splash ?? 0) * SHARD.heavyReachPerSplash,
    );
    const reachAxis = Math.sqrt(reach);
    const speed = SHARD.speed * reachAxis;
    const life = SHARD.life * reachAxis;
    const chain = Math.max(0, s.chain ?? 0);
    const generation = Math.max(1, (s.shardGen ?? 0) + 1);
    // 최초 피격체 제외는 모난 돌의 방사 회생 계약이다. 공용 파편을 쓰는 다른 돌까지
    // 같은 대상을 못 때리게 하면 보스·단일 대상 화력을 범위 밖에서 깎게 된다.
    const shardSource = s.stoneId === 'monan' ? sourceTarget : null;
    // 거친 ◆ — 기존 2대 연쇄의 **마지막 자식**만 남은 비행거리 안의 적을 본다.
    // 조각 수·세대·피해는 전혀 늘리지 않고 마지막 한 조각의 방향만 바꾼다.
    const finalSeek = !!s.finalSeek && chain <= 0;
    let seekTarget = null, seekD2 = (speed * life) ** 2;
    if (finalSeek) {
      for (const e of this.enemies) {
        if (!e || e.hp <= 0 || e === shardSource) continue;
        const dx = e.x - x, dy = e.y - y, d2 = dx * dx + dy * dy;
        if (d2 >= seekD2) continue;
        seekD2 = d2; seekTarget = e;
      }
    }
    this._shardAt = this.t;
    this._shardXY = { x, y };
    this._shardCount = k;
    this._shardGeneration = generation;
    this._shardSeq = (this._shardSeq ?? 0) + 1;
    const shardEvent = this._shardSeq;
    if (seekTarget) {
      this.m.shardSeeks++;
      this._shardSeekAt = this.t; this._shardSeekXY = { x, y };
      this._shardSeekSeq = (this._shardSeekSeq ?? 0) + 1;
    }
    for (let i = 0; i < k; i++) {
      const a = seekTarget
        ? Math.atan2(seekTarget.y - y, seekTarget.x - x)
        : a0 + (i / k) * Math.PI * 2;
      this.shots.push({ x, y, vx: Math.cos(a) * speed, vy: Math.sin(a) * speed,
                        life, shard: true, shardGen: generation,
                        shardEvent,
                        diamond: !!s.diamond, stoneId: s.stoneId,
                        // 모난 돌의 첫 파편만 한 몸을 더 지난다. 최초 피격체 제외로
                        // 잃은 마무리 화력을 방사 정체 안에서 돌려주되, 2·3세대까지
                        // 관통시키면 연쇄 총량이 곱으로 번지므로 그쪽은 0을 지킨다.
                        pierce: s.stoneId === 'monan' && generation === 1 ? 1 : 0,
                        // 파편은 맞고 갈라진 몸 안에서 태어난다. 그 몸을 제외하지 않으면
                        // 다음 프레임에 사방으로 퍼지기 전에 같은 적부터 다시 때린다.
                        // 형제 파편끼리 hit 배열을 공유하면 한 파편의 이후 명중이 전부에
                        // 전파되므로, 각 파편이 자기 배열을 따로 가져야 한다.
                        hit: shardSource ? [shardSource] : undefined,
                        frag: chain > 0 ? 1 : 0, chain: chain - 1,
                        finalSeek: !!s.finalSeek,
                        src: s?.dmg });
      this.m.shards = (this.m.shards ?? 0) + 1;
    }
  }

  // ⚠ **올무가 여기 있었다** 【폐기 2026-08-07 — 사장님: *"올무는 죽은 개념이야.
  //   만약 아니더라도 이제 안쓸거야"*】. 배치(`placeSnares`)와 판정(`stepSnares`)이
  //   여기 있었다 — 1회 30뎀 · 밤 시작 자동 배치. 낮의 수량·소이벤트 「낡은 올무」·
  //   밤 훅(`mods.snareAdd`)·스프라이트까지 같이 걷었다. **반쯤 남기지 않는다** —
  //   되살릴 일이 생기면 이 주석과 git 이력이 설계를 들고 있다.

  // ── 「기름을 붓는다」 화염 지대 ──────────────────────────────────
  // 정본은 효과뿐이다(반경 내 초당 5 · 합산 상한 12). **배치는 정본에 없다** —
  // 입력이 이동 하나뿐이므로 "미리 깔아 둔다"가 성립하는 유일한 읽기가 자취다.
  // 근거는 mods.js FIRE 주석에 적어 두었다.
  stepFire(dt) {
    if (this.mods.fire <= 0) return;
    const p = this.player;
    const iv = FIRE.interval * Math.pow(FIRE.intervalPerStack, this.mods.fire - 1);
    this._fireT = (this._fireT ?? 0) + dt;
    if (this._fireT >= iv) {
      this._fireT -= iv;
      this.fires.push({ x: p.x, y: p.y, life: FIRE.life });
      if (this.fires.length > FIRE.maxZones) this.fires.shift();
    }
    for (let i = this.fires.length - 1; i >= 0; i--) {
      if ((this.fires[i].life -= dt) <= 0) this.fires.splice(i, 1);
    }
    if (!this.fires.length) return;
    // **합산 상한 12** — 겹친 자리에서 지대 수만큼 곱해지지 않는다. 몇 겹인지 세고 자른다.
    for (let j = this.eagles.length - 1; j >= 0; j--) {
      const e = this.eagles[j];
      if (e.state !== 'grounded' || e.hp <= 0) continue;
      let layers = 0;
      for (const f of this.fires) {
        const fr = (f.r ?? FIRE.radius) + e.r;
        const dx = e.x - f.x, dy = e.y - f.y;
        if (dx * dx + dy * dy <= fr * fr) layers++;
      }
      if (layers) this.damageEagle(e, Math.min(FIRE.dps * layers, FIRE.capDps) * dt, 'fire');
    }
    for (let j = this.enemies.length - 1; j >= 0; j--) {
      const e = this.enemies[j];
      if (!e) continue;
      let layers = 0;
      for (const f of this.fires) {
        const fr = f.r ?? FIRE.radius;
        const dx = e.x - f.x, dy = e.y - f.y;
        if (dx * dx + dy * dy <= fr * fr) layers++;
      }
      if (!layers) continue;
      const fireDamage = Math.min(FIRE.dps * layers, FIRE.capDps) * dt;
      this.enemyBehavior?.recordDamageTaken(e, Math.min(fireDamage, Math.max(0, e.hp)));
      e.hp -= fireDamage;
      if (e.hp <= 0) this.killEnemy(j, 'player');
    }
    if (this.boss && !this.boss.dead) {
      let layers = 0;
      for (const f of this.fires) {
        const fr = (f.r ?? FIRE.radius) + this.boss.cfg.r;
        const dx = this.boss.x - f.x, dy = this.boss.y - f.y;
        if (dx * dx + dy * dy <= fr * fr) layers++;
      }
      if (layers) this.hitBoss(Math.min(FIRE.dps * layers, FIRE.capDps) * dt);
    }
  }

  // ⚠ `feel` = 이 죽음이 **손맛(히트스톱)을 쌓는가.** 기본은 true 이고 **재폭발만
  //   false** 다(`explodeAt` 이 넘긴다). 이유는 `stepBlasts`·`explodeAt` 주석에 있다 —
  //   히트스톱이 상한제라 죽음을 흩으면 총 감속이 늘어 세트가 순손해가 된다.
  //   흔들림(`shake`)은 `Math.max` 라 누적되지 않으므로 그대로 둔다.
  killEnemy(idx, by, feel = true) {
    const e = this.enemies[idx];
    if (!e) return false;
    this.enemyBehavior?.recordKill(e, this.t);
    this.enemies.splice(idx, 1);
    if (by === 'player') this.m.killsPlayer++; else this.m.killsDog++;
    // ── 연쇄 처치 — **짧은 창 안의 죽음을 한 덩어리로 센다** ──────
    // 다섯이 동시에 죽어도 각각 따로 처리되면 겹치는 쾌감이 없다.
    if (this.t - this._comboT > HIT.comboWin) this._comboN = 0;
    this._comboT = this.t; this._comboN++;
    this.m.comboMax = Math.max(this.m.comboMax ?? 0, this._comboN);
    // 처치 표식 — **적이 그냥 사라지던 자리.** 연쇄가 클수록 크게 번쩍인다.
    this.pops.push({
      x: e.x, y: e.y, t: this.t,
      big: e.kind !== 'swarm', kind: e.kind, r: e.r, n: this._comboN,
      dx: e.hitDx, dy: e.hitDy,
    });
    if (this.pops.length > 96) this.pops.shift();
    // **정예·위협은 죽을 때 시간이 멈칫한다** — 큰 것이 쓰러지는 것은 사건이다
    if (e.kind !== 'swarm') {
      this.present('elite', {
        hitstop: feel ? HIT.elite : 0, shake: SHAKE.shock * 0.7, power: 1,
        dx: e.x - this.player.x, dy: e.y - this.player.y,
      });
    }
    // ── 피 얼룩 ────────────────────────────────────────────────
    // **바닥에 남는다.** 목업의 인상 절반이 이것이다 — 지나온 자리가 보이는 것.
    //   ⚠ 목업은 적 4~5마리 화면이고 지금은 **동시 270**이다. 상한이 없으면 밤
    //   후반에 바닥이 통째로 붉어진다. 층으로 확률을 갈라 물량층은 드물게 남긴다.
    if (this.rnd() < (e.kind === 'swarm' ? BLOOD.pSwarm : BLOOD.pElite)) {
      this.blood.push({ x: e.x, y: e.y, t: this.t,
                        r: (e.kind === 'swarm' ? 1 : 1.7) * (0.8 + this.rnd() * 0.5),
                        a: Math.atan2(e.hitDy ?? 0, e.hitDx ?? 1)
                           + (this.rnd() - 0.5) * 0.34 });
      if (this.blood.length > BLOOD.cap) this.blood.shift();
    }
    // ── 시체 ──────────────────────────────────────────────────
    // 잠깐 남았다가 스러진다. **영구히 두면 270마리 화면에서 시체가 화면을 덮는다.**
    // **맞은 방향으로 밀려나며 스러진다.** 플레이어 반대쪽으로 튄다.
    const kx = e.hitDx ?? (e.x - this.player.x);
    const ky2 = e.hitDy ?? (e.y - this.player.y);
    const kd = Math.hypot(kx, ky2) || 1;
    const fs = HIT.fly * (e.kind === 'swarm' ? 1 : 0.55);
    this.corpses.push({ x: e.x, y: e.y, t: this.t, sp: e._sp, fn: e._fn, fx: e._fx, oy: e._oy,
                        vx: (kx / kd) * fs, vy: (ky2 / kd) * fs });
    if (this.corpses.length > BLOOD.corpseCap) this.corpses.shift();
    // 「불티」 — **조건: 「기름을 붓는다」 보유**(cards.js). 명세에 이미 있는 조항으로
    // 카드 간 조건부를 만드는 유일한 사례다 — 새 규칙을 안 만들고 조합을 하나 얻는다.
    if (this.mods.ember > 0 && this.fires.length < FIRE.maxZones
        && this.rnd() < 0.10 * this.mods.ember) {
      this.fires.push({ x: e.x, y: e.y, life: FIRE.emberLife, r: FIRE.emberR });
    }
    // **필드 드랍** — 드물게 「그러모으기」가 떨어진다. 주우면 화면 전체가 빨려온다.
    if (this.rnd() < GATHER.chance) {
      this.drops.push({ x: e.x, y: e.y, kind: 'gather', t: this.t });
      this.m.gatherDrops = (this.m.gatherDrops ?? 0) + 1;
    }
    // 「더 남긴다」 — 처치한 자리에 조각이 조금 더(부가 줄)
    this.dropPiece(e.x, e.y, (PIECE[e.kind] ?? PIECE.swarm) * (1 + (this.mods.traits?.pieceBonus ?? 0)));   // 6장 드랍값 정본
  }

  // ── 조각 ────────────────────────────────────────────────────
  // ⚠⚠ **음수 좌표에서 칸이 충돌한다** 【벽 제거 2026-08-02】
  //   구 열쇠는 `(y/128|0) * stride + (x/128|0)` 였다. 벽이 있어 좌표가 항상 0 이상일
  //   때는 맞았지만, 들판에 끝이 없어진 지금 x·y 가 **음수**가 된다. 그러면
  //     · `|0` 이 0 쪽으로 잘라서 x=−1 과 x=+1 이 **같은 칸**이 되고
  //     · 열쇠가 겹쳐 **엉뚱한 칸의 조각을 줍거나 못 줍는다**
  //   → `Math.floor` 로 바닥을 향해 자르고, 열쇠를 **충돌 없는 짝지음**으로 만든다.
  //   ⚠ `1e7` 은 「한 밤에 다윗이 갈 수 있는 칸 수」보다 훨씬 크다(12분 × 최대 속도로도
  //     수천 칸이다). 그 안에서는 (cx, cy) 한 쌍이 열쇠 하나에 정확히 대응한다.
  _pcell(x, y) { return this._pkey(Math.floor(x / 128), Math.floor(y / 128)); }
  // ⚠ **열쇠를 만드는 자리는 여기 하나다.** 구 판은 이웃 칸을 훑는 두 곳
  //   (`pullPieces`·`collectPieces`)이 `(gy+oy) * stride + (gx+ox)` 로 **손수 다시**
  //   만들고 있었다. 한쪽만 고치면 넣는 칸과 찾는 칸이 갈려 **조각이 영영 안 먹힌다** —
  //   이 저장소가 8-01 에 이미 한 번 당한 종류다(그때는 격자를 안 옮긴 것이었다).
  _pkey(cx, cy) { return cy * 1e7 + cx; }
  // ══════════════════════════════════════════════════════════════
  //  ⚠⚠ **조각이 칸을 넘어가면 격자도 따라가야 한다**
  //      【사장님 신고 2026-08-01 — *"밤에 치즈 대량으로 먹으면 다 안먹어지는 버그"*】
  //
  //  격자(`_pgrid`)는 **떨어뜨릴 때 한 번** 넣고 그 뒤로는 한 번도 안 옮겼다.
  //  그런데 조각은 계속 움직인다 — 튀어나오고(vx/vy), 흡인에 끌려오고,
  //  「그러모으기」 때는 **1.6초 × 1400px/s = 2240px** 를 날아온다.
  //  `collectPieces` 는 플레이어 둘레 칸만 훑으므로, **먼 칸에 등록된 채 몸 위에
  //  올라앉은 조각은 영영 안 먹힌다.** 실측(N1·시드 11·완주):
  //      몸 안에 있는데 안 먹힌 프레임 **2442** (최대 동시 **396개 · 543어치**)
  //      밤 끝에 318 이 그대로 남았다
  //  남은 것은 `sweepField` 가 쓸어 담아 **치즈는 되지만**, `inNight:false` 라
  //  **레벨업에는 안 잡힌다** — 그래서 「대량으로 먹었는데 다 안 들어온다」로 보인다.
  //  그러모으기는 *"한 번에 1~2단계"* 를 주라고 만든 사건인데 그 몫이 새고 있었다.
  //
  //  → **움직인 조각은 그 자리에서 다시 꽂는다.** 칸이 안 바뀌면 아무 일도 안 한다.
  // ══════════════════════════════════════════════════════════════
  _reseatPiece(g, oldCell) {
    const nc = this._pcell(g.x, g.y);
    if (nc === oldCell) return;
    const a = this._pgrid.get(oldCell);
    if (a) { const i = a.indexOf(g); if (i >= 0) a.splice(i, 1); }
    let b = this._pgrid.get(nc);
    if (!b) this._pgrid.set(nc, b = []);
    b.push(g);
  }
  dropPiece(x, y, val) {
    // 소이벤트 ⑥ 「무너진 우리」 같은 낮 계수가 여기서 먹는다.
    // **드랍 시점에 곱한다** — 획득 시점에 곱하면 흘린 조각(sweepField)과 어긋난다.
    if (this.dayIn.pieceMul !== 1) val *= this.dayIn.pieceMul;
    // **처치한 자리에서 튀어나온다** — 그냥 놓이면 화면이 조용하다.
    //   VS 는 바닥이 보석으로 깔리고 그게 화면 밀도의 절반이다.
    const a = this.rnd() * Math.PI * 2, sp = 70 + this.rnd() * 90;
    const g = { x, y, v: val, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, born: this.t };
    this.pieces.push(g);
    const c = this._pcell(x, y);
    let arr = this._pgrid.get(c);
    if (!arr) this._pgrid.set(c, arr = []);
    arr.push(g);
    this.m.dropped += val;
  }
  // ── 조각 흡인 ────────────────────────────────────────────────
  // **뱀서류에서 화면을 가장 시끄럽게 만드는 것이 빨려오는 젬 무리다.** 구 구현은
  // 반경에 들어온 조각을 **즉시 지웠다** — 규칙은 같은데 화면에서는 아무 일도 안
  // 일어났다. 그리고 이것은 장식이 아니라 정보다: *"내 사거리 안에서 죽은 것은
  // 내 것이다"*(10-B 2-3)가 눈에 보이는 유일한 자리다.
  //   반경에 들면 **끌려오기 시작**하고, 몸에 닿아야 실제로 들어온다. 판정 시점이
  //   반경 진입에서 접촉으로 옮겨 가지만 **거리가 짧아 밤 총량에는 영향이 없다**
  //   (검증: 조각 총합 ±0.5%). 흡인 속도가 그 지연을 정한다.
  pullPieces(dt) {
    const p = this.player;
    // ── 「그러모으기」 발동 중 — **화면 밖 것까지 전부 온다** ──────
    // 반경도 격자도 안 본다. 이 순간만큼은 밤 전체가 내 것이다.
    if (this.t < this._gatherUntil) {
      for (const g of this.pieces) {
        if (g.dead) continue;
        const dx = p.x - g.x, dy = p.y - g.y, d = Math.hypot(dx, dy) || 1;
        g.pull = true;
        const oc = this._pcell(g.x, g.y);      // 옮기기 전 칸
        g.x += (dx / d) * GATHER.pullV * dt;
        g.y += (dy / d) * GATHER.pullV * dt;
        // ⚠ **여기가 그 버그의 자리였다.** 이 구간은 화면 밖 조각까지 2240px 를
        //   끌어오는데 격자를 안 옮겨서, 도착한 조각을 `collectPieces` 가 못 봤다.
        this._reseatPiece(g, oc);
      }
      return;
    }
    const R = PLAYER.pickup * this.mods.pickupMul, R2 = R * R;
    // ⚠ 바닥 자르기(`Math.floor`)와 `_pkey` 를 쓴다 — 넣는 자리(`_pcell`)와 **같은 셈**이어야 한다.
    const gx = Math.floor(p.x / 128), gy = Math.floor(p.y / 128);
    const CR = Math.ceil(R / 128);
    for (let ox = -CR; ox <= CR; ox++) for (let oy = -CR; oy <= CR; oy++) {
      const arr = this._pgrid.get(this._pkey(gx + ox, gy + oy));
      if (!arr) continue;
      // ⚠ **배열을 복사해서 돈다.** `_reseatPiece` 가 이 배열에서 조각을 빼낼 수
      //   있어서(칸을 넘어가면), 그대로 돌면 도는 중에 원소가 사라져 **한 칸 건너뛴다.**
      for (const g of arr.slice()) {
        if (g.dead) continue;
        const dx = p.x - g.x, dy = p.y - g.y, d2 = dx * dx + dy * dy;
        if (!g.pull && d2 > R2) continue;
        g.pull = true;                       // 한번 걸리면 놓지 않는다
        const d = Math.sqrt(d2) || 1;
        // 가까울수록 빨라진다 — 마지막에 «톡» 하고 빨려드는 느낌이 여기서 난다
        const v = PIECE_PULL.base + PIECE_PULL.gain * (1 - Math.min(1, d / R));
        const oc = this._pcell(g.x, g.y);
        g.x += (dx / d) * v * dt; g.y += (dy / d) * v * dt;
        this._reseatPiece(g, oc);
      }
    }
  }
  collectPieces() {
    const p = this.player, R = PLAYER.pickup * this.mods.pickupMul, R2 = R * R;   // 「그러모으기」
    // ⚠ 넣는 자리(`_pcell`)와 **같은 셈**이어야 한다 — 바닥 자르기 + `_pkey`.
    const gx = Math.floor(p.x / 128), gy = Math.floor(p.y / 128);
    // **격자 탐색 범위는 흡수 반경에서 파생해야 한다.** 구 구현은 ±1칸(≈128px)로 박혀
    // 있었는데, 10-B 2-3이 흡수 반경을 2.5 → 13.5유닛(432px)으로 올리자 **반경을 올려도
    // 실제로는 안 주워지는** 상태가 됐다(조용한 상한). 칸 크기 128px 에 맞춰 계산한다.
    const CR = Math.ceil(R / 128);
    let got = 0;
    for (let ox = -CR; ox <= CR; ox++) for (let oy = -CR; oy <= CR; oy++) {
      const arr = this._pgrid.get(this._pkey(gx + ox, gy + oy));
      if (!arr) continue;
      for (let i = arr.length - 1; i >= 0; i--) {
        const g = arr[i];
        // ⚠ **한 번 먹은 것은 두 번 안 먹는다.** 이 검사가 없어서, 격자에 잘못
        //   되돌아온 죽은 조각이 다시 세어졌다(2026-08-01 실측 — 주운 양이 떨군 양
        //   초과). 되돌아오는 경로는 막았지만, **세는 자리에도 빗장을 건다** —
        //   경제 장부에 없는 조각이 들어오면 그 밤의 수가 통째로 거짓이 된다.
        if (g.dead) { arr.splice(i, 1); continue; }
        const dx = g.x - p.x, dy = g.y - p.y;
        // **몸에 닿아야 들어온다.** 반경은 「끌기 시작하는 선」이 됐다(pullPieces).
        if (dx * dx + dy * dy > PIECE_PULL.eat * PIECE_PULL.eat) continue;
        got += g.v; arr.splice(i, 1); g.dead = true;
      }
    }
    // 회수율 이동평균 — levelNeed 의 분자다. **떨군 양이 아니라 주운 양**을 센다.
    this._pickAcc = (this._pickAcc ?? 0) + got;
    this._pickT = (this._pickT ?? 0) + this._dt;
    if (this._pickT >= 0.5) {
      const inst = this._pickAcc / this._pickT;
      const a = Math.min(1, this._pickT / LEVEL_CURVE.win);
      this._pickRate = (this._pickRate ?? inst) * (1 - a) + inst * a;
      this._pickAcc = 0; this._pickT = 0;
    }
    if (got > 0) { this.m.pickedNight += got; this.gainPieces(got, true); }
  }
  // ── 레벨업 요구량 ───────────────────────────────────────────
  //  ■ **【2026-07-31】 요구량은 「레벨」의 함수다**(사장님 지시: 초반 빠르게 · 후반 느리게).
  //        need(L) = base × first × growth^(L−1)
  //    레벨이 오를수록 비싸지므로 후반이 저절로 뜸해진다. 근거는 `config.LEVEL_REQ` 머리.
  //  ■ 구 「유입 비례」(LEVEL_CURVE)는 **꺼져 있다** — 시간 균등을 노린 장치라 목표와
  //    반대였고, `cap: 2.5` 때문에 마지막 구간에서 오히려 폭주를 만들었다.
  //    배선은 남아 있어 `LEVEL_CURVE.on` 하나로 되켤 수 있다. **둘이 동시에 켜지면 안 된다.**
  // 그 밤의 **기준 요구량.** 레벨을 안 탄다 — 곡선과 무관한 밤 규모의 자(尺)다.
  //   레벨에 따라 움직이면 안 되는 것들(그러모으기 드랍 등)이 이것을 읽는다.
  get levelBase() {
    return this.levelStep ?? pieceReq(this.cfg, this.level, this.reqMode);
  }
  get levelNeed() {
    const base = this.levelBase;
    if (LEVEL_REQ.on) return levelRequirement(base, this.level);
    if (!LEVEL_CURVE.on) return base;
    // 분모 = 그 밤 평균 회수율(조각/초) = 총 조각 ÷ 밤 길이. 목표 레벨업 수 × base 가
    // 곧 총 조각이므로 **새 상수가 아니다.**
    const avg = (base * TARGET_LV) / NIGHT_SEC;
    const r = (this._pickRate ?? 0) / Math.max(1e-6, avg);
    const k = Math.min(LEVEL_CURVE.cap, Math.max(LEVEL_CURVE.floor, r * LEVEL_CURVE.g(this.t / NIGHT_SEC)));
    return Math.max(1, Math.round(base * k));
  }
  gainPieces(v, inNight) {
    this.piecesHeld += v;
    this.gauge += v;
    let need = this.levelNeed;
    while (this.gauge >= need) {
      this.gauge -= need;
      this.level++;
      this.m.levelUpsTotal++;
      if (inNight) {
        this.m.levelUpsNight++; this.m.levelUpTimes.push(this.t); this.openShop();
        this.refreshWeapon();      // 【10-B 6-2】 레벨업이 실제로 무기에 먹힌다
      }
      need = this.levelNeed;
    }
  }

  // ── 4-3 레벨업 상점 ─────────────────────────────────────────
  // 상점이 열리면 **최대 3회** 산다. 리롤은 그 3회에 포함되지 않고 가격만 제약한다.
  // 지갑 = 그 밤에 주운 조각의 치즈 환산 − 이미 쓴 치즈(밤 안에서만 결제. 새벽 리셋).
  openShop() {
    this.shopOpens++;
    this.buysWanted += SHOP.buysPerLevel;
    const before = this.buysThisNight;
    // 【2026-07-31】 캡스톤 「주머니의 것」의 **무료 요령 1**이 여기 있었다 — 카드 삭제로
    //   무효가 됐다(요령이라는 물건 자체가 없다). ⚠ 캡스톤의 나머지 효과(밀집 우선 해금)는
    //   산다. 빈자리의 대체는 **재설계 대기** — 지어내지 않는다(day/nodes.js 주석 참조).
    for (let k = 0; k < SHOP.buysPerLevel; k++) {
      const price = buyPrice(this.buysThisNight + 1, this.shopMult, NIGHT_NO[this.nightKey] ?? 1);
      if (this.wallet < price) break;
      this.spent += price; this.buysThisNight++;
    }
    // 한 장이라도 샀으면 그 레벨업은 「집은」 것이다. 두 장·세 장을 더 사도 **내 카드는
    // 그중 많아야 한 장**이므로 중첩은 여기서 한 번만 오른다(8장 "3장 서로 다름").
    this.refreshWeapon();     // 【10-B 6-2】 집은 만큼 세진다 — 못 사면 안 세진다
    // 리롤 — 기준 봇은 밤당 2회. 가격은 config.rerollPriceAt의 선형 정본을 쓴다.
    if (this.rerollsThisNight < SHOP.rerollPerNight) {
      const nextCum = rerollCum(this.rerollsThisNight + 1) - rerollCum(this.rerollsThisNight);
      if (this.wallet >= nextCum) { this.spent += nextCum; this.rerollsThisNight++; }
    }
  }
  // ⚠ **낮에서 들고 온 치즈를 밤에서 쓴다**(사장님 지시 2026-07-29 — 「치즈는 모든 날 연속」).
  //   구 구현은 `그 밤 환전액 − 쓴 것` 이라 **낮에 모아 둔 잔액이 밤 상점에 안 실렸다** —
  //   둘째 밤에 들어가면 치즈가 0부터 다시 시작하는 것처럼 보였다.
  //   `nightio.toNightOpts` 에 `walletCarry` 자리가 이미 있었고 **비어 있었다.**
  //
  //   ⚠ **`cheese` 는 그대로 「그 밤에 번 것」이다.** 이월분을 거기 섞으면
  //     불변식 ③(밤 소비 = 전투 수입의 50~60%)의 분모가 오염된다.
  //     이월은 **지갑에만** 얹고, 낮으로 돌려보내는 값은 `wallet − carry` 다
  //     (= 그 밤의 순증감. 이월분을 두 번 더하지 않는다 — nightio.js 참조).
  get wallet() { return this.walletCarry + Math.floor(this.piecesHeld / PIECE.perCheese) - this.spent; }
  // 그 밤이 낮 지갑에 남기는 순증감. 이월분을 뺀 것이며 **음수일 수 있다**
  // (번 것보다 많이 썼다 = 낮에 모아 둔 것을 헐었다. 그것이 정상이다).
  get walletNet() { return this.wallet - this.walletCarry; }

  // 4-6 — 밤이 끝나는 순간 필드 잔여 조각을 전량 회수한다(회수율 100%).
  sweepField() {
    let s = 0;
    for (const g of this.pieces) if (!g.dead) s += g.v;
    s *= SWEEP_AT_END;
    this.m.swept = s;
    if (s > 0) this.gainPieces(s, false);
    this.pieces.length = 0; this._pgrid.clear();
  }
  get cheese() { return Math.floor(this.piecesHeld / PIECE.perCheese); }

  // 개 — 자동. 6-1 계약(DPS 6 × 듀티 0.70, 우선순위 정예 > 물량). 임의 배율 금지.
  moveDogs(dt) {
    // 「짧은 휘파람」(반응·이동) · 「개를 앞세운다」(요격 거리) · dogDps 는 미결선(개를 먹인다)
    const c = this.player, DET = DOG.detect * this.mods.dogDetect, DET2 = DET * DET;
    const dogSp = DOG.speed * this.mods.dogSpeed, dogDmg = DOG.dps * DOG.duty * this.mods.dogDps;
    for (const [i, d] of this.dogs.entries()) {
      d.attack = Math.max(0, (d.attack ?? 0) - dt);
      let tgt = null, bs = Infinity;
      for (const e of this.enemies) {
        const ex = e.x - c.x, ey = e.y - c.y;
        if (ex * ex + ey * ey > DET2) continue;    // 감지 반경(밤눈)
        // **다른 개가 이미 문 표적은 뒤로 민다.** 둘 다 「가장 가까운 비물량층」을
        // 고르면 표적이 늘 같아지고, 같은 표적은 곧 같은 좌표다. 벌점이 물량층
        // 벌점(900)보다 작은 것이 요점이다 — 겹치느니 물량층으로 가라는 뜻은 아니다.
        const taken = e._dogClaim === this.t && e._dogBy !== i ? 420 : 0;
        const s = dist(d, e) + (e.kind === 'swarm' ? 900 : 0) + taken;
        if (s < bs) { bs = s; tgt = e; }
      }
      if (tgt) { tgt._dogClaim = this.t; tgt._dogBy = i; }
      // 개 짖음 = **연출**(2-7). 표식만 남고 규칙·봇 정책은 안 읽는다.
      if (tgt && tgt.kind !== 'swarm' && !tgt.barked) {
        tgt.barked = true;
        this.barks.push({ x: tgt.x, y: tgt.y, t: this.t });
      }
      d.mode = tgt ? '요격' : '순찰';
      if (tgt) {
        const dx = tgt.x - d.x, dy = tgt.y - d.y, dd = Math.hypot(dx, dy) || 1;
        d.x += (dx / dd) * dogSp * dt; d.y += (dy / dd) * dogSp * dt;
        if (dd < UNIT) {
          // ── 렌더 전용 ────────────────────────────────────────
          // ⚠ **판정은 한 줄도 안 바뀐다.** 피해는 아래처럼 여전히 연속(dps × dt)이고,
          //   여기서 하는 일은 **화면이 읽을 표식을 남기는 것**뿐이다.
          //   구 구현은 `d.attack` 하나만 찍었는데, 그것으로는 ⓐ 어느 쪽을 물었는지
          //   ⓑ 물린 놈이 누구인지를 화면이 알 수 없었다. 그래서 개가 일하는 것이
          //   **화면 어디에도 안 나타났다**(config.js DOGFX 머리 참조).
          d.attack = DOGFX.hold;                   // 무는 프레임이 서 있는 시간
          d.biteDx = dx / dd; d.biteDy = dy / dd;  // 어느 쪽을 물었나 — 호를 그 방향으로 편다
          // **물기 표시는 주기를 갖는다.** 매 프레임 섬광을 찍으면 상시 점등이 되어
          // 정보가 아니라 소음이 된다. 피해는 연속이고 **표시만** 리듬을 갖는다.
          if (this.t - (d.biteAt ?? -9) >= DOGFX.bite) {
            d.biteAt = this.t;
            // 물린 놈에게 **기존 타격 섬광 채널**을 그대로 켠다(`vfx.drawContactFx`).
            // 새 채널을 만들지 않는다 — 이미 「맞았다」를 말하는 표식이 있다.
            tgt.hitT = this.t; tgt.hitDx = d.biteDx; tgt.hitDy = d.biteDy;
          }
          const biteDamage = dogDmg * dt;
          this.enemyBehavior?.recordDamageTaken(tgt, Math.min(biteDamage, Math.max(0, tgt.hp)));
          tgt.hp -= biteDamage;
          if (tgt.hp <= 0) this.killEnemy(this.enemies.indexOf(tgt), 'dog');
        }
      } else {
        // 순찰 대기 반경. 두 마리를 반대편에 세운다.
        const a = this.t * 0.9 + i * (Math.PI * 2 / Math.max(1, this.dogs.length)),
              PR = 5 * UNIT;
        d.x += ((c.x + Math.cos(a) * PR) - d.x) * 2.2 * dt;
        d.y += ((c.y + Math.sin(a) * PR) - d.y) * 2.2 * dt;
      }
    }
    // ── 두 마리는 **절대로 같은 자리에 못 있는다** ─────────────
    // 위 표적 벌점은 「덜 겹치게」 하는 것이고, 이것은 「겹칠 수 없게」 하는 것이다.
    // 표적이 하나뿐인 순간(정예 한 마리)에는 벌점으로도 결국 같은 데로 모이므로
    // **좌표 자체를 갈라 놓아야** 한다. 서로를 정확히 반씩 민다.
    const G = DOG.minGap;
    for (let a = 0; a < this.dogs.length; a++) {
      for (let b = a + 1; b < this.dogs.length; b++) {
        const p = this.dogs[a], q = this.dogs[b];
        let dx = q.x - p.x, dy = q.y - p.y;
        let dd = Math.hypot(dx, dy);
        if (dd >= G) continue;
        // 완전히 겹쳐 방향이 없으면 개마다 다른 쪽으로 뗀다(결정적 — 시드 안 먹는다)
        if (dd < 1e-4) { dx = 1; dy = 0; dd = 1; }
        const push = (G - dd) / 2;
        p.x -= (dx / dd) * push;  // 벽 없음(FIELD)
        p.y -= (dy / dd) * push;
        q.x += (dx / dd) * push;
        q.y += (dy / dd) * push;
      }
    }
  }

  // ── 안정성 (3-1 확정본) ─────────────────────────────────────
  //   감소 = 반경 내 적 수 × coef × 무리배율, 상한 cap × 무리배율
  //   회복 = 반경 내 적이 **없을 때만** +rec
  //   **피격 항 없음.**
  // 여러 반경을 동시에 적분한다(위 constructor 주석의 독립성 논거).
  updateStability(dt) {
    const p = this.player, R = this.radii, K = R.length;
    const cnt = this._cnt ??= new Int32Array(64);
    for (let i = 0; i < K; i++) cnt[i] = 0;
    const rmax2 = R[K - 1] * R[K - 1];
    for (const e of this.enemies) {
      const dx = e.x - p.x, dy = e.y - p.y, d2 = dx * dx + dy * dy;
      if (d2 > rmax2) continue;
      // 가장 작은 "포함하는" 반경의 버킷에 넣고 뒤에서 누적한다
      let lo = 0, hi = K - 1;
      while (lo < hi) { const mid = (lo + hi) >> 1; if (R[mid] * R[mid] >= d2) hi = mid; else lo = mid + 1; }
      cnt[lo]++;
    }
    let acc = 0;
    const capped = STAB.cap * this.flockMult;
    for (let i = 0; i < K; i++) {
      acc += cnt[i];
      const tr = this.tracks[i];
      tr.inRadSum += acc * dt;
      if (acc > 0) {
        // 낮의 `stabDropMul` 은 **상한 안쪽에서** 곱한다 — 상한 밖에서 곱하면
        // 5장 상한(STAB.cap)이 낮 계수로 넘어가 버린다(헌법 5조 밴드 위반).
        const raw = acc * STAB.coef * this.flockMult * this.dayIn.stabDropMul;
        const drop = Math.min(capped, raw);
        if (raw >= capped) tr.capSec += dt;
        if (!tr.panic) {
          tr.s = Math.max(0, tr.s - drop * dt);
          if (tr.s <= 0) { tr.s = 0; tr.panic = true; }
        }
      } else {
        tr.recSec += dt;
        tr.s = Math.min(100, tr.s + STAB.rec * this.dayIn.stabRecMul * dt);   // ← 양축 1단계
        if (tr.panic && tr.s >= STAB.panicOut) tr.panic = false;
      }
      if (this.traceBins) {
        const bi = Math.min(this.traceBins - 1, (this.t / this.traceDt) | 0);
        const g = this.trace[i];
        g.allN[bi] += dt;
        if (acc > 0) { g.busySum[bi] += acc * dt; g.busyN[bi] += dt; }
      }
      tr.sum += tr.s * dt; tr.n += dt;
      if (tr.s < tr.min) tr.min = tr.s;
      if (tr.panic) tr.panicSec += dt;
      if (i === this.primary) {
        this._inRadPrimary = acc;
        // 대표 반경에서만 추가로 재는 것 — gb.html 판정선 4종(「12분 회피가 지루하지 않은가」)이
        // 안정성 표준편차와 위기 진입 횟수를 읽는다. 다중 반경 전부에 달 이유는 없다.
        const m = this.m, s = tr.s;
        m.stabSqSum += s * s * dt; m.stabN += dt;
        const line = this.crisisLine;
        if (s < line) {
          m.crisisSec += dt;
          if (!this._inCrisis) { this._inCrisis = true; m.crisisEnters++; }
        } else if (this._inCrisis && s >= line + 5) this._inCrisis = false;
        // +5%p 히스테리시스 — **임의값 — 미검증.** 경계 떨림으로 위기 횟수가 부풀지 않게 하는 폭
      }
    }

    this.sinceHit += dt;
    if (this.sinceHit > this.m.noHitMax) this.m.noHitMax = this.sinceHit;
    this.m.enemySum += this.enemies.length; this.m.enemyN++;
  }

  avgStabAt(i) { const t = this.tracks[i]; return t.n ? t.sum / t.n : 100; }
  inRadAt(i)   { const t = this.tracks[i]; return t.n ? t.inRadSum / t.n : 0; }
  get avgStability() { return this.avgStabAt(this.primary); }
  get avgInRadius()  { return this.inRadAt(this.primary); }

  // 손실 정산 (3-8) — 밤 평균 안정성 → 배율 → 그 밤에 잃을 양 수.
  settle(avg = this.avgStability) {
    const base = this.baseLoss;
    if (this.result === '다운' || this.finalCombatDown) {
      const remain = 1 - (this.m.downAt ?? this.t) / NIGHT_SEC;
      const mult = +(DOWN.base + DOWN.slope * remain).toFixed(2);
      return { avg, band: `다운(${((1 - remain) * 100).toFixed(0)}% 지점)`, mult,
               loss: +(base * mult).toFixed(2) };
    }
    const row = LOSS_TABLE.find(r => avg >= r.min) ?? LOSS_TABLE[LOSS_TABLE.length - 1];
    return { avg, band: row.label, mult: row.mult,
             loss: +(base * row.mult).toFixed(2) };
  }

  finish() { this.sweepField(); this.settled = this.settle(); }

  // 검증 리포트
  report() {
    const m = this.m, T = Math.max(this.t, 1);
    const avg = this.avgStability;
    const st = this.settle(avg);
    const totalKills = m.killsPlayer + m.killsDog;
    const tr = this.tracks[this.primary];
    return {
      결과: this.result ?? '진행중',
      무기: this.weapon === 'pasu' ? '원딜(물맷돌)' : '근딜(지팡이)',
      경과: `${this.t.toFixed(0)}초 / ${NIGHT_SEC}`,
      평균안정성: +avg.toFixed(1),
      '원장 목표': `${this.cfg.ledgerStab ?? '-'}%`,
      '판정 반경': `${(this.radii[this.primary] / UNIT).toFixed(0)}유닛`,
      '안정성 진폭': `${tr.min.toFixed(0)}~100 (σ ${Math.sqrt(Math.max(0,
        m.stabSqSum / Math.max(m.stabN, 1e-6) - avg * avg)).toFixed(1)})`,
      '위기 구간': `${m.crisisEnters}회 / ${m.crisisSec.toFixed(0)}초`,
      '반경 내 적(평균)': +this.avgInRadius.toFixed(1),
      '상한 접촉': `${(tr.capSec / T * 100).toFixed(0)}%`,
      '회복 가동': `${(tr.recSec / T * 100).toFixed(0)}%`,
      '손실 배율': `×${st.mult} [${st.band}]`,
      '예상 손실': `${st.loss}마리`,
      HP: `${Math.max(0, Math.round(this.player.hp))} / ${this.maxHp}`,
      '다운 시각': m.downAt == null ? '-' : `${m.downAt.toFixed(0)}초`,

      '피격 횟수': `${m.hits}회 (물량 ${m.hitsSwarm} / 정예 ${m.hitsElite} / 위협 ${m.hitsThreat})`,
      '피격 총량': m.dmgTaken,
      '물가 음용': this.waterOn ? `${m.drinks}회 / 회복 ${Math.round(m.healed)}` : '없음',
      '패닉': `${tr.panicSec.toFixed(0)}초`,
      '무피격 최장': `${m.noHitMax.toFixed(0)}초 (밤의 ${(m.noHitMax / T * 100).toFixed(0)}%)`,

      '플레이어 킬': m.killsPlayer,
      '개 킬': m.killsDog,
      '독수리': `출현 ${m.eaglesSpawned} / 처치 ${m.eaglesKilled} / 이탈 ${m.eaglesEscaped} / 강하 피격 ${m.eagleImpactHits}`,
      '플레이어 기여율': totalKills ? `${(m.killsPlayer / totalKills * 100).toFixed(0)}%` : '-',
      '명중률': m.shots ? `${(m.shotHits / m.shots * 100).toFixed(0)}%` : '-',
      '평균 적 수': m.enemyN ? +(m.enemySum / m.enemyN).toFixed(0) : 0,
      '동시 상한 점유': `${(m.atCapSec / T * 100).toFixed(0)}%`,
      '스폰': `${m.spawned} / ${this.plannedSpawnTotal}`,

      '빌드': this.build === 'ramp'
        ? `ramp — 구매 ${this.buysThisNight}회 · 축 ${(this.axis * 100).toFixed(0)}%`
        : (BUILD[this.build]?.label ?? this.build),
      '무기 실효': this.weapon === 'pasu'
        ? `${this._ws.rate.toFixed(2)}발/초 × ${this._ws.projectiles}발 · 사거리 ${(this._ws.range / UNIT).toFixed(1)}유닛`
        : `${this._ws.rate.toFixed(2)}스윙/초 · 반경 ${(this._ws.radius / UNIT).toFixed(2)}유닛`,
      '레벨': this.level,
      '레벨업(밤 중)': m.levelUpsNight,
      '레벨업(회수 포함)': m.levelUpsTotal,
      '상점 구매/희망': `${this.buysThisNight} / ${this.buysWanted}`,
      '밤 지출': `${this.spent}치즈 (리롤 ${this.rerollsThisNight}회 포함)`,
      '조각 밤중/회수': `${m.pickedNight.toFixed(0)} / ${m.swept.toFixed(0)}`,
      '밤중 픽업률': m.dropped ? `${(m.pickedNight / m.dropped * 100).toFixed(0)}%` : '-',
      '치즈': this.cheese,

      '이동 거리': `${(m.moveDist / UNIT).toFixed(0)}유닛`,
      '정지 시간': `${m.idleSec.toFixed(0)}초 (${(m.idleSec / T * 100).toFixed(0)}%)`,
      ...(this.rescue ? {
        '구출': this.foundAt == null ? '미도달'
              : `${this.foundBy} ${this.foundAt.toFixed(0)}초 (패널 후 ${(this.foundAt - RESCUE.panelAt).toFixed(0)}초)`,
        '양 초기 거리': this.rescueDist0 == null ? '미스폰'
              : `${(this.rescueDist0 / UNIT).toFixed(0)}유닛 (패널 등장 시점 기준)`,
      } : {}),
    };
  }
}

// 3막 밀도 곡선 — 6장 막별 배율(1막 0.6 / 2막 1.0 + 하울링 후 스파이크 1.5 / 3막 1.2)의 구현
// ══════════════════════════════════════════════════════════════════
//  스폰 곡선 — **「언제」를 정한다. 「몇 마리」는 예산이 정한다.**
//
//  캠페인은 `campaignSpawnTarget`, 명시적 실험은 `totalBudget`에 맞춰 정규화하므로
//  여기 숫자를 통째로 올려도 총 마리 수는 안 늘어난다. **바뀌는 것은 배분뿐이다.**
//  캠페인 총량은 밤별 `spawnMult`가 아니라 config의 하루 곡선이 올린다.
//
//  ⚠ **사장님 지시(2026-07-29)**
//        「시작부터 끝나기 전 81초까지 총 몹 수를 **1.2배**.
//         끝나기 전 80초부터는 **소강 없이 완전히 폭발적으로.**」
//
//  ■ 구 곡선의 마지막 칸이 **0.15 — 밤에서 가장 낮은 값**이었다. 12분을 버티고 온
//    마지막 1분 20초가 가장 텅 비어 있었다. 거기가 이제 밤에서 가장 두껍다:
//        p ≥ 640/720 (= 마지막 **80초**)  →  **24.0**  (마지막 파도 2.1 의 11.4배)
//
//  ■ **앞 구간(0~639초)의 rate 는 구 값 그대로다** — 0.25 / 0.7 / 1.9 / 1.2 / 2.1.
//    거기서 총 몹 수를 1.2배로 만드는 것은 rate 가 아니라 **예산**이 한다.
//
//  ■ ⚠ **두 구간은 서로를 밀어낸다 — 그래서 목표에서 역산했다.**
//    `spawnScale = totalBudget / (avgSpawnRate × NIGHT_SEC)` 가 곡선을 예산에 맞춰
//    정규화하므로, 각 구간의 몫은 **rate × 구간폭**의 비율로 갈린다.
//    끝을 올리면 앞의 몫이 줄어드는 구조라, 「앞 ×1.2」와 「끝 폭발」을 **동시에**
//    만족시키려면 예산을 그만큼 키워야 한다. 계산:
//        구 곡선에서 앞 구간의 몫 = 98.36%
//        신 곡선  S_앞 = 1.09554 · S_끝 = (1−640/720)×24 = 2.6664
//        필요 예산배수 = 1.2 × 0.9836 × (S_앞+S_끝) / S_앞 = **4.053**
//    → `config.SPAWN_VOLUME = 4.053`. **끝 rate 를 바꾸면 그 수도 같이 바꿔야 한다.**
//    결과: 앞 구간 정확히 ×1.2 · **마지막 80초 하나가 구 밤 전체의 2.87배.**
//
//  ■ ⚠ 예산·경제 정합은 **나중에 한 번에** 잡는다(사장님 지시). 여기서 안 맞춘다.
// ══════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════
//  ⚠⚠ **【2026-07-31 전면 교체】 rate 를 손으로 안 적는다 — 몫에서 유도한다.**
//    사장님 지시: 초반 2분 **5%** · 중간 8분 **40%** · 후반 2분 **55%**.
//    설계값은 몫이고 rate 는 그 결과다. 근거는 `config.SPAWN_SHAPE` 머리 참조.
//
//    구 곡선은 rate 만 적혀 있었고 그 몫이 어디에도 없었다. 실측하니
//    **첫 120초 1.1% · 마지막 80초 70.9%** 였고, 그것이 레벨업 폭주의 직접 원인이었다.
//
//    ■ 유도: 구간 몫 = rate × 구간폭 이므로 **rate = 몫 ÷ 폭**이다.
//      중간은 내부 모양(3막 리듬 — 헌법 1조)을 그대로 두고 **덩어리째 40% 로 정규화**한다.
//    ■ 이렇게 잡으면 보통 밤의 **raw 평균 rate 가 정확히 1.0** 이 된다(몫 합 = 1).
//      캠페인은 tail·wave·boss까지 포함한 실효 평균으로 한 번 더 정규화한다.
// ══════════════════════════════════════════════════════════════════
const END_SURGE_AT = SPAWN_SHAPE.tailFrom;    // 밤의 마지막 2분
const HEAD_UNTIL = SPAWN_SHAPE.headUntil;

// 중간 구간의 rate — 상대 모양을 유지한 채 덩어리 몫(40%)으로 정규화한다.
const MID = (() => {
  let prev = HEAD_UNTIL, raw = 0;
  const spans = SPAWN_SHAPE.midBands.map(([end, rel]) => {
    const w = end - prev; prev = end; raw += rel * w; return { end, rel, w };
  });
  const k = SPAWN_SHAPE.mid / raw;            // 덩어리 전체를 40% 로
  return spans.map(s => ({ end: s.end, rate: s.rel * k }));
})();
const HEAD_RATE = SPAWN_SHAPE.head / HEAD_UNTIL;
const TAIL_RATE = SPAWN_SHAPE.tail / (1 - END_SURGE_AT);
// 보스 밤 — **폭발하지 않는다.** 직전 구간 밀도의 1.2배까지만(몫으로는 28.5%).
//   카이팅(N29)·배후 급소 창(N28·N30)이 **움직일 공간**을 전제하기 때문이다.
export const BOSS_TAIL_RATE = MID[MID.length - 1].rate * SPAWN_SHAPE.bossTailMul;

// ══════════════════════════════════════════════════════════════════
//  **밤의 파형 4종** 【리듬 설계 2026-08-08 — 사장님 승인 「다 반영」】
//    뱀서의 매너리즘은 적이 같아서가 아니라 **매 밤 램프(성장 오르막)가 같아서** 온다.
//    파형은 3막 기본 곡선 위에 곱하는 **모양**이고, 캠페인의 실효 평균 정규화가
//    tail·boss와 함께 파형까지 읽으므로 **밤 총량은 안 바뀐다 — 배분만 바뀐다.**
//    조각이 스폰을 따라오므로 파형은 공짜로 넷을 바꾼다: 적의 밀물, 레벨업 곡선,
//    치즈 수입의 시각, 그리고 정적의 무게.
//      ramp  — 기본. 3막 곡선 그대로 (배정 없는 모든 밤)
//      waves — 파도 4번, 파도 사이 골이 깊다. 골이 상점·우물의 시간이다 (조합 밤)
//      hush-eased — 29 빈 들. 기존 hush의 희박한 정찰량과 양 끝값은 그대로 두고,
//              마지막 3분 12초의 선형 release만 기존 파도식 sin²로 연다. 행동 전환과
//              물량 전환은 같은 5구간 경계를 읽되 쇄도 첫 박자의 접촉 절벽을 줄인다.
//      hush  — 37 숨죽인 밤의 더 가파른 재등장. 이미 배운 리듬을 그대로 세게 묻는다.
//      twin  — 봉우리 둘(전반·후반). 세 방향의 밤(30)의 등뼈
//    ⚠ 상한(concurrent)이 파도 꼭대기를 자를 수 있다 — 잘린 몫은 _acc 에 남아
//      골로 흘러넘친다. 총량은 지켜지고 꼭대기만 눌린다(실측은 카드 배정 주석에).
// ══════════════════════════════════════════════════════════════════
const gauss = (p, c, w) => Math.exp(-((p - c) * (p - c)) / (2 * w * w));
const HUSH_SURGE_AT = SPAWN_SHAPE.phaseEnds[2];
const SPAWN_AVG_SAMPLES = 2000;
function baseSpawnRateAt(p, endSurge = TAIL_RATE) {
  if (p < HEAD_UNTIL) return HEAD_RATE;
  if (p >= END_SURGE_AT) return endSurge;
  for (const m of MID) if (p < m.end) return m.rate;
  return MID[MID.length - 1].rate;
}
const WAVE_SHAPES = {
  waves: (p) => 0.15 + 1.7 * Math.pow(Math.max(0, Math.sin(Math.PI * ((p * 4) % 1))), 2),
  hush:  (p) => (p < HUSH_SURGE_AT
    ? 0.10
    : 0.10 + ((p - HUSH_SURGE_AT) / (1 - HUSH_SURGE_AT)) * 3.4),
  'hush-eased': (p) => (p < HUSH_SURGE_AT
    ? 0.10
    : 0.10 + Math.pow(Math.sin(Math.PI
      * ((p - HUSH_SURGE_AT) / (1 - HUSH_SURGE_AT)) / 2), 2) * 3.4),
  twin:  (p) => 0.30 + 1.55 * (gauss(p, 0.35, 0.09) + gauss(p, 0.85, 0.09)),
};

function spawnRateAt(p, endSurge = TAIL_RATE, wave = 'ramp') {
  const r = baseSpawnRateAt(p, endSurge);
  const w = WAVE_SHAPES[wave];
  return w ? r * w(p) : r;
}
// ⚠ **끝 두께가 밤마다 다르므로 평균도 밤마다 다르다.** 하나로 캐시해 두면
//   보스 밤의 정규화가 다른 밤 것으로 계산돼 **총량이 조용히 어긋난다.**
//   (구 구현은 `_avgRate` 하나만 들고 있었다 — 끝 두께가 상수였을 때는 맞았다.)
const _avgRate = new Map();
function avgSpawnRate(endSurge = 24.0, wave = 'ramp') {
  const key = endSurge + '|' + wave;          // ⚠ 파형까지 열쇠에 넣는다 — 안 넣으면
  const hit = _avgRate.get(key);              //   파형 밤의 정규화가 남의 평균으로 계산돼
  if (hit) return hit;                        //   총량이 조용히 어긋난다(끝 두께와 같은 병)
  let s = 0;
  for (let i = 0; i < SPAWN_AVG_SAMPLES; i++)
    s += spawnRateAt((i + 0.5) / SPAWN_AVG_SAMPLES, endSurge, wave);
  const v = s / SPAWN_AVG_SAMPLES;
  _avgRate.set(key, v);
  return v;
}

// 캠페인에서 실제 누산기에 들어가는 평균. `spawnRate()`의 마지막 2분 cut과
// `spawn()`의 보스 cut까지 같은 2,000 midpoint 자로 적분한다. 따라서 파형을
// 바꿔도 총량이 조용히 깎이지 않고, 수동 spawnMult 경로에는 전혀 닿지 않는다.
const _avgEffectiveRate = new Map();
function avgEffectiveSpawnRate(endSurge, wave = 'ramp', bossActive = false) {
  const key = endSurge + '|' + wave + '|' + Number(bossActive);
  const hit = _avgEffectiveRate.get(key);
  if (hit) return hit;
  let s = 0;
  const bossCut = bossSwarmMult(bossActive);
  for (let i = 0; i < SPAWN_AVG_SAMPLES; i++) {
    const p = (i + 0.5) / SPAWN_AVG_SAMPLES;
    const tailCut = p >= SPAWN_SHAPE.tailFrom ? SPAWN_SHAPE.tailCut : 1;
    s += spawnRateAt(p, endSurge, wave) * tailCut * bossCut;
  }
  const v = s / SPAWN_AVG_SAMPLES;
  _avgEffectiveRate.set(key, v);
  return v;
}

function campaignReferenceEffectiveShare() {
  return avgEffectiveSpawnRate(TAIL_RATE, 'ramp', false)
       / avgSpawnRate(TAIL_RATE, 'ramp');
}

function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
// 칸 좌표 → 씨앗. **우물 타일링 전용**(cellWater) — 밤 난수열과 완전히 분리한다.
//   음수 좌표(왼쪽·위로 걸어 나간 칸)도 그대로 받는다: `|0` 과 imul 이 32비트로 감는다.
function cellHash(seed, cx, cy) {
  let h = (seed | 0) ^ 0x9E3779B9;
  h = Math.imul(h ^ (cx | 0), 0x85EBCA6B); h ^= h >>> 13;
  h = Math.imul(h ^ (cy | 0), 0xC2B2AE35); h ^= h >>> 16;
  return h >>> 0;
}
function mulberry(a) {
  return function () {
    a |= 0; a = a + 0x6D2B79F5 | 0;
    let t = Math.imul(a ^ a >>> 15, 1 | a);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}
